import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { jsPDF } from "jspdf";
import * as pdfjsLib from "pdfjs-dist";
import { PDFDocument } from "pdf-lib";

// Worker de pdfjs-dist vía CDN — evita problemas de configuración de webpack en CRA
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ── Upload adjunto a Vercel Blob ──────────────────────────────────────────────
// Comprime una imagen en el navegador antes de subirla (las fotos de celular suelen
// pesar 5-15MB, muy por encima del límite de ~4MB que permite Vercel)
function comprimirImagen(file,maxDim=1600,calidad=0.75){
  return new Promise((resolve,reject)=>{
    const img=new Image();
    const url=URL.createObjectURL(file);
    img.onload=()=>{
      let{width,height}=img;
      if(width>maxDim||height>maxDim){
        if(width>height){height=Math.round(height*maxDim/width);width=maxDim;}
        else{width=Math.round(width*maxDim/height);height=maxDim;}
      }
      const canvas=document.createElement("canvas");
      canvas.width=width;canvas.height=height;
      canvas.getContext("2d").drawImage(img,0,0,width,height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob=>{
        if(!blob){reject(new Error("No se pudo comprimir la imagen"));return;}
        resolve(new File([blob],file.name.replace(/\.[^.]+$/,"")+".jpg",{type:"image/jpeg"}));
      },"image/jpeg",calidad);
    };
    img.onerror=()=>reject(new Error("No se pudo cargar la imagen"));
    img.src=url;
  });
}
// Comprime un PDF re-renderizando cada página como imagen JPEG y reconstruyéndolo —
// los PDF de apps "escanear documento" suelen pesar 5-15MB por incrustar fotos sin comprimir
async function comprimirPDF(file,calidad=0.55,escala=1.3){
  const arrayBuffer=await file.arrayBuffer();
  const pdf=await pdfjsLib.getDocument({data:arrayBuffer}).promise;
  const outDoc=await PDFDocument.create();
  for(let i=1;i<=pdf.numPages;i++){
    const page=await pdf.getPage(i);
    const viewport=page.getViewport({scale:escala});
    const canvas=document.createElement("canvas");
    canvas.width=viewport.width;canvas.height=viewport.height;
    await page.render({canvasContext:canvas.getContext("2d"),viewport}).promise;
    const jpegBytes=await new Promise(res=>canvas.toBlob(b=>b.arrayBuffer().then(res),"image/jpeg",calidad));
    const jpegImage=await outDoc.embedJpg(jpegBytes);
    const pdfPage=outDoc.addPage([viewport.width,viewport.height]);
    pdfPage.drawImage(jpegImage,{x:0,y:0,width:viewport.width,height:viewport.height});
  }
  const outBytes=await outDoc.save();
  return new File([outBytes],file.name.replace(/\.pdf$/i,"")+"_comprimido.pdf",{type:"application/pdf"});
}
const UMBRAL_COMPRESION=3*1024*1024; // 3MB — por debajo de esto no se comprime, no hace falta
async function subirAdjunto(file,eId=null){
  if(!file)return null;
  let fileToUpload=file;
  if(file.type.startsWith("image/")){
    // Las fotos se comprimen siempre — es rápido y casi no se nota la pérdida de calidad
    try{fileToUpload=await comprimirImagen(file);}catch{/* si falla, intenta con el original */}
  }else if(file.type==="application/pdf"&&file.size>UMBRAL_COMPRESION){
    // Los PDF solo se comprimen cuando realmente pesan demasiado (más lento que una foto)
    try{fileToUpload=await comprimirPDF(file);}catch{/* si falla la compresión, intenta subir el original igual */}
  }
  const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(fileToUpload);});
  const body={base64,nombre:fileToUpload.name,tipo:fileToUpload.type};
  if(eId)body.eId=eId; // nombre fijo por equipo → sobreescribe
  const resp=await fetch("/api/upload",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
  if(!resp.ok){
    let msg=`HTTP ${resp.status}`;
    try{const d=await resp.json();if(d.error)msg=d.error;}catch{}
    if(resp.status===413)msg="El archivo sigue siendo demasiado grande incluso tras comprimirlo. Intenta reducirlo manualmente en smallpdf.com.";
    throw new Error(msg); // propaga el error REAL del servidor, no uno genérico
  }
  const d=await resp.json();
  return d.url||null;
}
// ──────────────────────────────────────────────────────────────────────────────

// ── Configuración sync servidor ───────────────────────────────────────────────
// Usa la API de Vercel (mismo dominio, sin CORS, sin clave en el frontend)
const SYNC_URL = "/api/datos";
const SYNC_KEY = "";   // No se necesita: la seguridad la maneja Vercel
// ──────────────────────────────────────────────────────────────────────────────

const C={
  bg:"#07090D",surf:"#0D1117",card:"#161B22",cardHi:"#1C2330",
  border:"#21262D",borderHi:"#363B42",
  orange:"#F07820",orangeL:"rgba(240,120,32,0.12)",orangeB:"rgba(240,120,32,0.38)",
  amber:"#E8A020",amberL:"rgba(232,160,32,0.13)",
  red:"#F85149",redL:"rgba(248,81,73,0.12)",
  green:"#3FB950",greenL:"rgba(63,185,80,0.12)",
  blue:"#58A6FF",blueL:"rgba(88,166,255,0.12)",
  txt:"#E6EDF3",txt2:"#8B949E",txt3:"#484F58",
};
const today=new Date();today.setHours(0,0,0,0);
const toISO=d=>new Date(d).toISOString().split("T")[0];
const addD=(d,n)=>{const r=new Date(d);r.setDate(r.getDate()+n);return r;};
const parseD=s=>new Date(s+"T00:00:00");
const fmtCL=s=>parseD(s).toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"});
const diffD=s=>Math.ceil((parseD(s).getTime()-today.getTime())/864e5);
const nxtId=a=>a.length?Math.max(...a.map(x=>x.id))+1:1;
const startOfWeek=d=>{const r=new Date(d);r.setHours(0,0,0,0);const day=r.getDay();r.setDate(r.getDate()-(day===0?6:day-1));return r;};
const normDate=v=>{if(!v)return"";if(typeof v==="number")return toISO(new Date(Math.round((v-25569)*86400*1000)));const s=String(v).trim();if(/^\d{4}-\d{2}-\d{2}$/.test(s))return s;const m4=s.match(/^(\d{2})[\/\-\.](\d{2})[\/\-\.](\d{4})$/);if(m4)return`${m4[3]}-${m4[2]}-${m4[1]}`;const m2=s.match(/^(\d{2})[\/\-\.](\d{2})[\/\-\.](\d{2})$/);if(m2)return`20${m2[3]}-${m2[2]}-${m2[1]}`;return s;};
// safeDate: corrige fechas donde la IA pudo haber transpuesto día↔año al leer el PDF.
// Si ambas interpretaciones son válidas (año dentro de rango plausible), se elige
// la que esté más cerca de HOY — las OTs de mantención casi nunca son de años atrás.
const safeDate=v=>{
  if(!v)return"";
  const d=normDate(v);
  if(!d)return"";
  const [yStr,mStr,ddStr]=d.split("-");
  const y=parseInt(yStr),dd=parseInt(ddStr),yLast2=parseInt(yStr.slice(-2));
  const candA=(y>=2015&&y<=2035)?d:null; // interpretación directa
  const newYearB=2000+dd,newDayB=yLast2;
  const candB=(newYearB>=2015&&newYearB<=2035&&newDayB>=1&&newDayB<=31)
    ?`${newYearB}-${mStr}-${String(newDayB).padStart(2,"0")}`:null; // interpretación intercambiada
  if(candA&&!candB)return candA;
  if(!candA&&candB)return candB;
  if(!candA&&!candB)return d;
  const distHoy=iso=>Math.abs((parseD(iso).getTime()-today.getTime())/864e5);
  return distHoy(candA)<=distHoy(candB)?candA:candB;
};

// ─── DÍA HÁBIL CHILE ─────────────────────────────────────────────────────────
// Algoritmo de Butcher para calcular Domingo de Pascua
const calcPascua=y=>{const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,m=Math.floor((a+11*h+22*l)/451),mes=Math.floor((h+l-7*m+114)/31),dia=((h+l-7*m+114)%31)+1;return new Date(y,mes-1,dia);};
// Feriados fijos Chile (MM-DD)
const FF=new Set(["01-01","05-01","05-21","06-29","07-16","08-15","09-18","09-19","10-12","10-31","11-01","12-08","12-25"]);
const esFeriado=d=>{
  const mmdd=`${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  if(FF.has(mmdd))return true;
  const p=calcPascua(d.getFullYear());
  const vs=new Date(p);vs.setDate(p.getDate()-2); // Viernes Santo
  const sg=new Date(p);sg.setDate(p.getDate()-1); // Sábado de Gloria
  const iso=d=>toISO(d);
  return iso(d)===iso(vs)||iso(d)===iso(sg);
};
// Avanza al siguiente día hábil si cae en sáb, dom o feriado
const sigDiaHabil=s=>{
  if(!s)return s;
  let d=parseD(s);let n=0;
  while(d.getDay()===0||d.getDay()===6||esFeriado(d)){d=addD(d,1);if(++n>14)break;}
  return toISO(d);
};
// Versión con aviso: retorna {fecha, ajustada, motivo}. Si exento=true, nunca ajusta.
const fpConAviso=(s,exento=false)=>{
  if(!s||exento)return{fecha:s,ajustada:false,motivo:""};
  const orig=parseD(s);const adj=sigDiaHabil(s);
  if(adj===s)return{fecha:s,ajustada:false,motivo:""};
  const dia=orig.getDay();
  const motivo=dia===6?"sábado":dia===0?"domingo":`feriado (${fmtCL(s)})`;
  return{fecha:adj,ajustada:true,motivo};
};
// ─── CLIENTES SIN RESTRICCIÓN DE DÍA HÁBIL ───────────────────────────────────
// Estos clientes pueden programarse cualquier día de la semana, incluido sábado/domingo/feriado
const CLIENTES_SIN_RESTRICCION_DIA=["cosmetica nacional","cosmética nacional","c.nacional","cnacional"];
const normCentroKey=s=>String(s||"").toLowerCase().replace(/[.\s]/g,""); // quita espacios y puntos para comparar
const centroExentoDiaHabil=nombreCentro=>{
  const n=normCentroKey(nombreCentro);
  return CLIENTES_SIN_RESTRICCION_DIA.some(k=>n.includes(normCentroKey(k)));
};
const cIdExentoDiaHabil=(cId,cl)=>{
  const c=cl.find(x=>x.id===cId);
  return c?centroExentoDiaHabil(c.nombre):false;
};
const eIdExentoDiaHabil=(eId,eq,cl)=>{
  const e=eq.find(x=>x.id===eId);
  return e?cIdExentoDiaHabil(e.cId,cl):false;
};
// sigDiaHabil condicional: si el centro está exento, devuelve la fecha sin tocar
const sigDiaHabilCid=(fecha,cId,cl)=>cIdExentoDiaHabil(cId,cl)?fecha:sigDiaHabil(fecha);
const sigDiaHabilEid=(fecha,eId,eq,cl)=>eIdExentoDiaHabil(eId,eq,cl)?fecha:sigDiaHabil(fecha);
// La próxima mantención NUNCA puede ser anterior a la fecha realizada
const fpEsValida=(fR,fP)=>{if(!fR||!fP)return true;return parseD(fP).getTime()>=parseD(fR).getTime();};
// Si fP es inválida (anterior a fR), la recalcula usando la frecuencia disponible (o 90 días por defecto)
const corregirFP=(fR,fP,freqDias,eId,eq,cl)=>{
  if(fpEsValida(fR,fP))return fP;
  const dias=parseInt(freqDias)>0?parseInt(freqDias):90;
  return sigDiaHabilEid(toISO(addD(parseD(fR),dias)),eId,eq,cl);
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── MOTOR DE RECONOCIMIENTO DE EQUIPOS ──────────────────────────────────────
const normFull=s=>String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
const soloDigitos=s=>String(s||"").replace(/[^0-9]/g,"");
const levenshtein=(a,b)=>{
  if(a===b)return 0;
  if(!a.length)return b.length;
  if(!b.length)return a.length;
  const dp=Array.from({length:a.length+1},(_,i)=>[i,...Array(b.length).fill(0)]);
  for(let j=0;j<=b.length;j++)dp[0][j]=j;
  for(let i=1;i<=a.length;i++)for(let j=1;j<=b.length;j++)
    dp[i][j]=a[i-1]===b[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j],dp[i][j-1],dp[i-1][j-1]);
  return dp[a.length][b.length];
};
const similitud=(a,b)=>{
  if(!a||!b)return 0;
  const maxLen=Math.max(a.length,b.length);
  if(maxLen===0)return 1;
  return 1-(levenshtein(a,b)/maxLen);
};
const matchScore=(serieRaw,marcaRaw,modeloRaw,equipo)=>{
  const sN=normFull(serieRaw),eN=normFull(equipo.serie);
  if(!sN||!eN)return 0;
  if(sN===eN)return 1;
  let score=0;
  const sNoPfx=sN.replace(/^[a-z]/,""),eNoPfx=eN.replace(/^[a-z]/,"");
  if(sNoPfx===eN||sN===eNoPfx||sNoPfx===eNoPfx)score=Math.max(score,0.95);
  const sD=soloDigitos(serieRaw),eD=soloDigitos(equipo.serie);
  if(sD.length>=4&&eD.length>=4){
    if(sD===eD)score=Math.max(score,0.97);
    else if(eD.includes(sD)||sD.includes(eD))score=Math.max(score,0.85);
    else{
      const suf=n=>n.slice(-6);
      if(suf(sD)===suf(eD)&&suf(sD).length>=5)score=Math.max(score,0.8);
    }
  }
  const simTxt=similitud(sN,eN);
  score=Math.max(score,simTxt*0.75);
  const mPdf=normFull(marcaRaw),mEq=normFull(equipo.marca);
  const moPdf=normFull(modeloRaw),moEq=normFull(equipo.modelo);
  if(mPdf&&mEq&&(mPdf===mEq||mPdf.includes(mEq)||mEq.includes(mPdf)))score=Math.min(1,score+0.08);
  if(moPdf&&moEq&&(moPdf===moEq||moPdf.includes(moEq)||moEq.includes(moPdf)))score=Math.min(1,score+0.05);
  return Math.min(1,score);
};
const buscarEquipo=(serieRaw,marcaRaw,modeloRaw,eqList)=>{
  if(!serieRaw)return{exacto:null,sugeridos:[]};
  const scored=eqList.map(e=>({equipo:e,score:matchScore(serieRaw,marcaRaw,modeloRaw,e)}))
    .filter(x=>x.score>0.45)
    .sort((a,b)=>b.score-a.score);
  const exacto=scored.length&&scored[0].score>=0.93?scored[0].equipo:null;
  const sugeridos=exacto?scored.slice(1,5):scored.slice(0,5);
  return{exacto,sugeridos};
};
// ─────────────────────────────────────────────────────────────────────────────
const MESES=["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS_CORTO=["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];
const FREQS=[[30,"Mensual"],[60,"Bimestral"],[90,"Trimestral"],[120,"Cuatrimestral"],[180,"Semestral"],[365,"Anual"]];

// Último registro por equipo (estado actual)
const latestByEq=mt=>{const r={};mt.forEach(m=>{if(!r[m.eId]||m.fR>r[m.eId].fR)r[m.eId]=m;});return r;};
// Una OT sin "tipo" definido se trata como Preventiva (compatibilidad con datos antiguos)
const esPreventiva=m=>(m.tipo||"Preventiva").toLowerCase()==="preventiva";
// El cálculo de "próxima mantención" SOLO debe considerar OTs preventivas — las correctivas
// son seguimientos/reparaciones puntuales y nunca deben mover la fecha de mantención programada
const latestPreventivaByEq=mt=>latestByEq(mt.filter(esPreventiva));
// Eliminar duplicados
const dedupMt=mts=>{const s=new Set();return mts.filter(m=>{const k=m.ot?`${m.eId}-OT-${m.ot}`:`${m.eId}-${m.fR}-${m.tipo}`;if(s.has(k))return false;s.add(k);return true;});};
// ── Fusión de datos en 3 vías (evita que un computador con datos viejos pise cambios de otro) ──
const stableStr=v=>{if(v===null||typeof v!=="object")return JSON.stringify(v===undefined?null:v);if(Array.isArray(v))return "["+v.map(stableStr).join(",")+"]";return "{"+Object.keys(v).sort().map(k=>JSON.stringify(k)+":"+stableStr(v[k])).join(",")+"}";};
const hashStr=str=>{let h=5381;for(let i=0;i<str.length;i++)h=((h<<5)+h+str.charCodeAt(i))|0;return String(h)+":"+str.length;};
const normDatos=d=>({cl:d.cl||[],eq:(d.eq||[]).map(e=>({...e,frecDefault:e.frecDefault||""})),mt:dedupMt(d.mt||[]),bitacora:d.bitacora||[]});
const hashesDe=d=>{const o={};["cl","eq","mt","bitacora"].forEach(k=>{o[k]={};(d[k]||[]).forEach(x=>{o[k][String(x.id)]=hashStr(stableStr(x));});});return o;};
const mismoContenido=(a,b)=>stableStr(hashesDe(a))===stableStr(hashesDe(b));
const mergeLista=(bh,local,server)=>{
  const b=bh||{};
  const lMap=new Map((local||[]).map(x=>[String(x.id),x]));
  const out=new Map((server||[]).map(x=>[String(x.id),x]));
  Object.keys(b).forEach(id=>{if(!lMap.has(id))out.delete(id);});            // borrado localmente
  lMap.forEach((l,id)=>{if(b[id]!==hashStr(stableStr(l)))out.set(id,l);});   // agregado o modificado localmente
  return [...out.values()];
};
const mergeDatos=(baseH,local,server)=>{
  const r={cl:mergeLista(baseH&&baseH.cl,local.cl,server.cl),eq:mergeLista(baseH&&baseH.eq,local.eq,server.eq),mt:mergeLista(baseH&&baseH.mt,local.mt,server.mt),bitacora:mergeLista(baseH&&baseH.bitacora,local.bitacora,server.bitacora)};
  r.mt=dedupMt(r.mt);return r;
};

const CL0=[
  {id:1,cod:"221",nombre:"CD"},{id:2,cod:"96",nombre:"Cerrillos"},{id:3,cod:"98",nombre:"Coquimbo"},
  {id:4,cod:"37",nombre:"Coyhaique"},{id:5,cod:"74",nombre:"El Bosque"},{id:6,cod:"70",nombre:"Estación Central"},
  {id:7,cod:"214",nombre:"Farfana"},{id:8,cod:"745",nombre:"Independencia"},{id:9,cod:"39",nombre:"La Serena"},
  {id:10,cod:"742",nombre:"La Serena Balmaceda"},{id:11,cod:"",nombre:"Lo Ruiz"},{id:12,cod:"753",nombre:"Maintencillo"},
  {id:13,cod:"88",nombre:"Maipú"},{id:14,cod:"7",nombre:"Osorno"},{id:15,cod:"79",nombre:"Parque Arauco"},
  {id:16,cod:"24",nombre:"Punta Arenas"},{id:17,cod:"723",nombre:"Quilicura"},{id:18,cod:"746",nombre:"Quilín"},
  {id:19,cod:"734",nombre:"Quillota"},{id:20,cod:"83",nombre:"Rancagua"},{id:21,cod:"726",nombre:"Tobalaba"},
  {id:22,cod:"754",nombre:"Villarrica"},
];
const ER=[
  [1,1,"Brazo Articulado","Haulotte","Ha151P","2003170"],[2,1,"Elevador Tijeras","Genie","Gs1930","Gs3013A121903"],
  [3,2,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3337"],[4,2,"Transpaleta","Crown","Pe 4500","6A286061"],
  [5,2,"Transpaleta","Crown","Pe 4500","6A286057"],[6,2,"Transpaleta","Raymond","8400","840-11-87251"],
  [7,2,"Transpaleta","Raymond","8400","840-12-92067"],[8,3,"Elevador Tijeras","JLG","1930Es","200144250"],
  [9,3,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3336"],[10,3,"Reach","Raymond","R40Tt","Ez-D-06-37138"],
  [11,3,"Reach","Raymond","R40Tt","Ez-D-06-35230"],[12,4,"Elevador Tijeras","Haulotte","Compact 10N","2039541"],
  [13,4,"Reach","Crown","RR5700","1A534372"],[14,5,"Elevador Tijeras","JLG","1930Es","200002300"],
  [15,5,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3289"],[16,5,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3290"],
  [17,5,"Reach","Raymond","R40Tt","Ezd-08-39447"],[18,5,"Transpaleta","Raymond","8400","84008-78801"],
  [19,5,"Transpaleta","Raymond","8400","84008-78800"],[20,6,"Elevador Tijeras","JLG","1930Es","200002341"],
  [21,6,"Reach","Raymond","R40Tt","Ezd-08-39446"],[22,6,"Transpaleta","Raymond","8400","840-08-78781"],
  [23,6,"Transpaleta","Raymond","8400","840-08-78789"],[24,7,"Brazo Articulado","Haulotte","Ha151P","2038948"],
  [25,7,"Carro Golf","Ez-Go","Freedom Txt","3112962"],[26,7,"FC20","Crown","Fc 4500","9A158621"],
  [27,7,"FC22","Crown","Fc 4500","9A158651"],[28,7,"FC28","Crown","Fc 4500","9A158745"],
  [29,7,"Grúa Pluma","Grove/Manlift","Yb5515","Yb5515"],[30,8,"Elevador Tijeras","JLG","1932R","200011369"],
  [31,8,"Elevador Tijeras","Haulotte","Compact 10 N","2036595"],[32,8,"Reach","Crown","Rr5715-40","1A533713"],
  [33,8,"Transpaleta","BT","Lpe250","6539728"],[34,9,"Elevador Tijeras","JLG","1930Es","M200011732"],
  [35,9,"Elevador Tijeras","JLG","2646Es","200051043"],[36,9,"Elevador Tijeras","Haulotte","Optimum 8","Ce 144638"],
  [37,9,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3303"],[38,9,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3307"],
  [39,9,"Reach","Crown","Rr5715-40","1A527902"],[40,9,"Reach","Raymond","R40Tt","Et-D-03-12661"],
  [41,9,"Reach","Crown","Rr5725-35","1A364115"],[42,9,"Transpaleta","Raymond","8400","840-08-78788"],
  [43,10,"Elevador Tijeras","Haulotte","Optimum 8","2029412"],[44,10,"Elevador Tijeras","Haulotte","Compact 10 N","2030236"],
  [45,10,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3306"],[46,10,"Reach","Crown","Rr5715-40","1A534102"],
  [47,10,"Transpaleta","BT","Lpe250","6539693"],[48,10,"Transpaleta","BT","Lpe250","6539694"],
  [49,11,"Grúa Horquilla","Hyundai","110D-7E","HWKHFT05VF00001121"],[50,11,"Grúa Horquilla","Komatsu","FD100-8","M246-9094"],
  [51,12,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571-3271"],[52,13,"Elevador Tijeras","JLG","1930Es","200195572"],
  [53,13,"Elevador Tijeras","JLG","1930Es","200175317"],[54,13,"Elevador Tijeras","JLG","2646E","200025318"],
  [55,13,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3270"],[56,13,"Transpaleta","Raymond","8400","87228"],
  [57,13,"Transpaleta","Raymond","8400","65846"],[58,14,"Ordenador de Pasillo","Haulotte","Xlt 1571","1571-3317"],
  [59,15,"Elevador Tijeras","JLG","1930Es","B200002339"],[60,15,"Elevador Tijeras","Haulotte","Optimum 8","Ce804706"],
  [61,15,"Ordenador de Pasillo","Haulotte","Xlt 1571","S/N"],[62,15,"Ordenador de Pasillo","Haulotte","Xlt 1571","Préstamo"],
  [63,15,"Reach","Raymond","R40Tt","EZ-D-06-37139"],[64,15,"Transpaleta","Raymond","8400","840-11-87252"],
  [65,15,"Transpaleta","Raymond","8400","840-11-87220"],[66,16,"Elevador Tijeras","JLG","1930ES","200160215"],
  [67,16,"Elevador Tijeras","Haulotte","Compact 10 N","2104131"],[68,16,"Reach","Raymond","R40Tt","730-16-AC01294"],
  [69,16,"Reach","Raymond","R40Tt","EZ-D-06-37276"],[70,17,"Elevador Tijeras","Haulotte","Compact 10N","S/N"],
  [71,17,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","3"],[72,17,"Reach","Raymond","R40Tt","Ez-11-Df-51058"],
  [73,18,"Elevador Tijeras","Haulotte","Optimum 8","2047903"],[74,18,"Elevador Tijeras","Haulotte","Compact 10 N","2058440"],
  [75,18,"Reach","Raymond","730-R40Tt","730-19-Bc04267"],[76,18,"Reach","Raymond","730-R40Tt","730-19-Bc04260"],
  [77,18,"Transpaleta","BT","Lpe200","6639657"],[78,18,"Transpaleta","BT","Lpe200","6639646"],
  [79,19,"Elevador Tijeras","Haulotte","Compact 10 N","Ce804641"],[80,19,"Ordenador de Pasillo","Haulotte","XLT-1571","1571-3326"],
  [81,19,"Reach","Crown","Rr5715-40","1A412703"],[82,20,"Elevador Tijeras","JLG","1930Es","200160216"],
  [83,20,"Elevador Tijeras","JLG","1930Es","200195571"],[84,20,"Transpaleta","Raymond","8400","840-06-65849"],
  [85,20,"Transpaleta","Crown","Pe 4500","6A286147"],[86,21,"Elevador Tijeras","JLG","2630Es","200002399"],
  [87,21,"Ordenador de Pasillo","Haulotte","Xlt 1571 Dc","1571 3341"],[88,21,"Reach","Raymond","R40Tt","51388"],
  [89,21,"Reach","Raymond","R40Tt","51385"],[90,21,"Transpaleta","Raymond","8400","840-12-93067"],
  [91,22,"Elevador Tijeras","JLG","1930ES","200192162"],[92,22,"Elevador Tijeras","Haulotte","Optimum BAC","2020820"],
  [93,22,"Elevador Tijeras","JLG","1930ES","200196093"],[94,22,"Reach","Raymond","R40tt","EZ-D-06-37269"],
  [95,22,"Transpaleta Eléctrica","Raymond","8400","840-12-93060"],
];
const E0=ER.map(([id,cId,tipo,marca,modelo,serie])=>({id,cId,tipo,marca,modelo,serie,est:"activo",ultimaOT:"",ultimoHorometro:"",frecDefault:""}));

const CARD={background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"1.1rem 1.3rem",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"};
const INP={width:"100%",boxSizing:"border-box",padding:"8px 12px",fontSize:13,borderRadius:7,border:`1px solid ${C.border}`,background:"#0D1117",color:C.txt,fontFamily:"inherit",outline:"none",transition:"border-color 0.15s"};
const BTNP={padding:"8px 18px",fontSize:13,fontWeight:600,borderRadius:7,border:"none",cursor:"pointer",background:`linear-gradient(135deg,${C.orange} 0%,#D4601A 100%)`,color:"#fff",fontFamily:"inherit",letterSpacing:"0.01em",boxShadow:"0 2px 8px rgba(240,120,32,0.25)"};
const BTNS={padding:"7px 14px",fontSize:13,borderRadius:7,border:`1px solid ${C.border}`,cursor:"pointer",background:"transparent",color:C.txt2,fontFamily:"inherit",transition:"border-color 0.15s,color 0.15s"};
const pill=d=>d<0?{bg:C.redL,c:"#E05050",l:`Vencida ${-d}d`}:d===0?{bg:C.amberL,c:"#D08840",l:"Hoy"}:d<=7?{bg:C.amberL,c:"#D08840",l:`En ${d}d`}:{bg:C.greenL,c:"#50B060",l:`En ${d}d`};
const estP=e=>({activo:{bg:C.greenL,c:"#50B060",l:"Activo"},mantencion:{bg:C.amberL,c:"#D08840",l:"En mantención"},fuera:{bg:C.redL,c:"#E05050",l:"Fuera de servicio"}}[e]||{bg:"rgba(90,90,90,0.2)",c:C.txt2,l:e||"—"});
const tipoP=t=>t==="Preventiva"?{bg:C.blueL,c:"#5090C0"}:{bg:C.amberL,c:"#D08840"};

function Chip({bg,c,l,size=11}){return <span style={{fontSize:size,padding:"2px 9px",borderRadius:20,fontWeight:600,background:bg,color:c,whiteSpace:"nowrap",letterSpacing:"0.02em",border:`1px solid ${c}22`}}>{l}</span>;}
function LBL({c}){return <label style={{fontSize:11,fontWeight:500,color:C.txt3,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>{c}</label>;}
function OTCard({m,mini=false}){
  if(!m||(!m.ot&&!m.actividad&&!m.horometro&&!m.frecuencia))return null;
  return <div style={{background:C.orangeL,border:`1px solid ${C.orangeB}`,borderRadius:9,padding:mini?"5px 9px":"9px 12px",marginTop:5}}>
    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:mini?2:4,flexWrap:"wrap"}}>
      {m.ot&&<span style={{fontSize:mini?10:12,fontWeight:500,color:C.orange}}>OT: {m.ot}</span>}
      {m.tipo&&<Chip {...tipoP(m.tipo)} l={m.tipo} size={mini?9:10}/>}
      {m.horometro&&<span style={{fontSize:mini?9:11,color:"#5090C0"}}>⏱ {m.horometro} hrs</span>}
      {m.frecuencia&&<span style={{fontSize:mini?9:11,color:C.txt3}}>↻ c/{m.frecuencia}d</span>}
    </div>
    {m.actividad&&<div style={{fontSize:mini?10:11,color:C.txt,marginBottom:2}}>{m.actividad}</div>}
    {m.obs&&!mini&&<div style={{fontSize:10,color:C.txt2}}>{m.obs}</div>}
    {m.adjunto&&<a href={m.adjunto} target="_blank" rel="noreferrer"
      style={{display:"inline-block",marginTop:4,fontSize:10,color:C.orange,textDecoration:"none",
              border:`1px solid ${C.orangeB}`,borderRadius:3,padding:"2px 8px",background:C.orangeL}}>
      📎 Ver adjunto
    </a>}
  </div>;
}
function Logo(){
  const [err,setErr]=useState(false);
  return <div style={{display:"flex",alignItems:"center",gap:12}}>
    {err
      ?<div style={{width:36,height:36,borderRadius:8,background:`linear-gradient(135deg,${C.orange},#C85510)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:"#fff",boxShadow:"0 2px 12px rgba(240,120,32,0.35)"}}>IL</div>
      :<img src="https://www.inmaluca.cl/images/logo_inmaluca_2026.png" alt="Inmaluca" onError={()=>setErr(true)} style={{height:36,objectFit:"contain"}}/>}
    <div style={{borderLeft:`1px solid ${C.border}`,paddingLeft:12}}>
      <div style={{fontSize:15,fontWeight:700,color:C.txt,letterSpacing:"-0.02em",lineHeight:1.2}}>SisteMant</div>
      <div style={{fontSize:10,color:C.txt3,letterSpacing:"0.08em",textTransform:"uppercase",marginTop:1}}>INMALUCA · Mantenciones</div>
    </div>
  </div>;
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({cl,eq,mt,setMt,setEq,onNav,onVerDetalle,onVerCentro}){
  const [showNotif,setShowNotif]=useState(false);
  const [selV,setSelV]=useState(null);
  const [updF,setUpdF]=useState(null);
  const gE=id=>eq.find(e=>e.id===id);
  const gC=id=>{const e=gE(id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const latMap=latestPreventivaByEq(mt); // solo preventivas determinan próxima fecha
  const curMts=Object.values(latMap);
  const hoy=curMts.filter(m=>diffD(m.fP)===0);
  const semana=curMts.filter(m=>{const d=diffD(m.fP);return d>0&&d<=7;});
  const vencidas=curMts.filter(m=>diffD(m.fP)<0).sort((a,b)=>a.fP.localeCompare(b.fP));
  const getFreq=eId=>{const e=gE(eId);const h=mt.filter(m=>m.eId===eId&&parseInt(m.frecuencia)>0).sort((a,b)=>b.fR.localeCompare(a.fR));return parseInt(h[0]?.frecuencia)||parseInt(e?.frecDefault)||0;};
  const sinFreq=eq.filter(e=>{const mtsEq=mt.filter(m=>m.eId===e.id);return mtsEq.length>0&&!mtsEq.some(m=>parseInt(m.frecuencia)>0)&&!parseInt(e.frecDefault);});
  const initUpd=m=>{const freq=getFreq(m.eId);setUpdF({eId:String(m.eId),tec:m.tec||"",fR:toISO(today),fP:freq>0?toISO(addD(today,freq)):"",tipo:m.tipo||"Preventiva",actividad:m.actividad||"",ot:"",horometro:"",frecuencia:String(freq||""),obs:""});};
  const saveUpd=()=>{
    if(!updF?.fR||!updF?.fP)return;
    setMt(p=>[...p,{...updF,id:nxtId(p),eId:Number(updF.eId)}]);
    if(updF.ot||updF.horometro)setEq(prev=>prev.map(e=>e.id===Number(updF.eId)?{...e,ultimaOT:(updF.tipo||"Preventiva").toLowerCase()==="preventiva"?(updF.ot||e.ultimaOT):e.ultimaOT,ultimoHorometro:updF.horometro||e.ultimoHorometro}:e));
    setSelV(null);setUpdF(null);
  };


  return <div style={{display:"flex",flexDirection:"column",gap:12}}>
    {showNotif&&<ModalNotificar cl={cl} eq={eq} mt={mt} onClose={()=>setShowNotif(false)}/>}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:10}}>
      {[
        {l:"Hoy",v:hoy.length,c:hoy.length>0?C.orange:C.txt3,sub:"mantenciones",icon:"📅",bg:hoy.length>0?C.orangeL:"transparent"},
        {l:"Esta semana",v:semana.length,c:C.amber,sub:"próximas",icon:"📆",bg:semana.length>0?C.amberL:"transparent"},
        {l:"Vencidas",v:vencidas.length,c:C.red,sub:"sin atender",icon:"⚠",bg:vencidas.length>0?C.redL:"transparent"},
        {l:"Centros",v:cl.length,c:C.blue,sub:`${eq.length} equipos`,icon:"🏭",bg:C.blueL}
      ].map(k=><div key={k.l} className="sm-card-hover" style={{background:C.card,border:`1px solid ${k.v>0&&k.c!==C.blue?k.c+"33":C.border}`,borderRadius:12,padding:"1rem 1.1rem",cursor:"pointer",transition:"all 0.2s",position:"relative",overflow:"hidden"}} onClick={()=>onNav("planificador")}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <div style={{fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:"0.08em"}}>{k.l}</div>
          <span style={{fontSize:16,opacity:0.7}}>{k.icon}</span>
        </div>
        <div style={{fontSize:28,fontWeight:700,color:k.c,letterSpacing:"-0.03em",lineHeight:1,marginBottom:4}}>{k.v}</div>
        <div style={{fontSize:11,color:C.txt3,fontWeight:400}}>{k.sub}</div>
      </div>)}
    </div>
    <div style={{display:"flex",justifyContent:"flex-end"}}>
      <button style={{...BTNP,fontSize:12,padding:"7px 18px",display:"flex",alignItems:"center",gap:7,borderRadius:8}}
        onClick={()=>setShowNotif(true)}>
        🔔 Enviar notificaciones {(vencidas.length>0||semana.length>0)?<span style={{background:"rgba(255,255,255,0.2)",borderRadius:10,padding:"1px 6px",fontSize:10,marginLeft:2}}>{vencidas.length+semana.length}</span>:""} 
      </button>
    </div>
    {hoy.length>0&&<div style={{background:C.orangeL,border:`1px solid ${C.orangeB}`,borderRadius:12,padding:"12px 16px"}}>
      <div style={{fontSize:12,fontWeight:500,color:C.orange,marginBottom:8}}>🔔 HOY ({hoy.length})</div>
      {hoy.map(m=>{const e=gE(m.eId);return(
        <div key={m.id} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`0.5px solid rgba(232,113,26,0.15)`,alignItems:"center"}}>
          <div><span style={{fontSize:12,fontWeight:500,color:C.txt}}>{e?.tipo||"—"}</span><span style={{fontSize:11,color:C.txt3,marginLeft:8}}>{gC(m.eId)} · {e?.marca} {e?.modelo}</span></div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {m.ot&&<span style={{fontSize:11,color:C.orange,fontWeight:500}}>OT: {m.ot}</span>}
            <button onClick={()=>onVerDetalle&&onVerDetalle(m)} style={{fontSize:10,padding:"2px 8px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:C.orangeL,color:C.orange,fontFamily:"inherit",fontWeight:600}}>⬡ Detalle</button>
          </div>
        </div>
      );})}
    </div>}
    {vencidas.length>0&&<div style={{background:C.redL,border:`1px solid ${C.red}33`,borderRadius:12,padding:"12px 16px"}}>
      <div style={{fontSize:12,fontWeight:500,color:"#E05050",marginBottom:8}}>⚠ {vencidas.length} vencida{vencidas.length>1?"s":""} — clic para registrar atención</div>
      <div style={{display:"grid",gridTemplateColumns:selV?"1fr 1.1fr":"1fr",gap:10,alignItems:"start"}}>
        <div>
          {vencidas.map(m=>{const e=gE(m.eId);const isSel=selV===m.id;return(
            <div key={m.id} onClick={()=>{setSelV(isSel?null:m.id);setUpdF(null);}}
              style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",borderRadius:6,cursor:"pointer",marginBottom:3,background:isSel?"rgba(139,32,32,0.35)":"rgba(139,32,32,0.1)",border:isSel?"0.5px solid rgba(224,80,80,0.5)":"0.5px solid transparent"}}>
              <div>
                <div style={{fontSize:12,fontWeight:isSel?500:400,color:C.txt}}>{e?.tipo||"—"} — {e?.marca} {e?.modelo}</div>
                <div style={{fontSize:10,color:C.txt3}}>{gC(m.eId)} · {e?.serie}</div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <Chip {...pill(diffD(m.fP))} size={10}/>
                <button onClick={e2=>{e2.stopPropagation();onVerDetalle&&onVerDetalle(m);}} style={{fontSize:9,padding:"1px 6px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:C.orangeL,color:C.orange,fontFamily:"inherit",fontWeight:600}}>⬡</button>
                <span style={{fontSize:10,color:C.txt3}}>{isSel?"▲":"▼"}</span>
              </div>
            </div>
          );})}
        </div>
        {selV&&(()=>{
          const m=vencidas.find(x=>x.id===selV);if(!m)return null;
          const e=gE(m.eId);
          return <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:12,fontWeight:500,color:C.txt,marginBottom:2}}>{e?.tipo||"—"} — {e?.marca} {e?.modelo}</div>
            <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>{gC(m.eId)} · Serie: {e?.serie}</div>
            <OTCard m={m}/>
            <div style={{display:"flex",justifyContent:"space-between",marginTop:6,marginBottom:8}}>
              <span style={{fontSize:11,color:"#E05050"}}>Vencida: {fmtCL(m.fP)}</span><Chip {...pill(diffD(m.fP))} size={10}/>
            </div>
            {!updF?<button style={{...BTNP,fontSize:12,padding:"6px 14px",width:"100%"}} onClick={()=>initUpd(m)}>+ Registrar atención</button>
            :<div>
              <div style={{fontSize:11,fontWeight:500,color:C.orange,marginBottom:8}}>Nueva atención</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}}>
                <div><LBL c="Técnico"/><input style={INP} value={updF.tec} onChange={e=>setUpdF(p=>({...p,tec:e.target.value}))} placeholder="Nombre técnico"/></div>
                <div><LBL c="N° OT"/><input style={INP} value={updF.ot} onChange={e=>setUpdF(p=>({...p,ot:e.target.value}))}/></div>
                <div><LBL c="Fecha realizada *"/><input type="date" style={INP} value={updF.fR} onChange={e=>{const n=parseInt(updF.frecuencia);setUpdF(p=>({...p,fR:e.target.value,fP:n>0?sigDiaHabilEid(toISO(addD(parseD(e.target.value),n)),Number(updF.eId),eq,cl):p.fP}));}}/></div>
                <div><LBL c={`Próxima *${!updF.fP?" ⚠":""}`}/><input type="date" style={{...INP,borderColor:updF.fP?C.orangeB:"#E05050"}} value={updF.fP} onChange={e=>setUpdF(p=>({...p,fP:e.target.value}))}/></div>
                <div><LBL c="Horómetro"/><input type="number" style={INP} value={updF.horometro} onChange={e=>setUpdF(p=>({...p,horometro:e.target.value}))}/></div>
                <div><LBL c="Actividad"/><input style={INP} value={updF.actividad} onChange={e=>setUpdF(p=>({...p,actividad:e.target.value}))}/></div>
                <div style={{gridColumn:"1/-1"}}><LBL c="Observaciones"/><input style={INP} value={updF.obs} onChange={e=>setUpdF(p=>({...p,obs:e.target.value}))} placeholder="Notas"/></div>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button style={{...BTNP,fontSize:11,padding:"5px 14px"}} onClick={saveUpd}>✅ Guardar</button>
                <button style={{...BTNS,fontSize:11,padding:"5px 10px"}} onClick={()=>setUpdF(null)}>Cancelar</button>
              </div>
            </div>}
          </div>;
        })()}
      </div>
    </div>}
    {sinFreq.length>0&&<div style={{background:"rgba(232,113,26,0.08)",border:`1px solid ${C.orangeB}`,borderRadius:8,padding:"10px 14px"}}>
      <div style={{fontSize:12,fontWeight:500,color:C.orange,marginBottom:6}}>📋 {sinFreq.length} equipo{sinFreq.length>1?"s":""} sin frecuencia — definir en Centros</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{sinFreq.slice(0,10).map(e=>{const c=cl.find(x=>x.id===e.cId);return <span key={e.id} style={{fontSize:10,background:"rgba(232,113,26,0.15)",border:`1px solid ${C.orangeB}`,borderRadius:4,padding:"2px 8px",color:C.orange}}>{c?.nombre} · {e.tipo} [{e.serie}]</span>;})}{sinFreq.length>10&&<span style={{fontSize:10,color:C.txt3}}>+{sinFreq.length-10} más</span>}</div>
    </div>}
    <div style={CARD}>
      <div style={{fontSize:13,fontWeight:500,color:C.txt,marginBottom:10}}>Estado por centro</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:5}}>
        {cl.map(c=>{
          const eqs=eq.filter(e=>e.cId===c.id);
          const cur=eqs.map(e=>latMap[e.id]).filter(Boolean);
          const v=cur.filter(m=>diffD(m.fP)<0).length;
          const p=cur.filter(m=>{const d=diffD(m.fP);return d>=0&&d<=7;}).length;
          return <div key={c.id} style={{background:C.surf,border:`1px solid ${v>0?C.orangeB:C.border}`,borderRadius:7,padding:"7px 9px",cursor:"pointer"}} onClick={()=>onVerCentro?onVerCentro(c.id):onNav("centros")}>
            <div style={{fontSize:11,fontWeight:500,color:C.txt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nombre}</div>
            <div style={{fontSize:10,color:C.txt3,marginBottom:3}}>{eqs.length} eq.</div>
            {v>0?<Chip bg={C.redL} c="#E05050" l={`${v} venc.`} size={9}/>:p>0?<Chip bg={C.amberL} c="#D08840" l={`${p} próx.`} size={9}/>:cur.length>0?<Chip bg={C.greenL} c="#50B060" l="Al día" size={9}/>:<Chip bg="rgba(80,80,80,0.15)" c={C.txt3} l="Sin datos" size={9}/>}
          </div>;
        })}
      </div>
    </div>
  </div>;
}


// ─── MODAL NOTIFICACIONES ────────────────────────────────────────────────────
function ModalNotificar({cl,eq,mt,onClose}){
  const [sending,setSending]=useState(false);
  const [result,setResult]=useState(null);
  const [diasAlerta,setDiasAlerta]=useState(7);
  const [emailsExtra,setEmailsExtra]=useState("");
  const [preview,setPreview]=useState(false);
  const latMap=latestPreventivaByEq(mt);
  const hoy=new Date();hoy.setHours(0,0,0,0);
  const diff=fP=>{try{return Math.round((new Date(fP+"T00:00:00")-hoy)/86400000);}catch{return null;}};
  const fmt=s=>{try{return new Date(s+"T00:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"});}catch{return s;}};

  const venc=eq.filter(e=>{const m=latMap[e.id];return m&&diff(m.fP)<0;})
               .sort((a,b)=>diff(latMap[a.id].fP)-diff(latMap[b.id].fP));
  const prox=eq.filter(e=>{const m=latMap[e.id];const d=diff(m?.fP);return d!==null&&d>=0&&d<=diasAlerta;})
               .sort((a,b)=>diff(latMap[a.id].fP)-diff(latMap[b.id].fP));

  const buildResumen=()=>{
    const lineas=[];
    lineas.push("📋 RESUMEN MANTENCIONES — SisteMant INMALUCA");
    lineas.push(`Fecha: ${new Date().toLocaleDateString("es-CL",{weekday:"long",day:"2-digit",month:"long",year:"numeric"})}`);
    lineas.push("");
    if(venc.length>0){
      lineas.push(`🔴 MANTENCIONES VENCIDAS (${venc.length}):`);
      venc.forEach(e=>{
        const c=cl.find(x=>x.id===e.cId);const m=latMap[e.id];
        lineas.push(`  • ${e.tipo} ${e.marca} ${e.modelo}`);
        lineas.push(`    Serie: ${e.serie||"—"} | Centro: ${c?.nombre||"—"}`);
        lineas.push(`    Vencida hace ${Math.abs(diff(m.fP))} días (debía hacerse el ${fmt(m.fP)})`);
      });
      lineas.push("");
    }
    if(prox.length>0){
      lineas.push(`🟡 PRÓXIMAS EN ${diasAlerta} DÍAS (${prox.length}):`);
      prox.forEach(e=>{
        const c=cl.find(x=>x.id===e.cId);const m=latMap[e.id];
        const d=diff(m.fP);
        lineas.push(`  • ${e.tipo} ${e.marca} ${e.modelo}`);
        lineas.push(`    Serie: ${e.serie||"—"} | Centro: ${c?.nombre||"—"}`);
        lineas.push(`    Fecha programada: ${fmt(m.fP)} (${d===0?"hoy":"en "+d+" días"})`);
      });
      lineas.push("");
    }
    if(venc.length===0&&prox.length===0){
      lineas.push("✅ No hay mantenciones vencidas ni próximas en el período seleccionado.");
    }
    lineas.push("─".repeat(40));
    lineas.push("Enviado desde SisteMant · sistemant.inmaluca.cl");
    return lineas.join("\n");
  };

  const enviar=async()=>{
    setSending(true);setResult(null);
    const resumen=buildResumen();
    const emails=emailsExtra.split(/[,;\n]/).map(e=>e.trim()).filter(Boolean);
    try{
      const r=await fetch("/api/notificar",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          mensaje:resumen,
          asunto:`SisteMant — ${venc.length} vencidas, ${prox.length} próximas`,
          vencidas:venc.map(e=>({tipo:e.tipo,marca:e.marca,modelo:e.modelo,serie:e.serie,centro:cl.find(x=>x.id===e.cId)?.nombre,diasVencida:Math.abs(diff(latMap[e.id].fP)),fP:latMap[e.id].fP})),
          proximas:prox.map(e=>({tipo:e.tipo,marca:e.marca,modelo:e.modelo,serie:e.serie,centro:cl.find(x=>x.id===e.cId)?.nombre,diasRestantes:diff(latMap[e.id].fP),fP:latMap[e.id].fP})),
          emailsExtra:emails,
          diasAlerta,
        })});
      const d=await r.json();
      setResult(d.ok
        ?{ok:true,txt:`✅ Notificación enviada correctamente${d.enviados?" a "+d.enviados+" destinatario(s)":""}`}
        :{ok:false,txt:"❌ "+(d.error||"Error al enviar")});
    }catch(ex){setResult({ok:false,txt:"❌ Error de conexión: "+ex.message});}
    setSending(false);
  };

  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"1rem"}}>
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto"}}>
      {/* Header */}
      <div style={{padding:"1rem 1.3rem",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:C.card,zIndex:1}}>
        <div style={{fontSize:15,fontWeight:700}}>🔔 Enviar Notificación</div>
        <button style={{...BTNS,padding:"3px 10px",fontSize:12}} onClick={onClose}>✕</button>
      </div>
      <div style={{padding:"1.1rem 1.3rem",display:"flex",flexDirection:"column",gap:14}}>
        {/* KPIs */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div style={{background:"rgba(212,44,44,0.1)",border:"1px solid rgba(212,44,44,0.3)",borderRadius:10,padding:"12px 14px"}}>
            <div style={{fontSize:28,fontWeight:700,color:"#E05050",lineHeight:1}}>{venc.length}</div>
            <div style={{fontSize:11,color:C.txt3,marginTop:2}}>🔴 Mantenciones vencidas</div>
          </div>
          <div style={{background:"rgba(240,120,0,0.08)",border:`1px solid ${C.orangeB}`,borderRadius:10,padding:"12px 14px"}}>
            <div style={{fontSize:28,fontWeight:700,color:C.orange,lineHeight:1}}>{prox.length}</div>
            <div style={{fontSize:11,color:C.txt3,marginTop:2}}>🟡 Próximas en {diasAlerta} días</div>
          </div>
        </div>

        {/* Config días alerta */}
        <div>
          <div style={{fontSize:11,color:C.txt3,marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:1}}>Alertar próximas en los próximos</div>
          <div style={{display:"flex",gap:6}}>
            {[3,7,14,30].map(d=><button key={d} onClick={()=>setDiasAlerta(d)}
              style={{padding:"5px 14px",fontSize:12,borderRadius:6,border:`1px solid ${diasAlerta===d?C.orange:C.border}`,background:diasAlerta===d?C.orangeL:"transparent",color:diasAlerta===d?C.orange:C.txt2,cursor:"pointer",fontFamily:"inherit",fontWeight:diasAlerta===d?600:400}}>
              {d} días
            </button>)}
          </div>
        </div>

        {/* Emails adicionales */}
        <div>
          <div style={{fontSize:11,color:C.txt3,marginBottom:6,fontWeight:600,textTransform:"uppercase",letterSpacing:1}}>Destinatarios adicionales (opcional)</div>
          <textarea value={emailsExtra} onChange={e=>setEmailsExtra(e.target.value)}
            placeholder={"email1@empresa.cl, email2@empresa.cl"}
            style={{...INP,width:"100%",resize:"vertical",minHeight:52,fontSize:12}}/>
          <div style={{fontSize:10,color:C.txt3,marginTop:3}}>Los destinatarios base se configuran en las variables de entorno de Vercel (ALERT_EMAILS)</div>
        </div>

        {/* Preview del mensaje */}
        <div>
          <button onClick={()=>setPreview(p=>!p)} style={{...BTNS,fontSize:11,marginBottom:preview?8:0}}>
            {preview?"▲ Ocultar":"▼ Ver"} preview del mensaje
          </button>
          {preview&&<pre style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:8,padding:12,fontSize:10,color:C.txt2,whiteSpace:"pre-wrap",maxHeight:200,overflowY:"auto",fontFamily:"monospace"}}>
            {buildResumen()}
          </pre>}
        </div>

        {/* Resultado */}
        {result&&<div style={{padding:"10px 14px",borderRadius:8,fontSize:12,background:result.ok?C.greenL:C.redL,color:result.ok?"#60C070":"#E05050",fontWeight:500}}>
          {result.txt}
        </div>}

        {/* Botones */}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button style={BTNS} onClick={onClose}>Cerrar</button>
          <button style={{...BTNP,opacity:(!sending&&(venc.length>0||prox.length>0))?1:0.5}}
            onClick={enviar} disabled={sending||(!venc.length&&!prox.length)}>
            {sending?"⏳ Enviando...":"🔔 Enviar notificación"}
          </button>
        </div>
        {venc.length===0&&prox.length===0&&
          <div style={{fontSize:12,color:C.green,textAlign:"center",padding:"8px 0"}}>
            ✅ No hay alertas que enviar — todos los equipos están al día
          </div>}
      </div>
    </div>
  </div>;
}

// ─── VISOR PDF INLINE ────────────────────────────────────────────────────────
function VisorPDF({url,titulo,onClose}){
  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.96)",zIndex:300,display:"flex",flexDirection:"column"}}>
    <div style={{padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",background:C.surf,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:16}}>📄</span>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:C.txt}}>{titulo||"Documento OT"}</div>
          <div style={{fontSize:10,color:C.txt3}}>Visualizador de documento</div>
        </div>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <a href={url} download style={{...BTNS,fontSize:11,padding:"5px 12px",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:5}}>
          ⬇ Descargar
        </a>
        <a href={url} target="_blank" rel="noreferrer" style={{...BTNS,fontSize:11,padding:"5px 12px",textDecoration:"none",display:"inline-flex",alignItems:"center",gap:5}}>
          ↗ Nueva pestaña
        </a>
        <button style={{...BTNS,padding:"5px 12px",fontSize:12,fontWeight:600}} onClick={onClose}>✕ Cerrar</button>
      </div>
    </div>
    <div style={{flex:1,position:"relative",background:"#1a1a1a"}}>
      <iframe
        src={url+"#toolbar=1&view=FitH"}
        style={{width:"100%",height:"100%",border:"none"}}
        title={titulo||"Documento OT"}
      />
    </div>
  </div>;
}

// ─── MODAL DETALLE MANTENCIÓN ─────────────────────────────────────────────────
function ModalDetalle({m,eq,cl,mt,setMt,setEq,setCl,onClose,bitacora=[]}){
  const e=eq.find(x=>x.id===m.eId)||{};
  const c=cl.find(x=>x.id===e.cId)||{};
  // Recalcular siempre desde mt: "OT actual" = última PREVENTIVA, nunca una correctiva
  const historialEq=mt.filter(x=>x.eId===e.id).sort((a,b)=>b.fR.localeCompare(a.fR));
  const latest=historialEq.find(esPreventiva)||null;
  const correctivas=historialEq.filter(x=>!esPreventiva(x));
  const dias=latest?diffD(latest.fP):null;
  const dp=latest?pill(dias):null;
  const ep=estP(e.est||"activo");
  const [subiendo,setSubiendo]=useState(false);
  const [adjErr,setAdjErr]=useState("");
  const [adjOk,setAdjOk]=useState(false);
  const [adjuntoActual,setAdjuntoActual]=useState(e.adjunto||null);
  const [adjuntoTs,setAdjuntoTs]=useState(e.adjuntoTs||null);
  const [verPDF,setVerPDF]=useState(false);
  const [showCorrForm,setShowCorrForm]=useState(false);
  const [corrForm,setCorrForm]=useState({tec:"",fR:toISO(today),actividad:"",obs:""});
  const [editCorrId,setEditCorrId]=useState(null);
  const [editOT,setEditOT]=useState(false);
  const [formOT,setFormOT]=useState(null);
  const [cIdOT,setCIdOT]=useState("");const [cliNuevoOT,setCliNuevoOT]=useState({nombre:"",cod:""});const [errOT,setErrOT]=useState("");
  const [editCorrFecha,setEditCorrFecha]=useState("");
  // URL con cache-bust: la misma ruta se sobreescribe en Blob, pero el navegador/CDN
  // puede servir la versión vieja en caché — el parámetro ?v= fuerza a pedir la nueva
  const pdfUrl=adjuntoActual?`${adjuntoActual}${adjuntoActual.includes("?")?"&":"?"}v=${adjuntoTs||Date.now()}`:null;
  // Sube PDF asociado al EQUIPO (no a la OT) — sobreescribe el anterior
  const handleAdjunto=async(ev)=>{
    const file=ev.target.files[0];if(!file)return;
    setSubiendo(true);setAdjErr("");
    try{
      const url=await subirAdjunto(file,e.id);
      if(url){
        const ts=Date.now();
        setEq(prev=>prev.map(x=>x.id===e.id?{...x,adjunto:url,adjuntoTs:ts}:x));
        setAdjuntoActual(url);setAdjuntoTs(ts);setAdjOk(true);
      }
    }catch(err){
      setAdjErr(`Error real: ${err.message}`);
    }
    setSubiendo(false);ev.target.value="";
  };
  // Corrige la fecha de una correctiva ya registrada (ej: la IA leyó mal el año del PDF)
  const guardarFechaCorrectiva=(id)=>{
    if(!editCorrFecha)return;
    setMt(prev=>prev.map(x=>x.id===id?{...x,fR:editCorrFecha,fP:editCorrFecha}:x));
    setEditCorrId(null);
  };
  // Registra un seguimiento correctivo — NUNCA modifica la fecha de próxima mantención preventiva
  const abrirEditOT=()=>{
    if(!latest)return;
    setFormOT({ot:latest.ot||"",tipo:latest.tipo||"Preventiva",tec:latest.tec||"",fR:latest.fR||"",fP:latest.fP||"",actividad:latest.actividad||"",obs:latest.obs||"",horometro:latest.horometro||""});
    setCIdOT(cl.some(x=>x.id===e.cId)?String(e.cId):"");setCliNuevoOT({nombre:"",cod:""});setErrOT("");
    setEditOT(true);
  };
  const guardarOT=()=>{
    if(!formOT||!formOT.fR)return;
    // Cliente / centro: se asigna al equipo de la OT (y por lo tanto a todo su historial)
    let cIdFinal=cIdOT;
    if(cIdOT==="__nuevo__"){
      const nombre=cliNuevoOT.nombre.trim();
      if(!nombre){setErrOT("Ingresa el nombre del nuevo cliente / centro.");return;}
      const existente=cl.find(x=>x.nombre.trim().toLowerCase()===nombre.toLowerCase());
      if(existente)cIdFinal=String(existente.id);
      else{
        cIdFinal=String(Date.now());
        if(setCl)setCl(prev=>[...prev,{id:Number(cIdFinal),nombre,cod:cliNuevoOT.cod.trim()}]);
      }
    }
    if(!cIdFinal){setErrOT("Selecciona el cliente / centro de la OT.");return;}
    if(Number(cIdFinal)!==e.cId)setEq(prev=>prev.map(x=>x.id===e.id?{...x,cId:Number(cIdFinal)}:x));
    setMt(prev=>prev.map(m=>m.id===latest.id?{...m,...formOT}:m));
    setEditOT(false);setFormOT(null);setErrOT("");
  };
  const guardarCorrectiva=()=>{
    if(!corrForm.tec||!corrForm.fR||!corrForm.actividad)return;
    setMt(prev=>[...prev,{id:nxtId(prev),eId:e.id,tec:corrForm.tec,fR:corrForm.fR,fP:corrForm.fR,tipo:"Correctiva",actividad:corrForm.actividad,ot:"",horometro:"",frecuencia:"",obs:corrForm.obs}]);
    setCorrForm({tec:"",fR:toISO(today),actividad:"",obs:""});
    setShowCorrForm(false);
  };
  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"1rem"}}>
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>
      {/* Header */}
      <div style={{padding:"1.1rem 1.3rem",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:15,fontWeight:700,color:C.txt,letterSpacing:"-0.02em"}}>{e.tipo||"—"}</div>
          <div style={{fontSize:11,color:C.txt3,marginTop:2}}>{e.marca} {e.modelo}</div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <Chip {...ep}/>
          <button style={{...BTNS,padding:"3px 10px",fontSize:12}} onClick={onClose}>✕</button>
        </div>
      </div>
      {/* Cuerpo */}
      <div style={{padding:"1.1rem 1.3rem",display:"flex",flexDirection:"column",gap:14}}>
        {/* Info equipo */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {[
            ["N° Serie",e.serie],["Marca / Modelo",`${e.marca} ${e.modelo}`],
            ["Sucursal",c.nombre?c.nombre+(c.cod?` (#${c.cod})`:""):"⚠ Sin asignar"],
            ["Horómetro",(latest?.horometro)?`${latest.horometro} hrs`:(e.ultimoHorometro?`${e.ultimoHorometro} hrs`:"—")],
          ].map(([k,v])=><div key={k} style={{background:C.surf,borderRadius:7,padding:"8px 10px",border:`1px solid ${C.border}`}}>
            <div style={{fontSize:9,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>{k}</div>
            <div style={{fontSize:12,color:C.txt,fontWeight:500}}>{v||"—"}</div>
          </div>)}
        </div>
        {/* OT preventiva actual */}
        {latest?<div style={{background:C.orangeL,border:`1px solid ${C.orangeB}`,borderRadius:9,padding:"10px 12px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:C.orange,textTransform:"uppercase",letterSpacing:"0.06em"}}>OT preventiva actual</div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <Chip {...dp}/>
              <button onClick={abrirEditOT} style={{fontSize:10,padding:"2px 8px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:"transparent",color:C.orange,fontFamily:"inherit"}}>
                ✏️ Editar OT
              </button>
            </div>
          </div>
                    {editOT&&formOT?<div style={{background:C.surf,border:`1px solid ${C.orangeB}`,borderRadius:8,padding:12,marginBottom:4}}>
              <div style={{fontSize:11,fontWeight:600,color:C.orange,marginBottom:10}}>✏️ Editando OT</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div style={{gridColumn:"1/-1"}}><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Cliente / Centro (sucursal)</div>
                  <select style={{...INP,width:"100%",fontSize:12,borderColor:cIdOT?C.border:C.red}} value={cIdOT} onChange={ev=>{setCIdOT(ev.target.value);setErrOT("");}}>
                    {!cIdOT&&<option value="">⚠ Seleccionar cliente / centro...</option>}
                    {cl.map(x=><option key={x.id} value={x.id}>{x.nombre}{x.cod?` (#${x.cod})`:""}</option>)}
                    <option value="__nuevo__">➕ Crear nuevo cliente / centro...</option>
                  </select>
                  {cIdOT==="__nuevo__"&&<div style={{display:"flex",gap:6,marginTop:6}}>
                    <input style={{...INP,flex:1,fontSize:12}} placeholder="Nombre del nuevo cliente / centro" value={cliNuevoOT.nombre} onChange={ev=>{setCliNuevoOT(p=>({...p,nombre:ev.target.value}));setErrOT("");}}/>
                    <input style={{...INP,width:90,fontSize:12}} placeholder="Código" value={cliNuevoOT.cod} onChange={ev=>setCliNuevoOT(p=>({...p,cod:ev.target.value}))}/>
                  </div>}
                  <div style={{fontSize:9,color:C.txt3,marginTop:3}}>Se asigna al equipo de esta OT ({e.marca} {e.modelo}) y a todo su historial.</div>
                </div>
                <div><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>N° OT</div>
                  <input style={{...INP,width:"100%",fontSize:12}} value={formOT.ot} onChange={e=>setFormOT(p=>({...p,ot:e.target.value}))}/></div>
                <div><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Tipo</div>
                  <select style={{...INP,width:"100%",fontSize:12}} value={formOT.tipo} onChange={e=>setFormOT(p=>({...p,tipo:e.target.value}))}>
                    <option>Preventiva</option><option>Correctiva</option><option>Diagnóstico</option><option>Garantía</option>
                  </select></div>
                <div style={{gridColumn:"1/-1"}}><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Técnico</div>
                  <input style={{...INP,width:"100%",fontSize:12}} value={formOT.tec} onChange={e=>setFormOT(p=>({...p,tec:e.target.value}))}/></div>
                <div><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Fecha Realizada</div>
                  <input type="date" style={{...INP,width:"100%",fontSize:12}} value={formOT.fR} onChange={e=>setFormOT(p=>({...p,fR:e.target.value}))}/></div>
                <div><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Próxima Mantención</div>
                  <input type="date" style={{...INP,width:"100%",fontSize:12}} value={formOT.fP} onChange={e=>setFormOT(p=>({...p,fP:e.target.value}))}/></div>
                <div><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Horómetro (hrs)</div>
                  <input type="number" style={{...INP,width:"100%",fontSize:12}} value={formOT.horometro} onChange={e=>setFormOT(p=>({...p,horometro:e.target.value}))}/></div>
                <div style={{gridColumn:"1/-1"}}><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Actividad</div>
                  <input style={{...INP,width:"100%",fontSize:12}} value={formOT.actividad} onChange={e=>setFormOT(p=>({...p,actividad:e.target.value}))}/></div>
                <div style={{gridColumn:"1/-1"}}><div style={{fontSize:9,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:3,fontWeight:600}}>Observaciones</div>
                  <textarea style={{...INP,width:"100%",fontSize:12,resize:"vertical",minHeight:60}} value={formOT.obs} onChange={e=>setFormOT(p=>({...p,obs:e.target.value}))}/></div>
              </div>
              {errOT&&<div style={{fontSize:11,color:"#F85149",marginBottom:8}}>⚠️ {errOT}</div>}
              <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                <button style={BTNS} onClick={()=>{setEditOT(false);setFormOT(null);setErrOT("");}}>Cancelar</button>
                <button style={BTNP} onClick={guardarOT}>💾 Guardar cambios</button>
              </div>
            </div>
            :<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}}>
            {[
              ["N° OT",latest.ot||"—"],["Tipo",latest.tipo||"—"],
              ["Técnico",latest.tec||"—"],["Frecuencia",e.frecDefault?`c/${e.frecDefault} días`:"—"],
              ["Fecha realizada",fmtCL(latest.fR)],["Próxima mantención",fmtCL(latest.fP)],
            ].map(([k,v])=><div key={k}>
              <div style={{fontSize:9,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{k}</div>
              <div style={{fontSize:12,color:C.txt,fontWeight:500}}>{v}</div>
            </div>)}
          </div>}
          {latest.actividad&&<div style={{marginBottom:6}}>
            <div style={{fontSize:9,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Actividad</div>
            <div style={{fontSize:12,color:C.txt}}>{latest.actividad}</div>
          </div>}
          {latest.obs&&<div style={{borderTop:`1px solid ${C.orangeB}`,paddingTop:6,marginTop:4}}>
            <div style={{fontSize:9,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Observaciones</div>
            <div style={{fontSize:11,color:C.txt2}}>{latest.obs}</div>
          </div>}
        </div>:<div style={{background:C.amberL,border:`1px solid ${C.amber}33`,borderRadius:9,padding:"10px 12px",fontSize:12,color:C.amber}}>
          ⚠ Sin OT preventiva registrada para este equipo aún.
        </div>}

        {/* Correctivas y seguimientos */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:"0.06em"}}>
              🔧 Correctivas y seguimientos {correctivas.length>0?`(${correctivas.length})`:""}
            </div>
            <button onClick={()=>setShowCorrForm(p=>!p)}
              style={{fontSize:11,padding:"3px 10px",borderRadius:5,cursor:"pointer",border:`1px solid ${C.amber}55`,background:showCorrForm?C.amber:C.amberL,color:showCorrForm?"#fff":C.amber,fontFamily:"inherit",fontWeight:600}}>
              {showCorrForm?"✕ Cancelar":"+ Registrar seguimiento"}
            </button>
          </div>

          {/* Formulario inline para registrar corrective */}
          {showCorrForm&&<div style={{background:C.amberL,border:`1px solid ${C.amber}44`,borderRadius:8,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:10,color:C.amber,marginBottom:8,fontWeight:500}}>
              Esto NO modifica la fecha de la próxima mantención preventiva — solo deja registro de la reparación.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div><LBL c="Técnico *"/><input style={INP} value={corrForm.tec} onChange={ev=>setCorrForm(p=>({...p,tec:ev.target.value}))} placeholder="Nombre del técnico"/></div>
              <div><LBL c="Fecha *"/><input type="date" style={INP} value={corrForm.fR} onChange={ev=>setCorrForm(p=>({...p,fR:ev.target.value}))}/></div>
              <div style={{gridColumn:"1/-1"}}><LBL c="Qué se hizo / solución *"/><input style={INP} value={corrForm.actividad} onChange={ev=>setCorrForm(p=>({...p,actividad:ev.target.value}))} placeholder="Ej: Se cambió fusible doblado, se selló filtración"/></div>
              <div style={{gridColumn:"1/-1"}}><LBL c="Observaciones"/><textarea style={{...INP,height:50,resize:"vertical"}} value={corrForm.obs} onChange={ev=>setCorrForm(p=>({...p,obs:ev.target.value}))}/></div>
            </div>
            <button style={{...BTNP,fontSize:12,padding:"5px 16px",opacity:(corrForm.tec&&corrForm.fR&&corrForm.actividad)?1:0.5}} onClick={guardarCorrectiva}>
              ✅ Guardar seguimiento
            </button>
          </div>}

          {correctivas.length===0&&!showCorrForm&&<div style={{fontSize:11,color:C.txt3,padding:"6px 0"}}>Sin correctivas registradas.</div>}
          {correctivas.length>0&&<div style={{display:"flex",flexDirection:"column",gap:5}}>
            {correctivas.map(corr=>{const isEdit=editCorrId===corr.id;return(
              <div key={corr.id} style={{background:C.surf,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 10px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                  {isEdit
                    ?<div style={{display:"flex",gap:5,alignItems:"center"}}>
                      <input type="date" style={{...INP,padding:"3px 8px",fontSize:11,width:140}} value={editCorrFecha} onChange={ev=>setEditCorrFecha(ev.target.value)} autoFocus/>
                      <button style={{...BTNP,fontSize:10,padding:"3px 9px"}} onClick={()=>guardarFechaCorrectiva(corr.id)}>OK</button>
                      <button style={{...BTNS,fontSize:10,padding:"3px 8px"}} onClick={()=>setEditCorrId(null)}>✕</button>
                    </div>
                    :<span style={{fontSize:11,fontWeight:600,color:C.txt}}>{fmtCL(corr.fR)}</span>}
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {corr.tec&&!isEdit&&<span style={{fontSize:10,color:C.txt3}}>{corr.tec}</span>}
                    {!isEdit&&<button title="Corregir fecha" onClick={()=>{setEditCorrId(corr.id);setEditCorrFecha(corr.fR);}}
                      style={{fontSize:10,padding:"1px 6px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.txt2,fontFamily:"inherit"}}>✏️</button>}
                  </div>
                </div>
                <div style={{fontSize:11,color:C.txt2}}>{corr.actividad}</div>
                {corr.obs&&<div style={{fontSize:10,color:C.txt3,marginTop:2}}>{corr.obs}</div>}
              </div>
            );})}
          </div>}
        </div>

        {/* Adjunto PDF */}
        <div>
          <div style={{fontSize:9,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
            {adjuntoActual?"Documento adjunto":"Adjuntar documento"}
          </div>
          {verPDF&&pdfUrl&&<VisorPDF url={pdfUrl} titulo={`${e.tipo} — ${e.marca} ${e.modelo}`} onClose={()=>setVerPDF(false)}/>}
          {adjuntoActual&&<button onClick={()=>setVerPDF(true)}
            style={{display:"flex",alignItems:"center",gap:8,background:C.orangeL,border:`1px solid ${C.orangeB}`,borderRadius:8,padding:"10px 12px",cursor:"pointer",width:"100%",marginBottom:6,textAlign:"left"}}>
            <span style={{fontSize:20}}>📄</span>
            <div><div style={{fontSize:12,color:C.orange,fontWeight:600}}>Ver documento OT</div><div style={{fontSize:10,color:C.txt3}}>Se abre en esta misma página</div></div>
            <span style={{marginLeft:"auto",fontSize:13,color:C.orange}}>▶</span>
          </button>}
          {adjOk&&<div style={{fontSize:11,color:C.green,marginBottom:6,fontWeight:500}}>✅ Documento adjuntado correctamente</div>}
          {adjErr&&<div style={{fontSize:11,color:C.red,marginBottom:6}}>{adjErr}</div>}
          <label style={{display:"flex",alignItems:"center",gap:8,background:C.surf,border:`1px dashed ${C.border}`,borderRadius:8,padding:"9px 12px",cursor:"pointer"}}>
            <span style={{fontSize:16}}>{subiendo?"⏳":"📎"}</span>
            <div>
              <div style={{fontSize:12,color:adjuntoActual?C.txt2:C.orange,fontWeight:500}}>
                {subiendo?"Subiendo archivo...":adjuntoActual?"Reemplazar documento":"Adjuntar PDF o imagen"}
              </div>
              <div style={{fontSize:10,color:C.txt3}}>PDF, JPG, PNG — máx. 10 MB</div>
            </div>
            <input type="file" accept=".pdf,image/*" style={{display:"none"}} onChange={handleAdjunto} disabled={subiendo}/>
          </label>
        </div>
      </div>
      {/* ── BITÁCORA DE CAMBIOS ──────────────────────────────── */}
      {(()=>{
        const bEq=(bitacora||[]).filter(b=>b.eId===e.id).sort((a,b)=>b.ts.localeCompare(a.ts));
        if(!bEq.length)return null;
        return <div style={{margin:"16px 0",padding:"0 1.3rem"}}>
          <div style={{fontSize:11,fontWeight:600,color:C.amber,textTransform:"uppercase",letterSpacing:1,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
            📋 Bitácora de Cambios
            <span style={{background:C.amber+"22",color:C.amber,borderRadius:20,padding:"1px 8px",fontSize:10}}>{bEq.length}</span>
          </div>
          {bEq.map((b,i)=>{
            const fecha=new Date(b.ts).toLocaleDateString("es-CL",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
            const colTipo=b.tipo==="Postergación"?C.amber:b.tipo==="Cambio Frecuencia"?C.orange:b.tipo==="Cambio Estado"?C.blue:C.green;
            return <div key={b.id||i} style={{background:C.surf,borderRadius:8,padding:"9px 12px",marginBottom:5,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{background:colTipo+"22",color:colTipo,borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:600}}>{b.tipo}</span>
                  <span style={{fontSize:12,color:C.txt}}>{b.campo}</span>
                </div>
                <span style={{fontSize:10,color:C.txt3}}>{fecha}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:5,fontSize:12}}>
                <span style={{color:C.txt3,textDecoration:"line-through"}}>{b.anterior}</span>
                <span style={{color:C.txt3}}>→</span>
                <span style={{color:C.txt,fontWeight:500}}>{b.nuevo}</span>
              </div>
              {b.motivo&&<div style={{marginTop:3,fontSize:11,color:C.txt2,fontStyle:"italic"}}>💬 {b.motivo}</div>}
            </div>;
          })}
        </div>;
      })()}
    </div>
  </div>;
}

// ─── PLANIFICADOR ─────────────────────────────────────────────────────────────
function Planificador({cl,eq,mt,setMt,setEq,onVerDetalle}){
  const PM_CICLO=[{label:"PM-1",color:"#4CAF50"},{label:"PM-2",color:C.blue},{label:"PM-3",color:C.amber},{label:"PM-Mayor",color:C.orange}];
  const getPMLabel=(mId,eId)=>{
    if(!eId)return null;
    const prevs=mt.filter(m=>m.eId===eId&&(m.tipo||"Preventiva").toLowerCase()==="preventiva")
                  .sort((a,b)=>a.fR.localeCompare(b.fR));
    const pos=prevs.findIndex(m=>m.id===mId);
    if(pos<0)return null;
    return PM_CICLO[pos%4];
  };
  const [modo,setModo]=useState("mensual");
  const [mesNum,setMesNum]=useState(today.getFullYear()*12+today.getMonth());
  const [semStart,setSemStart]=useState(startOfWeek(today));
  const [detalle,setDetalle]=useState(null);
  const [filtroCentros,setFiltroCentros]=useState([]); // [] = todos los centros
  const [showFiltro,setShowFiltro]=useState(false);
  const [buscarCentro,setBuscarCentro]=useState("");
  const mesY=Math.floor(mesNum/12);
  const mesM=mesNum%12;
  const gE=id=>eq.find(e=>e.id===id);
  const gC=id=>{const e=gE(id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const tabS=a=>({padding:"6px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit",borderRadius:7,fontWeight:a?600:400,background:a?C.orangeL:"transparent",border:a?`1px solid ${C.orangeB}`:"1px solid transparent",color:a?C.orange:C.txt2,transition:"all 0.15s"});
  const latMap=latestPreventivaByEq(mt); // el calendario programa según preventivas únicamente
  const curMtsAll=Object.values(latMap);
  // Filtro de centros/clientes — afecta tanto la vista en pantalla como el PDF exportado
  const curMts=filtroCentros.length===0?curMtsAll:curMtsAll.filter(m=>{const e=gE(m.eId);return e&&filtroCentros.includes(e.cId);});
  const clOrdenados=[...cl].sort((a,b)=>a.nombre.localeCompare(b.nombre));
  const clFiltrados=clOrdenados.filter(c=>c.nombre.toLowerCase().includes(buscarCentro.toLowerCase()));
  const toggleCentro=cId=>setFiltroCentros(prev=>prev.includes(cId)?prev.filter(x=>x!==cId):[...prev,cId]);

  // Navegación: un solo setMesNum garantiza re-render atómico
  const prevMes=()=>setMesNum(n=>n-1);
  const nextMes=()=>setMesNum(n=>n+1);

  // Color por estado de la mantención
  const colorEstado=m=>{
    const d=diffD(m.fP);
    if(d<0)return{bg:C.redL,border:`1px solid ${C.red}44`,dot:C.red,label:"Vencida"};
    if(d<=7)return{bg:C.amberL,border:`1px solid ${C.amber}44`,dot:C.amber,label:"Próxima"};
    return{bg:C.greenL,border:`1px solid ${C.green}44`,dot:C.green,label:"Programada"};
  };

  const semDays=Array.from({length:7},(_,i)=>addD(semStart,i));

  // Grilla del mes completo: desde el lunes de la semana del día 1 hasta el domingo de la semana del último día
  const primerDiaMes=new Date(mesY,mesM,1);
  const ultimoDiaMes=new Date(mesY,mesM+1,0);
  const inicioGrilla=startOfWeek(primerDiaMes);
  const finGrilla=startOfWeek(ultimoDiaMes);finGrilla.setDate(finGrilla.getDate()+6);
  const totalDiasGrilla=Math.round((finGrilla.getTime()-inicioGrilla.getTime())/864e5)+1;
  const diasGrillaMes=Array.from({length:totalDiasGrilla},(_,i)=>addD(inicioGrilla,i));
  const semanasMes=[];
  for(let i=0;i<diasGrillaMes.length;i+=7)semanasMes.push(diasGrillaMes.slice(i,i+7));

  // Tarjeta de mantención reutilizable (mes y semana) — incluye marca/modelo y serie
  const TarjetaMt=({m})=>{
    const e=gE(m.eId);const ce=colorEstado(m);
    return <div onClick={()=>onVerDetalle(m)} style={{background:ce.bg,border:ce.border,borderRadius:6,padding:"4px 6px",marginBottom:3,cursor:"pointer",transition:"all 0.15s"}}>
      <div style={{fontSize:9,fontWeight:700,color:C.txt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e?.tipo||"—"}</div>
      <div style={{fontSize:8,color:C.txt2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e?.marca} {e?.modelo}</div>
      <div style={{fontSize:7,color:C.txt3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{gC(m.eId)} · S/N {e?.serie}</div>
      {m.ot&&(()=>{
        const pm=esPreventiva(m)?getPMLabel(m.id,m.eId):null;
        return <div style={{fontSize:8,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
          <span style={{color:ce.dot}}>OT: {m.ot}</span>
          {pm&&<span style={{color:pm.color,background:pm.color+"22",borderRadius:4,padding:"0 4px",fontSize:7,fontWeight:700}}>({pm.label})</span>}
        </div>;
      })()}
    </div>;
  };



  return <div style={{display:"flex",flexDirection:"column",gap:12}}>
    {/* ModalDetalle ahora es global en App */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
      <div style={{display:"flex",gap:4}}>
        <button style={tabS(modo==="mensual")} onClick={()=>setModo("mensual")}>📅 Mensual</button>
        <button style={tabS(modo==="semanal")} onClick={()=>setModo("semanal")}>📋 Semanal</button>
        <div style={{position:"relative"}}>
          <button style={{...tabS(filtroCentros.length>0),display:"flex",alignItems:"center",gap:5}} onClick={()=>setShowFiltro(p=>!p)}>
            🔍 Filtrar {filtroCentros.length>0?<span style={{background:C.orange,color:"#fff",borderRadius:10,padding:"0px 6px",fontSize:10,fontWeight:700}}>{filtroCentros.length}</span>:""}
          </button>
          {showFiltro&&<div style={{position:"absolute",top:"110%",left:0,zIndex:100,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px",width:300,boxShadow:"0 10px 30px rgba(0,0,0,0.5)"}}>
            <input style={{...INP,fontSize:12,marginBottom:8}} placeholder="Buscar cliente/centro..." value={buscarCentro} onChange={e=>setBuscarCentro(e.target.value)}/>
            <div style={{display:"flex",gap:6,marginBottom:8}}>
              <button style={{...BTNS,fontSize:10,padding:"3px 8px"}} onClick={()=>setFiltroCentros(cl.map(c=>c.id))}>Seleccionar todos</button>
              <button style={{...BTNS,fontSize:10,padding:"3px 8px"}} onClick={()=>setFiltroCentros([])}>Limpiar</button>
            </div>
            <div style={{maxHeight:260,overflowY:"auto",display:"flex",flexDirection:"column",gap:2}}>
              {clFiltrados.map(c=>{
                const checked=filtroCentros.includes(c.id);
                return <label key={c.id} style={{display:"flex",alignItems:"center",gap:7,fontSize:12,padding:"4px 6px",borderRadius:5,cursor:"pointer",background:checked?C.orangeL:"transparent",color:checked?C.orange:C.txt2}}>
                  <input type="checkbox" checked={checked} onChange={()=>toggleCentro(c.id)} style={{accentColor:C.orange}}/>
                  {c.nombre}
                </label>;
              })}
              {clFiltrados.length===0&&<div style={{fontSize:11,color:C.txt3,padding:"8px 0",textAlign:"center"}}>Sin resultados</div>}
            </div>
            <button style={{...BTNP,fontSize:11,padding:"5px 0",width:"100%",marginTop:8}} onClick={()=>setShowFiltro(false)}>Aplicar</button>
          </div>}
        </div>
      </div>
      <div style={{fontSize:11,color:C.txt2}}>{curMts.filter(m=>{const d=diffD(m.fP);return d>=0&&d<=30;}).length} mantenciones próximos 30 días{filtroCentros.length>0?` (filtrado)`:""}</div>
    </div>

    {modo==="mensual"&&<div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <button style={{...BTNS,fontSize:12,padding:"5px 12px"}} onClick={prevMes}>← Anterior</button>
        <span style={{fontSize:14,fontWeight:700,color:C.txt,letterSpacing:"-0.01em"}}>{MESES[mesM]} {mesY}</span>
        <div style={{display:"flex",gap:6}}>
          <button style={{...BTNP,fontSize:11,padding:"5px 12px",display:"flex",alignItems:"center",gap:5}}
            onClick={()=>generarPDFCalendario(mesM,mesY,semanasMes,curMts,eq,cl,filtroCentros.length>0?cl.filter(c=>filtroCentros.includes(c.id)).map(c=>c.nombre):null)}>
            📄 Descargar PDF{filtroCentros.length>0?` (${filtroCentros.length})`:""}
          </button>
          <button style={{...BTNS,fontSize:12,padding:"5px 12px"}} onClick={nextMes}>Siguiente →</button>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6,marginBottom:6}}>
        {DIAS_CORTO.map(d=><div key={d} style={{fontSize:10,color:C.txt3,textAlign:"center",fontWeight:600,letterSpacing:"0.04em",padding:"2px 0"}}>{d}</div>)}
      </div>
      {semanasMes.map((semana,wi)=><div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:6,marginBottom:6}}>
        {semana.map((day,idx)=>{
          const iso=toISO(day);const dayMts=curMts.filter(m=>m.fP===iso);
          const isT=day.getTime()===today.getTime();
          const fueraDeMes=day.getMonth()!==mesM;
          return <div key={iso} style={{background:fueraDeMes?"rgba(255,255,255,0.015)":C.card,border:`1px solid ${isT?C.orangeB:C.border}`,borderRadius:10,padding:"8px 6px",minHeight:90,opacity:fueraDeMes?0.45:1}}>
            <div style={{textAlign:"center",marginBottom:6,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontSize:17,fontWeight:700,color:isT?C.orange:C.txt,letterSpacing:"-0.02em"}}>{day.getDate()}</div>
              {dayMts.length>0&&<div style={{fontSize:9,background:C.orangeL,color:C.orange,borderRadius:10,padding:"1px 6px",display:"inline-block",marginTop:1,fontWeight:600}}>{dayMts.length}</div>}
            </div>
            {dayMts.length===0?<div style={{fontSize:9,color:C.txt3,textAlign:"center",marginTop:10}}>–</div>
            :dayMts.map(m=><TarjetaMt key={m.id} m={m}/>)}
          </div>;
        })}
      </div>)}
      <div style={{marginTop:8,display:"flex",gap:14,fontSize:10,color:C.txt3}}>
        <span style={{color:C.red,fontWeight:500}}>● Vencida</span>
        <span style={{color:C.amber,fontWeight:500}}>● Próxima (7d)</span>
        <span style={{color:C.green,fontWeight:500}}>● Programada</span>
      </div>
    </div>}


    {modo==="semanal"&&<div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <button style={{...BTNS,fontSize:12,padding:"5px 12px"}} onClick={()=>setSemStart(addD(semStart,-7))}>← Anterior</button>
        <span style={{fontSize:13,fontWeight:700,color:C.txt,letterSpacing:"-0.01em"}}>{fmtCL(toISO(semStart))} — {fmtCL(toISO(addD(semStart,6)))}</span>
        <button style={{...BTNS,fontSize:12,padding:"5px 12px"}} onClick={()=>setSemStart(addD(semStart,7))}>Siguiente →</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:6}}>
        {semDays.map((day,idx)=>{
          const iso=toISO(day);const dayMts=curMts.filter(m=>m.fP===iso);const isT=day.getTime()===today.getTime();
          return <div key={iso} style={{background:C.card,border:`1px solid ${isT?C.orangeB:C.border}`,borderRadius:10,padding:"8px 6px",minHeight:90}}>
            <div style={{textAlign:"center",marginBottom:6,paddingBottom:4,borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontSize:9,color:isT?C.orange:C.txt3,fontWeight:600,letterSpacing:"0.04em"}}>{DIAS_CORTO[idx]}</div>
              <div style={{fontSize:17,fontWeight:700,color:isT?C.orange:C.txt,letterSpacing:"-0.02em"}}>{day.getDate()}</div>
              {dayMts.length>0&&<div style={{fontSize:9,background:C.orangeL,color:C.orange,borderRadius:10,padding:"1px 6px",display:"inline-block",marginTop:1,fontWeight:600}}>{dayMts.length}</div>}
            </div>
            {dayMts.length===0?<div style={{fontSize:9,color:C.txt3,textAlign:"center",marginTop:10}}>–</div>
            :dayMts.map(m=><TarjetaMt key={m.id} m={m}/>)}
          </div>;
        })}
      </div>
    </div>}
  </div>;
}

// ─── MODAL EDITAR EQUIPO ─────────────────────────────────────────────────────
const TIPOS_EQUIPO=["Brazo Articulado","Carro Golf","Elevador Tijeras","FC20","FC22","FC28","Grúa Horquilla","Grúa Pluma","Ordenador de Pasillo","Reach","Stacker Eléctrica","Transpaleta","Transpaleta Eléctrica","Otro"];
function ModalEditarEquipo({equipo,cl,latestMt,onSave,onClose}){
  const [motivo,setMotivo]=useState("");
  const [form,setForm]=useState({
    tipo:equipo.tipo||"",marca:equipo.marca||"",modelo:equipo.modelo||"",
    serie:equipo.serie||"",est:equipo.est||"activo",cId:cl.some(c=>c.id===equipo.cId)?String(equipo.cId):"",
    frecDefault:equipo.frecDefault||"",ultimaOT:latestMt?.ot||equipo.ultimaOT||"",ultimoHorometro:latestMt?.horometro||equipo.ultimoHorometro||"",
    fR:latestMt?.fR||"",fP:latestMt?.fP||""
  });
  const h=k=>ev=>setForm(p=>({...p,[k]:ev.target.value}));
  // Al cambiar fR o frecDefault, recalcular fP automáticamente
  const hFR=val=>{
    const n=parseInt(form.frecDefault);
    setForm(p=>({...p,fR:val,fP:n>0&&val?sigDiaHabilCid(toISO(addD(parseD(val),n)),Number(form.cId),cl):p.fP}));
  };
  const hFrec=val=>{
    const n=parseInt(val);
    setForm(p=>({...p,frecDefault:val,fP:n>0&&p.fR?sigDiaHabilCid(toISO(addD(parseD(p.fR),n)),Number(form.cId),cl):p.fP}));
  };
  const ok=form.tipo&&form.marca&&form.modelo&&form.serie&&form.cId;
  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"1rem"}}>
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"1.5rem",width:"100%",maxWidth:520,maxHeight:"90vh",overflowY:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:500,color:C.txt}}>✏️ Editar Equipo</div>
        <button style={{...BTNS,padding:"3px 10px",fontSize:12}} onClick={onClose}>✕</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        <div style={{gridColumn:"1/-1"}}>
          <LBL c="Tipo de equipo *"/>
          <select style={INP} value={form.tipo} onChange={h("tipo")}>
            {TIPOS_EQUIPO.map(t=><option key={t}>{t}</option>)}
          </select>
        </div>
        <div><LBL c="Marca *"/><input style={INP} value={form.marca} onChange={h("marca")} placeholder="Ej: Haulotte"/></div>
        <div><LBL c="Modelo *"/><input style={INP} value={form.modelo} onChange={h("modelo")} placeholder="Ej: Compact 10N"/></div>
        <div style={{gridColumn:"1/-1"}}><LBL c="N° Serie *"/><input style={INP} value={form.serie} onChange={h("serie")} placeholder="Número de serie"/></div>
        <div style={{gridColumn:"1/-1"}}>
          <LBL c="Sucursal *"/>
          <select style={{...INP,borderColor:form.cId?C.border:C.red}} value={form.cId} onChange={h("cId")}>
            {!form.cId&&<option value="">⚠ Seleccionar sucursal...</option>}
            {cl.map(c=><option key={c.id} value={c.id}>{c.nombre}{c.cod?` (#${c.cod})`:""}</option>)}
          </select>
        </div>
        <div>
          <LBL c="Estado"/>
          <select style={INP} value={form.est} onChange={h("est")}>
            <option value="activo">Activo</option>
            <option value="mantencion">En mantención</option>
            <option value="fuera">Fuera de servicio</option>
          </select>
        </div>
        <div>
          <LBL c="Frecuencia por defecto (días)"/>
          <input type="number" style={INP} value={form.frecDefault} onChange={e=>hFrec(e.target.value)} placeholder="Ej: 90"/>
          <div style={{display:"flex",gap:3,marginTop:4,flexWrap:"wrap"}}>
            {FREQS.map(([d,l])=><button key={d} style={{fontSize:9,padding:"2px 6px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:form.frecDefault===String(d)?C.orange:C.orangeL,color:form.frecDefault===String(d)?"#fff":C.orange,fontFamily:"inherit"}} onClick={()=>hFrec(String(d))}>{l}</button>)}
          </div>
        </div>
        <div><LBL c="Última OT"/><input style={INP} value={form.ultimaOT} onChange={h("ultimaOT")} placeholder="Ej: OT-001"/></div>
        <div><LBL c="Último horómetro (hrs)"/><input type="number" style={INP} value={form.ultimoHorometro} onChange={h("ultimoHorometro")} placeholder="Ej: 1250"/></div>
        {/* ── Sección fechas ── */}
        <div style={{gridColumn:"1/-1",borderTop:`0.5px solid ${C.border}`,paddingTop:10,marginTop:2}}>
          <div style={{fontSize:11,color:C.orange,fontWeight:500,marginBottom:8}}>📅 Fechas del servicio</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <LBL c="Fecha del servicio (OT)"/>
              <input type="date" style={INP} value={form.fR} onChange={e=>hFR(e.target.value)}/>
              {!latestMt&&<div style={{fontSize:9,color:C.txt3,marginTop:3}}>Sin OT previa — creará el primer registro</div>}
            </div>
            <div>
              <LBL c="Próxima mantención"/>
              <input type="date" style={{...INP,borderColor:form.fP?C.orangeB:C.border}} value={form.fP} onChange={e=>setForm(p=>({...p,fP:sigDiaHabilCid(e.target.value,Number(form.cId),cl)}))}/>
              {form.fP&&(()=>{const av=fpConAviso(form.fP,cIdExentoDiaHabil(Number(form.cId),cl));return av.ajustada?<div style={{fontSize:9,color:C.amber,marginTop:3}}>📅 Ajustada — {av.motivo}</div>:null;})()}
              {form.fR&&form.frecDefault&&<div style={{fontSize:9,color:C.txt3,marginTop:3}}>Auto-calculada según frecuencia</div>}
            </div>
          </div>
        </div>
      </div>
      <div style={{marginBottom:10}}>
        <div style={{fontSize:9,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Motivo del cambio (opcional)</div>
        <input style={{...INP,width:"100%",fontSize:12}} placeholder="Ej: Postergado por solicitud del cliente, equipo en mantención mayor..." value={motivo} onChange={e=>setMotivo(e.target.value)}/>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button style={BTNS} onClick={onClose}>Cancelar</button>
        <button style={{...BTNP,opacity:ok?1:0.5,cursor:ok?"pointer":"not-allowed"}} onClick={()=>ok&&onSave({...equipo,...form,cId:Number(form.cId),frecDefault:form.frecDefault,_fR:form.fR,_fP:form.fP,_mtId:latestMt?.id},motivo)}>Guardar cambios</button>
      </div>
    </div>
  </div>;
}

// ─── MODAL CONFIRMAR ELIMINACIÓN ─────────────────────────────────────────────
function ModalEliminar({equipo,nOTs,onConfirm,onClose}){
  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"1rem"}}>
    <div style={{background:C.card,border:`1px solid rgba(224,80,80,0.4)`,borderRadius:12,padding:"1.5rem",width:"100%",maxWidth:420}}>
      <div style={{fontSize:14,fontWeight:500,color:"#E05050",marginBottom:8}}>⚠ Eliminar equipo</div>
      <div style={{fontSize:13,color:C.txt,marginBottom:6}}>
        <strong>{equipo.tipo} — {equipo.marca} {equipo.modelo}</strong>
      </div>
      <div style={{fontSize:12,color:C.txt3,marginBottom:6}}>Serie: {equipo.serie}</div>
      {nOTs>0&&<div style={{background:C.redL,border:"0.5px solid rgba(224,80,80,0.3)",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#E05050",marginBottom:12}}>
        ⚠ Este equipo tiene <strong>{nOTs} OT{nOTs!==1?"s":""}</strong> registrada{nOTs!==1?"s":""}. Se eliminarán también.
      </div>}
      {nOTs===0&&<div style={{fontSize:12,color:C.txt2,marginBottom:12}}>Esta acción no se puede deshacer.</div>}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button style={BTNS} onClick={onClose}>Cancelar</button>
        <button style={{...BTNP,background:"#C03030"}} onClick={onConfirm}>Sí, eliminar</button>
      </div>
    </div>
  </div>;
}

// ─── CENTROS ─────────────────────────────────────────────────────────────────
function ModalNuevoEquipo({cl,eq,setCl,cIdInicial,onCrear,onClose}){
  const tipos=[...new Set([...TIPOS_EQUIPO.filter(t=>t!=="Otro"),...eq.map(e=>e.tipo).filter(Boolean)])].sort((a,b)=>a.localeCompare(b));
  const [f,setF]=useState({tipo:tipos[0]||"",marca:"",modelo:"",serie:"",cId:cIdInicial!=null?String(cIdInicial):"",frecDefault:""});
  const [tipoNuevo,setTipoNuevo]=useState("");
  const [cliNuevo,setCliNuevo]=useState({nombre:"",cod:""});
  const [err,setErr]=useState("");
  const esTipoNuevo=f.tipo==="__nuevo__";
  const esCliNuevo=f.cId==="__nuevo__";
  const guardar=()=>{
    const tipo=(esTipoNuevo?tipoNuevo:f.tipo).trim();
    const marca=f.marca.trim(),modelo=f.modelo.trim(),serie=f.serie.trim();
    if(!tipo){setErr("Indica el tipo de equipo.");return;}
    if(!marca||!modelo||!serie){setErr("Marca, modelo y N° de serie son obligatorios.");return;}
    if(eq.some(e=>String(e.serie||"").trim().toLowerCase()===serie.toLowerCase())){setErr("Ya existe un equipo con ese N° de serie.");return;}
    let cId=f.cId;
    if(esCliNuevo){
      const nombre=cliNuevo.nombre.trim();
      if(!nombre){setErr("Indica el nombre del nuevo cliente.");return;}
      if(cl.some(c=>c.nombre.trim().toLowerCase()===nombre.toLowerCase())){setErr("Ya existe un cliente con ese nombre; selecciónalo de la lista.");return;}
      cId=Date.now();
      setCl(prev=>[...prev,{id:cId,nombre,cod:cliNuevo.cod.trim()}]);
    }
    if(!cId){setErr("Selecciona el cliente / centro al que pertenece el equipo.");return;}
    const nuevo={id:nxtId(eq),cId:Number(cId),tipo,marca,modelo,serie,est:"activo",frecDefault:f.frecDefault?String(f.frecDefault):"",ultimaOT:"",ultimoHorometro:""};
    onCrear(nuevo);
  };
  const upd=k=>e=>{setF(p=>({...p,[k]:e.target.value}));setErr("");};
  return <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
    <div onClick={e=>e.stopPropagation()} style={{...CARD,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto"}}>
      <div style={{fontSize:16,fontWeight:700,color:C.txt,marginBottom:14}}>➕ Nuevo equipo</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div style={{gridColumn:"1/-1"}}>
          <LBL c="Cliente / Centro *"/>
          <select style={INP} value={f.cId} onChange={upd("cId")}>
            <option value="">Seleccionar cliente...</option>
            {cl.map(c=><option key={c.id} value={c.id}>{c.nombre}{c.cod?` (#${c.cod})`:""}</option>)}
            <option value="__nuevo__">➕ Crear nuevo cliente...</option>
          </select>
          {esCliNuevo&&<div style={{display:"flex",gap:6,marginTop:6}}>
            <input style={{...INP,flex:1}} placeholder="Nombre del nuevo cliente" value={cliNuevo.nombre} onChange={e=>{setCliNuevo(p=>({...p,nombre:e.target.value}));setErr("");}}/>
            <input style={{...INP,width:100}} placeholder="Código" value={cliNuevo.cod} onChange={e=>setCliNuevo(p=>({...p,cod:e.target.value}))}/>
          </div>}
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <LBL c="Tipo de equipo *"/>
          <select style={INP} value={f.tipo} onChange={upd("tipo")}>
            {tipos.map(t=><option key={t} value={t}>{t}</option>)}
            <option value="__nuevo__">➕ Crear nuevo tipo...</option>
          </select>
          {esTipoNuevo&&<input style={{...INP,marginTop:6}} placeholder="Nombre del nuevo tipo" value={tipoNuevo} onChange={e=>{setTipoNuevo(e.target.value);setErr("");}}/>}
        </div>
        <div><LBL c="Marca *"/><input style={INP} value={f.marca} onChange={upd("marca")} placeholder="Ej: Haulotte"/></div>
        <div><LBL c="Modelo *"/><input style={INP} value={f.modelo} onChange={upd("modelo")} placeholder="Ej: Xlt 1571 Dc"/></div>
        <div style={{gridColumn:"1/-1"}}><LBL c="N° Serie *"/><input style={INP} value={f.serie} onChange={upd("serie")} placeholder="Número de serie"/></div>
        <div>
          <LBL c="Frecuencia (días)"/>
          <input type="number" style={INP} value={f.frecDefault} onChange={upd("frecDefault")} placeholder="Ej: 90"/>
        </div>
        <div style={{display:"flex",alignItems:"flex-end",paddingBottom:2,flexWrap:"wrap",gap:4}}>
          {FREQS.map(([d,l])=><button key={d} type="button" style={{fontSize:9,padding:"2px 7px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:f.frecDefault===String(d)?C.orange:C.orangeL,color:f.frecDefault===String(d)?"#fff":C.orange,fontFamily:"inherit"}} onClick={()=>setF(p=>({...p,frecDefault:String(d)}))}>{l}</button>)}
        </div>
      </div>
      {err&&<div style={{fontSize:12,color:"#F85149",marginBottom:12}}>⚠️ {err}</div>}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button onClick={onClose} style={BTNS}>Cancelar</button>
        <button onClick={guardar} style={BTNP}>Crear equipo</button>
      </div>
    </div>
  </div>;
}

function Centros({cl,eq,mt,setEq,setMt,setCl,setBitacora,onVerDetalle,preseleccionar,onPreseleccionado}){
  const [sel,setSel]=useState(null);const [busq,setBusq]=useState("");const [busqSerie,setBusqSerie]=useState("");
  const [tabEq,setTabEq]=useState(null);
  const [editFreq,setEditFreq]=useState(null);const [tmpFreq,setTmpFreq]=useState("");
  const [editFecha,setEditFecha]=useState(null);const [tmpFechas,setTmpFechas]=useState({fR:"",fP:""});
  const [modalEdit,setModalEdit]=useState(null);
  const [modalDel,setModalDel]=useState(null);
  const [editNombre,setEditNombre]=useState(false);const [tmpNombre,setTmpNombre]=useState("");
  const [modalNuevoEq,setModalNuevoEq]=useState(null);
  const crearEquipo=nuevo=>{
    setEq(prev=>[...prev,{...nuevo,id:nxtId(prev)}]);
    setModalNuevoEq(null);
    setBusq("");setBusqSerie("");
    setSel(nuevo.cId);setTabEq(nuevo.id);
  };
  const [modalNuevo,setModalNuevo]=useState(false);const [nuevoForm,setNuevoForm]=useState({nombre:"",cod:""});const [nuevoErr,setNuevoErr]=useState("");
  const crearCentro=()=>{
    const nombre=nuevoForm.nombre.trim();const cod=nuevoForm.cod.trim();
    if(!nombre){setNuevoErr("Ingresa el nombre del cliente o centro.");return;}
    if(cl.some(c=>c.nombre.trim().toLowerCase()===nombre.toLowerCase())){setNuevoErr("Ya existe un centro con ese nombre.");return;}
    if(cod&&cl.some(c=>String(c.cod||"").trim()===cod)){setNuevoErr("Ya existe un centro con ese código.");return;}
    const newId=Date.now();
    setCl(prev=>[...prev,{id:newId,nombre,cod}]);
    setModalNuevo(false);setNuevoForm({nombre:"",cod:""});setNuevoErr("");
    setBusq("");setBusqSerie("");setSel(newId);
  };
  // Si el Dashboard (u otra pestaña) pide abrir un centro específico, lo seleccionamos automáticamente
  useEffect(()=>{
    if(preseleccionar!=null){
      setSel(preseleccionar);
      onPreseleccionado&&onPreseleccionado();
    }
  },[preseleccionar]);
  const saveFreq=eId=>{setEq(prev=>prev.map(e=>e.id===eId?{...e,frecDefault:tmpFreq}:e));setEditFreq(null);};
  const saveNombre=cId=>{
    if(!tmpNombre.trim())return;
    setCl(prev=>prev.map(c=>c.id===cId?{...c,nombre:tmpNombre.trim()}:c));
    setEditNombre(false);
  };
  const saveEdit=(updated,motivo)=>{
    // Registrar cambios en la bitácora — incluirlos en el objeto para que App los procese
    const eqAnterior=eq.find(e=>e.id===updated.id)||{};
    const cambios=[];
    const{_fR,_fP,_mtId,...eq_clean}=updated;
    const ts=new Date().toISOString();
    const idBit=Date.now();
    const mtActual=mt.filter(m=>m.eId===updated.id&&(m.tipo||"Preventiva").toLowerCase()==="preventiva").sort((a,b)=>b.fR.localeCompare(a.fR))[0];
    const fpAnterior=mtActual?.fP||"";
    const fpNueva=_fP||"";
    if(fpAnterior&&fpNueva&&fpAnterior!==fpNueva)cambios.push({id:idBit,eId:updated.id,ts,tipo:"Postergación",campo:"Próxima Mantención",anterior:fpAnterior,nuevo:fpNueva,motivo:motivo||""});
    else if(!fpAnterior&&fpNueva)cambios.push({id:idBit,eId:updated.id,ts,tipo:"Asignación",campo:"Próxima Mantención",anterior:"—",nuevo:fpNueva,motivo:motivo||""});
    if(eqAnterior.frecDefault&&updated.frecDefault&&String(eqAnterior.frecDefault)!==String(updated.frecDefault))cambios.push({id:idBit+1,eId:updated.id,ts,tipo:"Cambio Frecuencia",campo:"Frecuencia Default",anterior:eqAnterior.frecDefault+"d",nuevo:updated.frecDefault+"d",motivo:motivo||""});
    if(eqAnterior.est&&updated.est&&eqAnterior.est!==updated.est)cambios.push({id:idBit+2,eId:updated.id,ts,tipo:"Cambio Estado",campo:"Estado",anterior:eqAnterior.est,nuevo:updated.est,motivo:motivo||""});
    if(cambios.length>0&&setBitacora)setBitacora(prev=>[...prev,...cambios]);

    // Actualizar equipo
    setEq(prev=>prev.map(e=>e.id===updated.id?updated:e));
    // Actualizar fechas en mt si el usuario las cambió
    if(_fR||_fP){
      if(_mtId){
        setMt(prev=>prev.map(m=>m.id===_mtId?{...m,fR:_fR||m.fR,fP:_fP||m.fP}:m));
      } else if(_fR&&_fP){
        setMt(prev=>[...prev,{id:nxtId(prev),eId:updated.id,fR:_fR,fP:_fP,tipo:"Preventiva",tec:"",ot:updated.ultimaOT||"",actividad:"",horometro:updated.ultimoHorometro||"",frecuencia:updated.frecDefault||"",obs:""}]);
      }
    }
    setModalEdit(null);
  };
  const saveFecha=mId=>{
    setMt(prev=>prev.map(m=>{
      if(m.id!==mId)return m;
      const fr=tmpFechas.fR||m.fR;const fp=tmpFechas.fP||m.fP;
      return{...m,fR:fr,fP:fp};
    }));
    setEditFecha(null);
  };
  const confirmDel=eId=>{
    setEq(prev=>prev.filter(e=>e.id!==eId));
    setMt(prev=>prev.filter(m=>m.eId!==eId));
    setModalDel(null);setTabEq(null);
  };
  if(sel){
    const c=cl.find(x=>x.id===sel);const eqs=eq.filter(e=>e.cId===sel);
    const getMts=id=>mt.filter(m=>m.eId===id).sort((a,b)=>b.fR.localeCompare(a.fR));
    const getP=id=>Object.values(latestPreventivaByEq(mt.filter(m=>m.eId===id)))[0];

    const byTipo={};eqs.forEach(e=>{if(!byTipo[e.tipo])byTipo[e.tipo]=[];byTipo[e.tipo].push(e);});
    const allOTs=mt.filter(m=>eqs.some(e=>e.id===m.eId)&&m.ot).sort((a,b)=>b.fR.localeCompare(a.fR));
    return <div>
      {modalNuevoEq&&<ModalNuevoEquipo cl={cl} eq={eq} setCl={setCl} cIdInicial={modalNuevoEq.cId} onCrear={crearEquipo} onClose={()=>setModalNuevoEq(null)}/>}
      {modalEdit&&<ModalEditarEquipo equipo={modalEdit} cl={cl} latestMt={mt.filter(m=>m.eId===modalEdit.id&&esPreventiva(m)).sort((a,b)=>b.fR.localeCompare(a.fR))[0]||null} onSave={saveEdit} onClose={()=>setModalEdit(null)}/>}
      {modalDel&&<ModalEliminar equipo={modalDel} nOTs={mt.filter(m=>m.eId===modalDel.id).length} onConfirm={()=>confirmDel(modalDel.id)} onClose={()=>setModalDel(null)}/>}
      <button style={{...BTNS,marginBottom:12,fontSize:12}} onClick={()=>{setSel(null);setTabEq(null);setEditFreq(null);setEditNombre(false);}}>← Volver</button>
      <div style={{...CARD,marginBottom:12,borderLeft:`2px solid ${C.orange}`}}>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <div>
            {editNombre?
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                <input style={{...INP,width:240,padding:"4px 8px",fontSize:14}} value={tmpNombre} onChange={ev=>setTmpNombre(ev.target.value)} autoFocus/>
                <button style={{...BTNP,fontSize:11,padding:"4px 10px"}} onClick={()=>saveNombre(c.id)}>Guardar</button>
                <button style={{...BTNS,fontSize:11,padding:"4px 8px"}} onClick={()=>setEditNombre(false)}>✕</button>
              </div>
              :<div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:15,fontWeight:500,color:C.txt}}>{c.nombre}</div>
                <button title="Editar nombre" onClick={()=>{setEditNombre(true);setTmpNombre(c.nombre);}}
                  style={{fontSize:11,padding:"1px 7px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.txt2,fontFamily:"inherit"}}>✏️</button>
              </div>}
            <div style={{fontSize:12,color:C.txt2}}>{c.cod?`Código ${c.cod} · `:""}{eqs.length} equipos · {allOTs.length} OTs</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
            <button onClick={()=>setModalNuevoEq({cId:c.id})} style={{...BTNP,padding:"6px 12px",fontSize:12,whiteSpace:"nowrap"}}>➕ Nuevo equipo</button>
            <div style={{fontSize:11,color:C.txt3}}>{[...new Set(eqs.map(e=>e.marca))].join(" · ")}</div>
          </div>
        </div>
      </div>
      {allOTs.length>0&&<div style={{...CARD,marginBottom:12,background:"rgba(232,113,26,0.04)"}}>
        <div style={{fontSize:12,fontWeight:500,color:C.orange,marginBottom:8}}>📋 Últimas OTs</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:6}}>
          {allOTs.slice(0,6).map(m=>{const e=eq.find(x=>x.id===m.eId);return(
            <div key={m.id} style={{background:C.surf,border:`1px solid ${C.orangeB}`,borderRadius:6,padding:"7px 9px"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{fontSize:11,fontWeight:500,color:C.orange}}>OT: {m.ot}</span><span style={{fontSize:10,color:C.txt3}}>{fmtCL(m.fR)}</span></div>
              <div style={{fontSize:11,color:C.txt}}>{e?.tipo} — {e?.marca} {e?.modelo}</div>
              <div style={{fontSize:10,color:C.txt3}}>Serie: {e?.serie}</div>
              {m.actividad&&<div style={{fontSize:10,color:C.txt2,marginTop:2}}>{m.actividad}</div>}
              {m.horometro&&<div style={{fontSize:10,color:"#5090C0"}}>⏱ {m.horometro} hrs</div>}
            </div>
          );})}
        </div>
      </div>}
      {Object.entries(byTipo).map(([tipo,items])=>(
        <div key={tipo} style={{marginBottom:12}}>
          <div style={{fontSize:11,color:C.orange,fontWeight:500,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>{tipo} ({items.length})</div>
          {items.map(e=>{
            const serieMatch=busqSerie.trim().length>1?busqSerie.trim().toLowerCase():null;
            const p=getP(e.id);const ep=estP(e.est);const hist=getMts(e.id);const isOpen=tabEq===e.id||(serieMatch&&e.serie&&e.serie.toLowerCase().includes(serieMatch)&&tabEq===null);const isEditF=editFreq===e.id;
            const freqDisplay=e.frecDefault?`${e.frecDefault}d`:"— Sin definir";
            return <div key={e.id} style={{...CARD,marginBottom:6,padding:"0.75rem 1rem"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <div style={{cursor:"pointer",flex:1}} onClick={()=>setTabEq(isOpen?null:e.id)}>
                  <span style={{fontSize:12,fontWeight:500,color:C.txt}}>{e.marca} {e.modelo}</span>
                  <span style={{fontSize:11,color:serieMatch&&e.serie&&e.serie.toLowerCase().includes(serieMatch)?C.orange:C.txt3,marginLeft:8,fontWeight:serieMatch&&e.serie&&e.serie.toLowerCase().includes(serieMatch)?700:400}}>
                  Serie: {e.serie}
                  {serieMatch&&e.serie&&e.serie.toLowerCase().includes(serieMatch)&&<span style={{marginLeft:4,fontSize:9,background:C.orange,color:"#fff",borderRadius:4,padding:"0 4px"}}>✓ encontrado</span>}
                </span>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <Chip {...ep}/>
                  {(()=>{const anyOT=mt.find(m=>m.eId===e.id);return anyOT?<button
                    title="Ver detalle OT"
                    onClick={e2=>{e2.stopPropagation();onVerDetalle&&onVerDetalle(anyOT);}}
                    style={{fontSize:11,padding:"2px 8px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:C.orangeL,color:C.orange,fontFamily:"inherit",lineHeight:1.4,fontWeight:600}}
                  >⬡ OT</button>:null;})()}
                  <button
                    title="Editar equipo"
                    onClick={e2=>{e2.stopPropagation();setModalEdit(e);}}
                    style={{fontSize:12,padding:"2px 8px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.txt2,fontFamily:"inherit",lineHeight:1.4}}
                  >✏️</button>
                  <button
                    title="Eliminar equipo"
                    onClick={e2=>{e2.stopPropagation();setModalDel(e);}}
                    style={{fontSize:12,padding:"2px 8px",borderRadius:4,cursor:"pointer",border:"0.5px solid rgba(224,80,80,0.3)",background:"transparent",color:"#E05050",fontFamily:"inherit",lineHeight:1.4}}
                  >🗑</button>
                  <span style={{fontSize:10,color:C.txt3,cursor:"pointer"}} onClick={()=>setTabEq(isOpen?null:e.id)}>{isOpen?"▲":"▼"}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:6}}>
                {(p?.ot||e.ultimaOT)&&<span style={{fontSize:11,color:C.orange}}>OT: {p?.ot||e.ultimaOT}</span>}
                {e.ultimoHorometro&&<span style={{fontSize:11,color:"#5090C0"}}>⏱ {e.ultimoHorometro} hrs</span>}
                {p&&<span style={{fontSize:11,color:C.txt2}}>Próxima: {fmtCL(p.fP)} <Chip {...pill(diffD(p.fP))} size={9}/></span>}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,padding:"5px 8px",background:"rgba(255,255,255,0.03)",borderRadius:5,border:`1px solid ${C.border}`}}>
                <span style={{fontSize:11,color:C.txt3,flexShrink:0}}>Frecuencia:</span>
                {isEditF
                  ?<div style={{display:"flex",gap:4,alignItems:"center",flex:1,flexWrap:"wrap"}}>
                    <input type="number" style={{...INP,width:80,padding:"3px 8px",fontSize:12}} value={tmpFreq} onChange={ev=>setTmpFreq(ev.target.value)} autoFocus/>
                    <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>{FREQS.map(([d,l])=><button key={d} style={{fontSize:9,padding:"2px 6px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:C.orangeL,color:C.orange,fontFamily:"inherit"}} onClick={()=>setTmpFreq(String(d))}>{l}</button>)}</div>
                    <button style={{...BTNP,fontSize:11,padding:"3px 10px"}} onClick={()=>saveFreq(e.id)}>OK</button>
                    <button style={{...BTNS,fontSize:11,padding:"3px 8px"}} onClick={()=>setEditFreq(null)}>✕</button>
                  </div>
                  :<div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                    <span style={{fontSize:11,color:freqDisplay.includes("Sin")?"#E05050":C.orange,fontWeight:500}}>{freqDisplay}</span>
                  </div>}
              </div>
              {isOpen&&<div style={{marginTop:8,borderTop:`0.5px solid ${C.border}`,paddingTop:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontSize:11,fontWeight:500,color:C.txt}}>Historial — más reciente primero</span>
                  <span style={{fontSize:10,color:C.txt3}}>{hist.length} OT{hist.length!==1?"s":""}</span>
                </div>
                {hist.length===0?<div style={{fontSize:11,color:C.txt3}}>Sin registros</div>
                :(()=>{const latestPrev=hist.find(esPreventiva);return hist.map((m,idx)=>{const tp=tipoP(m.tipo);const isEditM=editFecha===m.id;const esActual=latestPrev&&m.id===latestPrev.id;return(
                  <div key={m.id} style={{padding:"8px 0",borderBottom:`0.5px solid rgba(255,255,255,0.05)`,borderLeft:esActual?`2px solid ${C.orange}`:"none",paddingLeft:esActual?8:0}}>
                    {esActual&&<div style={{fontSize:9,color:C.orange,fontWeight:500,marginBottom:3,textTransform:"uppercase"}}>▲ OT preventiva actual</div>}
                    {!esActual&&!esPreventiva(m)&&<div style={{fontSize:9,color:C.amber,fontWeight:500,marginBottom:3,textTransform:"uppercase"}}>🔧 Correctiva — no afecta la próxima mantención</div>}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <div style={{display:"flex",gap:6,alignItems:"center"}}><Chip {...tp} l={m.tipo}/><span style={{fontSize:11,color:C.txt2}}>{fmtCL(m.fR)}</span>{m.tec&&<span style={{fontSize:10,color:C.txt3}}>{m.tec}</span>}</div>
                      <div style={{display:"flex",gap:5,alignItems:"center"}}>
                        <Chip {...pill(diffD(m.fP))} size={10}/>
                        <button title="Editar fechas" onClick={()=>{setEditFecha(isEditM?null:m.id);setTmpFechas({fR:m.fR,fP:m.fP});}}
                          style={{fontSize:10,padding:"1px 6px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.txt2,fontFamily:"inherit",lineHeight:1.5}}>✏️</button>
                      </div>
                    </div>
                    {isEditM&&<div style={{background:"rgba(232,113,26,0.06)",border:`1px solid ${C.orangeB}`,borderRadius:6,padding:"8px 10px",marginBottom:6}}>
                      <div style={{fontSize:10,color:C.orange,marginBottom:6}}>Editar fechas de esta OT</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                        <div><LBL c="Fecha realizada"/><input type="date" style={INP} value={tmpFechas.fR} onChange={ev=>setTmpFechas(p=>({...p,fR:ev.target.value}))}/></div>
                        <div><LBL c="Próxima fecha"/><input type="date" style={{...INP,borderColor:C.orangeB}} value={tmpFechas.fP} onChange={ev=>setTmpFechas(p=>({...p,fP:ev.target.value}))}/></div>
                      </div>
                      <div style={{display:"flex",gap:6}}>
                        <button style={{...BTNP,fontSize:11,padding:"4px 14px"}} onClick={()=>saveFecha(m.id)}>Guardar</button>
                        <button style={{...BTNS,fontSize:11,padding:"4px 10px"}} onClick={()=>setEditFecha(null)}>Cancelar</button>
                      </div>
                    </div>}
                    <OTCard m={m}/>
                  </div>
                );});})()}
              </div>}
            </div>;
          })}
        </div>
      ))}
    </div>;
  }
  const eqPorSerie=busqSerie.trim().length>1
    ?new Set(eq.filter(e=>(e.serie||"").toLowerCase().includes(busqSerie.trim().toLowerCase())).map(e=>e.cId))
    :null;
  const huerfanos=eq.filter(e=>!cl.some(c=>c.id===e.cId));
  const filtrados=cl.filter(c=>{
    if(busq&&!c.nombre.toLowerCase().includes(busq.toLowerCase())&&!String(c.cod||"").includes(busq))return false;
    if(eqPorSerie&&!eqPorSerie.has(c.id))return false;
    return true;
  });
  return <div>
    <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center"}}>
      <input style={{...INP,flex:1}} placeholder="Buscar centro..." value={busq} onChange={e=>setBusq(e.target.value)}/>
      <input style={{...INP,flex:1,maxWidth:220}} placeholder="🔍 Buscar por N° serie..." value={busqSerie} onChange={e=>setBusqSerie(e.target.value)}/>
      {busqSerie.trim()&&<button onClick={()=>setBusqSerie("")} style={{...BTNS,padding:"5px 10px",fontSize:11,flexShrink:0}}>✕</button>}
      <span style={{fontSize:12,color:C.txt2,flexShrink:0}}>{filtrados.length} centros · {eq.length} equipos</span>
      <button onClick={()=>setModalNuevoEq({cId:null})} style={{...BTNS,padding:"7px 14px",flexShrink:0,whiteSpace:"nowrap",borderColor:C.orangeB,color:C.orange}}>➕ Nuevo equipo</button>
      <button onClick={()=>{setNuevoForm({nombre:"",cod:""});setNuevoErr("");setModalNuevo(true);}} style={{...BTNP,padding:"7px 14px",flexShrink:0,whiteSpace:"nowrap"}}>➕ Nuevo centro</button>
    </div>
    {modalNuevoEq&&<ModalNuevoEquipo cl={cl} eq={eq} setCl={setCl} cIdInicial={modalNuevoEq.cId} onCrear={crearEquipo} onClose={()=>setModalNuevoEq(null)}/>}
    {modalEdit&&<ModalEditarEquipo equipo={modalEdit} cl={cl} latestMt={mt.filter(m=>m.eId===modalEdit.id&&esPreventiva(m)).sort((a,b)=>b.fR.localeCompare(a.fR))[0]||null} onSave={saveEdit} onClose={()=>setModalEdit(null)}/>}
    {huerfanos.length>0&&<div style={{...CARD,marginBottom:12,borderLeft:`2px solid ${C.red}`,background:"rgba(248,81,73,0.06)"}}>
      <div style={{fontSize:12,fontWeight:600,color:"#F85149",marginBottom:8}}>⚠️ {huerfanos.length} equipo{huerfanos.length!==1?"s":""} sin sucursal asignada</div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {huerfanos.map(e=><div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,fontSize:12,color:C.txt}}>
          <span>{e.tipo} — {e.marca} {e.modelo} <span style={{color:C.txt3}}>· Serie {e.serie}</span></span>
          <button onClick={()=>setModalEdit(e)} style={{...BTNP,padding:"4px 12px",fontSize:11,flexShrink:0}}>Asignar sucursal</button>
        </div>)}
      </div>
    </div>}
    {modalNuevo&&<div onClick={()=>setModalNuevo(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{...CARD,width:"100%",maxWidth:420}}>
        <div style={{fontSize:16,fontWeight:700,color:C.txt,marginBottom:14}}>➕ Nuevo cliente / centro</div>
        <label style={{fontSize:12,color:C.txt2,display:"block",marginBottom:4}}>Nombre del cliente o centro *</label>
        <input autoFocus style={{...INP,marginBottom:12}} placeholder="Ej: Clínica Las Condes" value={nuevoForm.nombre} onChange={e=>{setNuevoForm(p=>({...p,nombre:e.target.value}));setNuevoErr("");}} onKeyDown={e=>{if(e.key==="Enter")crearCentro();}}/>
        <label style={{fontSize:12,color:C.txt2,display:"block",marginBottom:4}}>Código (opcional)</label>
        <input style={{...INP,marginBottom:12}} placeholder="Ej: 1234" value={nuevoForm.cod} onChange={e=>{setNuevoForm(p=>({...p,cod:e.target.value}));setNuevoErr("");}} onKeyDown={e=>{if(e.key==="Enter")crearCentro();}}/>
        {nuevoErr&&<div style={{fontSize:12,color:"#F85149",marginBottom:12}}>⚠️ {nuevoErr}</div>}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={()=>setModalNuevo(false)} style={BTNS}>Cancelar</button>
          <button onClick={crearCentro} style={BTNP}>Crear centro</button>
        </div>
      </div>
    </div>}
    <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10}}>
      {filtrados.map(c=>{
        const eqs=eq.filter(e=>e.cId===c.id);
        const cur=eqs.map(e=>latestPreventivaByEq(mt.filter(m=>m.eId===e.id))[e.id]).filter(Boolean);
        const venc=cur.filter(m=>diffD(m.fP)<0).length;
        const prox7=cur.filter(m=>{const d=diffD(m.fP);return d>=0&&d<=7;}).length;
        const otCount=mt.filter(m=>eqs.some(e=>e.id===m.eId)&&m.ot).length;
        return <div key={c.id} className='sm-card-hover' style={{...CARD,borderLeft:`3px solid ${venc>0?C.red:C.orange}`,cursor:"pointer",transition:"all 0.2s"}} onClick={()=>setSel(c.id)}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
            <div><div style={{fontSize:13,fontWeight:500,color:C.txt}}>{c.nombre}</div>{c.cod&&<div style={{fontSize:10,color:C.txt3}}>Código {c.cod}</div>}</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
              {venc>0&&<Chip bg={C.redL} c="#E05050" l={`${venc} venc.`}/>}
              {prox7>0&&<Chip bg={C.amberL} c="#D08840" l={`${prox7} próx.`}/>}
              {venc===0&&prox7===0&&cur.length>0&&<Chip bg={C.greenL} c="#50B060" l="Al día"/>}
            </div>
          </div>
          <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>{eqs.length} equipo{eqs.length!==1?"s":""} · {[...new Set(eqs.map(e=>e.marca))].slice(0,3).join(", ")}</div>
          {otCount>0&&<div style={{fontSize:10,color:C.orange,marginBottom:4}}>📋 {otCount} OT{otCount>1?"s":""}</div>}
          <div style={{fontSize:11,color:C.orange}}>Ver detalle →</div>
        </div>;
      })}
    </div>
  </div>;
}

// ─── PDF OT READER ────────────────────────────────────────────────────────────
// NOTA: Requiere api/read-ot.js y ANTHROPIC_API_KEY en Vercel → Environment Variables
function PDFReader({cl,setCl,eq,setEq,mt,setMt}){
  const [loading,setLoading]=useState(false);
  const [form,setForm]=useState(null);
  const [ok,setOk]=useState(false);
  const [err,setErr]=useState("");
  const [nuevoEq,setNuevoEq]=useState(false);
  const [nForm,setNForm]=useState({tipo:"Elevador Tijeras",marca:"",modelo:"",serie:"",cId:"",est:"activo",frecDefault:""});
  const [nuevoTipoStr,setNuevoTipoStr]=useState("");
  const [nuevaSucursalForm,setNuevaSucursalForm]=useState({nombre:"",cod:""});
  const [tiposLocales,setTiposLocales]=useState(TIPOS_EQUIPO);
  const [pdfOrigFile,setPdfOrigFile]=useState(null); // PDF original para guardar como adjunto
  const [sugeridos,setSugeridos]=useState([]); // candidatos similares cuando no hay match exacto

  const readPDF=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    setLoading(true);setErr("");setForm(null);setNuevoEq(false);
    setPdfOrigFile(file); // guardar referencia para adjuntar al equipo
    try{
      const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(file);});
      const resp=await fetch("/api/read-ot",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({base64})});
      if(!resp.ok)throw new Error("Error en servidor");
      const data=await resp.json();
      const txt=data.content?.find(b=>b.type==="text")?.text||"{}";
      const parsed=JSON.parse(txt.replace(/```json|```/g,"").trim());
      // ── FRECUENCIA: usar frecDefault del equipo para calcular próxima ──
      const fR=safeDate(parsed.fecha_realizada)||toISO(today);
      const{exacto:equipo,sugeridos}=buscarEquipo(parsed.serie,parsed.marca,parsed.modelo,eq);
      const cIdDetectado=equipo?.cId;
      // Frecuencia siempre desde frecDefault (predefinida en el equipo)
      const freqD=equipo&&parseInt(equipo.frecDefault)>0?parseInt(equipo.frecDefault):0;
      parsed.frecuencia=freqD>0?String(freqD):"";
      // Próxima fecha: calculada automáticamente con frecDefault
      parsed.fecha_proxima=freqD>0&&fR
        ?sigDiaHabilCid(toISO(addD(parseD(fR),freqD)),cIdDetectado,cl)
        :"";
      // Prellenar nForm con serie detectada para facilitar creación
      setNForm(p=>({...p,serie:parsed.serie||"",marca:parsed.marca||"",modelo:parsed.modelo||""}));
      setSugeridos(sugeridos);
      setForm({eId:equipo?.id?.toString()||"",tec:parsed.tecnico||"",fR,fP:safeDate(parsed.fecha_proxima)||"",tipo:parsed.tipo||"Preventiva",actividad:parsed.actividad||"",ot:parsed.ot||"",horometro:parsed.horometro||"",frecuencia:parsed.frecuencia||"",obs:parsed.observaciones||"",_enc:equipo?`${equipo.tipo} — ${equipo.marca} ${equipo.modelo} [${equipo.serie}]`:null,_txt:parsed.serie||parsed.equipo||"",_sinFreq:!freqD||freqD===0});
    }catch(ex){setErr("No se pudo procesar el PDF. Verifica el archivo y que ANTHROPIC_API_KEY esté en Vercel.");}
    setLoading(false);e.target.value="";
  };

  const crearYSeleccionar=()=>{
    if(!nForm.tipo||!nForm.marca||!nForm.modelo||!nForm.serie||!nForm.cId)return;
    let cIdFinal=nForm.cId;
    if(cIdFinal==="__nueva__"){
      const nombreSuc=nuevaSucursalForm.nombre.trim();
      if(!nombreSuc){setErr("Ingresa el nombre de la nueva sucursal.");return;}
      const existente=cl.find(c=>c.nombre.trim().toLowerCase()===nombreSuc.toLowerCase());
      if(existente)cIdFinal=String(existente.id);
      else{
        cIdFinal=String(Date.now());
        setCl(prev=>[...prev,{id:Number(cIdFinal),nombre:nombreSuc,cod:nuevaSucursalForm.cod.trim()}]);
      }
      setNuevaSucursalForm({nombre:"",cod:""});
    }
    if(!Number.isFinite(Number(cIdFinal)))return;
    const nuevoId=nxtId(eq);
    const nuevo={...nForm,id:nuevoId,cId:Number(cIdFinal),est:"activo",ultimaOT:"",ultimoHorometro:""};
    setEq(prev=>[...prev,nuevo]);
    setForm(p=>({...p,eId:String(nuevoId),_enc:`${nuevo.tipo} — ${nuevo.marca} ${nuevo.modelo} [${nuevo.serie}]`,_txt:""}));
    setNuevoEq(false);
  };

  const save=async()=>{
    if(!form.eId||!form.fR||!form.fP)return;
    if(form.ot&&mt.some(m=>m.eId===Number(form.eId)&&m.ot===form.ot)){setErr(`OT "${form.ot}" ya registrada.`);return;}
    const eId=Number(form.eId);
    const fPSeguro=corregirFP(form.fR,form.fP,form.frecuencia,eId,eq,cl);
    setMt(p=>[...p,{...form,fP:fPSeguro,id:nxtId(p),eId}]);
    setEq(prev=>prev.map(e=>e.id===eId?{...e,ultimaOT:(form.tipo||"Preventiva").toLowerCase()==="preventiva"?(form.ot||e.ultimaOT):e.ultimaOT,ultimoHorometro:form.horometro||e.ultimoHorometro}:e));
    // Subir PDF original como adjunto del equipo (en segundo plano)
    if(pdfOrigFile){
      subirAdjunto(pdfOrigFile,eId).then(url=>{
        if(url)setEq(prev=>prev.map(e=>e.id===eId?{...e,adjunto:url,adjuntoTs:Date.now()}:e));
      }).catch(err=>console.error("Error al subir PDF original:",err.message));
    }
    setForm(null);setNuevoEq(false);setPdfOrigFile(null);setOk(true);setTimeout(()=>setOk(false),4000);
  };

  const nOk=nForm.tipo&&nForm.marca&&nForm.modelo&&nForm.serie&&nForm.cId&&(nForm.cId!=="__nueva__"||nuevaSucursalForm.nombre.trim());

  return <div style={CARD}>
    <div style={{fontSize:14,fontWeight:500,color:C.orange,marginBottom:4}}>Subir Orden de Trabajo — PDF</div>
    <div style={{fontSize:12,color:C.txt2,marginBottom:16}}>La IA extrae datos y calcula próxima fecha automáticamente</div>
    {ok&&<div style={{background:C.greenL,color:"#60C070",padding:"8px 12px",borderRadius:6,fontSize:12,marginBottom:12,border:"0.5px solid rgba(42,122,59,0.5)"}}>✅ OT registrada y planilla actualizada.</div>}
    {err&&<div style={{background:C.redL,color:"#E05050",padding:"8px 12px",borderRadius:6,fontSize:12,marginBottom:12}}>{err}</div>}
    {!form&&!loading&&<label style={{display:"block",border:`2px dashed ${C.border}`,borderRadius:10,padding:"2.5rem",textAlign:"center",cursor:"pointer",marginBottom:12}}>
      <div style={{fontSize:36,marginBottom:8}}>📄</div>
      <div style={{fontSize:14,color:C.txt,marginBottom:4,fontWeight:500}}>Arrastra el PDF aquí</div>
      <div style={{fontSize:12,color:C.txt2,marginBottom:12}}>o haz clic para seleccionar</div>
      <div style={{display:"inline-block",background:C.orange,color:"#fff",padding:"7px 18px",borderRadius:6,fontSize:13,fontWeight:500}}>Seleccionar PDF</div>
      <input type="file" accept=".pdf" onChange={readPDF} style={{display:"none"}}/>
    </label>}
    {loading&&<div style={{textAlign:"center",padding:"2rem"}}><div style={{fontSize:32,marginBottom:8}}>🤖</div><div style={{fontSize:13,fontWeight:500,color:C.txt}}>Leyendo OT con IA...</div></div>}
    {form&&<div>
      {/* Banner equipo reconocido / no reconocido */}
      {form._enc
        ?<div style={{background:C.greenL,color:"#60C070",padding:"8px 12px",borderRadius:6,fontSize:12,marginBottom:8,border:"0.5px solid rgba(42,122,59,0.5)"}}>✅ {form._enc}</div>
        :<div style={{marginBottom:8}}>
          <div style={{background:C.amberL,color:"#D08840",padding:"8px 12px",borderRadius:6,fontSize:12,marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>⚠ Serie <strong>"{form._txt}"</strong> no identificada con exactitud.</span>
            <button onClick={()=>setNuevoEq(p=>!p)} style={{fontSize:11,padding:"3px 10px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:nuevoEq?C.orange:C.orangeL,color:nuevoEq?"#fff":C.orange,fontFamily:"inherit",flexShrink:0,marginLeft:8}}>
              {nuevoEq?"✕ Cancelar":"➕ Crear equipo nuevo"}
            </button>
          </div>
          {/* Sugerencias de equipos similares por serie/marca/modelo */}
          {sugeridos.length>0&&<div style={{marginBottom:8}}>
            <div style={{fontSize:11,color:C.txt2,marginBottom:6,fontWeight:500}}>🔍 Equipos similares encontrados — ¿es alguno de estos?</div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {sugeridos.map(({equipo:s,score})=>{
                const c=cl.find(x=>x.id===s.cId);
                const pct=Math.round(score*100);
                const pctColor=pct>=80?C.green:pct>=60?C.amber:C.txt3;
                return <button key={s.id} onClick={()=>{
                  const fd=parseInt(s.frecDefault)>0?parseInt(s.frecDefault):0;
                  setForm(p=>{
                    const fP=fd&&p.fR?sigDiaHabilEid(toISO(addD(parseD(p.fR),fd)),s.id,eq,cl):"";
                    return {...p,eId:String(s.id),frecuencia:fd?String(fd):"",fP,_enc:`${s.tipo} — ${s.marca} ${s.modelo} [${s.serie}]`};
                  });
                  setSugeridos([]);
                }} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.surf,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",cursor:"pointer",textAlign:"left",width:"100%",fontFamily:"inherit"}}>
                  <div>
                    <div style={{fontSize:12,color:C.txt,fontWeight:500}}>{s.tipo} — {s.marca} {s.modelo}</div>
                    <div style={{fontSize:10,color:C.txt3}}>Serie: {s.serie} · {c?.nombre||"—"}</div>
                  </div>
                  <span style={{fontSize:10,fontWeight:700,color:pctColor,background:pctColor+"18",borderRadius:10,padding:"2px 8px",flexShrink:0,marginLeft:8}}>{pct}% similar</span>
                </button>;
              })}
            </div>
          </div>}
          {/* Formulario inline para crear equipo */}
          {nuevoEq&&<div style={{background:"rgba(232,113,26,0.06)",border:`1px solid ${C.orangeB}`,borderRadius:8,padding:"12px 14px",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:500,color:C.orange,marginBottom:10}}>Nuevo equipo</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div style={{gridColumn:"1/-1"}}>
                <LBL c="Tipo de equipo *"/>
                <select style={INP} value={nForm.tipo} onChange={e=>{
                  if(e.target.value==="__nuevo__")setNForm(p=>({...p,tipo:"__nuevo__"}));
                  else setNForm(p=>({...p,tipo:e.target.value}));
                }}>
                  {tiposLocales.map(t=><option key={t}>{t}</option>)}
                  <option value="__nuevo__">➕ Crear nuevo tipo...</option>
                </select>
                {nForm.tipo==="__nuevo__"&&<div style={{display:"flex",gap:6,marginTop:6}}>
                  <input style={{...INP,flex:1}} placeholder="Nombre del nuevo tipo" value={nuevoTipoStr} onChange={e=>setNuevoTipoStr(e.target.value)}/>
                  <button style={{...BTNS,fontSize:11,whiteSpace:"nowrap"}} onClick={()=>{
                    const t=nuevoTipoStr.trim();
                    if(!t)return;
                    setTiposLocales(prev=>[...prev.filter(x=>x!==t),t]);
                    setNForm(p=>({...p,tipo:t}));
                    setNuevoTipoStr("");
                  }}>✓ Agregar</button>
                </div>}
              </div>
              <div><LBL c="Marca *"/><input style={INP} value={nForm.marca} onChange={e=>setNForm(p=>({...p,marca:e.target.value}))} placeholder="Ej: Haulotte"/></div>
              <div><LBL c="Modelo *"/><input style={INP} value={nForm.modelo} onChange={e=>setNForm(p=>({...p,modelo:e.target.value}))} placeholder="Ej: Xlt 1571 Dc"/></div>
              <div style={{gridColumn:"1/-1"}}>
                <LBL c="N° Serie *"/>
                <input style={INP} value={nForm.serie} onChange={e=>setNForm(p=>({...p,serie:e.target.value}))} placeholder="Número de serie del PDF"/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <LBL c="Sucursal *"/>
                <select style={INP} value={nForm.cId} onChange={e=>{
                  if(e.target.value==="__nueva__")setNForm(p=>({...p,cId:"__nueva__"}));
                  else setNForm(p=>({...p,cId:e.target.value}));
                }}>
                  <option value="">Seleccionar sucursal...</option>
                  {cl.map(c=><option key={c.id} value={c.id}>{c.nombre}{c.cod?` (#${c.cod})`:""}</option>)}
                  <option value="__nueva__">➕ Crear nueva sucursal...</option>
                </select>
                {nForm.cId==="__nueva__"&&<div style={{display:"flex",flexDirection:"column",gap:6,marginTop:6,background:C.surf,borderRadius:7,padding:10,border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:11,color:C.orange,fontWeight:600}}>Nueva sucursal</div>
                  <input style={{...INP,width:"100%"}} placeholder="Nombre (ej: Farfana)" value={nuevaSucursalForm.nombre} onChange={e=>setNuevaSucursalForm(p=>({...p,nombre:e.target.value}))}/>
                  <div style={{display:"flex",gap:6}}>
                    <input style={{...INP,width:80}} placeholder="Código (ej: 214)" value={nuevaSucursalForm.cod} onChange={e=>setNuevaSucursalForm(p=>({...p,cod:e.target.value}))}/>
                    <button style={{...BTNS,fontSize:11,flex:1}} onClick={()=>{
                      const nombre=nuevaSucursalForm.nombre.trim();
                      if(!nombre)return;
                      const newId=Date.now();
                      setCl(prev=>[...prev,{id:newId,nombre,cod:nuevaSucursalForm.cod.trim()}]);
                      setNForm(p=>({...p,cId:String(newId)}));
                      setNuevaSucursalForm({nombre:"",cod:""});
                    }}>✓ Crear sucursal</button>
                  </div>
                </div>}
              </div>
              <div>
                <LBL c="Frecuencia por defecto (días)"/>
                <input type="number" style={INP} value={nForm.frecDefault} onChange={e=>setNForm(p=>({...p,frecDefault:e.target.value}))} placeholder="Ej: 90"/>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",paddingBottom:2,flexWrap:"wrap",gap:4}}>
                {FREQS.map(([d,l])=><button key={d} style={{fontSize:9,padding:"2px 7px",borderRadius:3,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:nForm.frecDefault===String(d)?C.orange:C.orangeL,color:nForm.frecDefault===String(d)?"#fff":C.orange,fontFamily:"inherit"}} onClick={()=>setNForm(p=>({...p,frecDefault:String(d)}))}>{l}</button>)}
              </div>
            </div>
            <button style={{...BTNP,opacity:nOk?1:0.5,cursor:nOk?"pointer":"not-allowed",fontSize:12,padding:"5px 16px"}} onClick={()=>nOk&&crearYSeleccionar()}>
              ✅ Crear y seleccionar
            </button>
          </div>}
        </div>}

      {form._sinFreq&&<div style={{background:"rgba(232,113,26,0.12)",border:`1px solid ${C.orangeB}`,borderRadius:8,padding:"10px 12px",marginBottom:12,display:"flex",gap:10,alignItems:"flex-start"}}>
        <span style={{fontSize:22,flexShrink:0}}>⚠️</span>
        <div>
          <div style={{fontSize:13,fontWeight:500,color:C.orange,marginBottom:3}}>Sin frecuencia definida</div>
          <div style={{fontSize:11,color:C.txt2,marginBottom:8}}>Selecciona la frecuencia:</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {FREQS.map(([d,l])=><button key={d} style={{fontSize:11,padding:"4px 12px",borderRadius:5,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:C.orangeL,color:C.orange,fontFamily:"inherit",fontWeight:500}}
              onClick={()=>setForm(p=>({...p,frecuencia:String(d),fP:p.fR?sigDiaHabilEid(toISO(addD(parseD(p.fR),d)),Number(form.eId),eq,cl):"",_sinFreq:false}))}>
              {l} ({d}d)
            </button>)}
          </div>
        </div>
      </div>}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div style={{gridColumn:"1/-1"}}><LBL c="Equipo *"/>
          <select style={INP} value={form.eId} onChange={e=>{
              const selEq=eq.find(x=>x.id===Number(e.target.value));
              const fd=selEq&&parseInt(selEq.frecDefault)>0?parseInt(selEq.frecDefault):0;
              setForm(p=>{
                const fP=fd&&p.fR?sigDiaHabilEid(toISO(addD(parseD(p.fR),fd)),Number(e.target.value),eq,cl):"";
                return {...p,
                  eId:e.target.value,
                  frecuencia:fd?String(fd):"",
                  fP,
                  _enc:selEq?`${selEq.tipo} — ${selEq.marca} ${selEq.modelo} [${selEq.serie}]`:null
                };
              });
            }}>
            <option value="">Seleccionar...</option>
            {cl.map(c=><optgroup key={c.id} label={`${c.nombre}${c.cod?` (#${c.cod})`:""}`}>{eq.filter(e=>e.cId===c.id).map(e=><option key={e.id} value={e.id}>{e.tipo} — {e.marca} {e.modelo} [{e.serie}]</option>)}</optgroup>)}
          </select>
        </div>
        <div><LBL c="N° OT"/><input style={INP} value={form.ot} onChange={e=>setForm(p=>({...p,ot:e.target.value}))}/></div>
        <div><LBL c="Técnico"/><input style={INP} value={form.tec} onChange={e=>setForm(p=>({...p,tec:e.target.value}))}/></div>
        <div><LBL c="Actividad"/><input style={INP} value={form.actividad} onChange={e=>setForm(p=>({...p,actividad:e.target.value}))}/></div>
        <div><LBL c="Tipo"/><select style={INP} value={form.tipo} onChange={e=>setForm(p=>({...p,tipo:e.target.value}))}><option>Preventiva</option><option>Correctiva</option><option>Diagnóstico</option><option>Garantía</option></select></div>
        <div><LBL c="Horómetro"/><input type="number" style={INP} value={form.horometro} onChange={e=>setForm(p=>({...p,horometro:e.target.value}))}/></div>
        <div>
          <LBL c="Frecuencia del equipo"/>
          <div style={{...INP,display:"flex",alignItems:"center",gap:8,background:C.surf,cursor:"default"}}>
            <span style={{color:form.frecuencia?C.orange:C.txt3,fontWeight:600}}>
              {form.frecuencia?`c/${form.frecuencia} días`:"Sin frecDefault — defínela en el equipo"}
            </span>
          </div>
        </div>
        <div><LBL c="Fecha realizada *"/><input type="date" style={INP} value={form.fR} onChange={e=>setForm(p=>({...p,fR:e.target.value}))}/></div>
        <div>
          <LBL c="Próxima fecha *"/>
          <input type="date" style={{...INP,borderColor:!fpEsValida(form.fR,form.fP)?C.red:form.fP?C.orangeB:C.border}} value={form.fP} onChange={e=>setForm(p=>({...p,fP:sigDiaHabilEid(e.target.value,Number(form.eId),eq,cl)}))}/>
          {!fpEsValida(form.fR,form.fP)&&<div style={{fontSize:10,color:C.red,marginTop:3,fontWeight:600}}>⚠ La próxima mantención no puede ser anterior a la fecha realizada — se corregirá automáticamente al guardar</div>}
          {form.fP&&(()=>{const av=fpConAviso(form.fP);return av.ajustada?null:null;})()}
          {(()=>{const orig=form._fpOrig;if(!orig||orig===form.fP)return null;return<div style={{fontSize:10,color:C.amber,marginTop:3}}>📅 Ajustada por {fpConAviso(form._fpOrig||"")?.motivo||"día no hábil"} → lunes siguiente</div>;})()}
        </div>
        <div style={{gridColumn:"1/-1"}}><LBL c="Observaciones"/><textarea style={{...INP,height:60,resize:"vertical"}} value={form.obs} onChange={e=>setForm(p=>({...p,obs:e.target.value}))}/></div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button style={{...BTNP,opacity:form.eId&&form.fR&&form.fP?1:0.5}} onClick={save}>✅ Guardar y actualizar planilla</button>
        <button style={BTNS} onClick={()=>{setForm(null);setNuevoEq(false);setPdfOrigFile(null);setSugeridos([]);}}>Cancelar</button>
      </div>
    </div>}
  </div>;
}


// ─── IMPORTAR EXCEL (masivo) ──────────────────────────────────────────────────
function ImportarExcel({cl,eq,mt,setMt,setEq}){
  const [filas,setFilas]=useState(null);
  const [done,setDone]=useState(null);
  const FMAP={mensual:30,bimestral:60,trimestral:90,cuatrimestral:120,semestral:180,anual:365};

  const findEq=raw=>{
    const{exacto}=buscarEquipo(raw,"","",eq);
    return exacto;
  };
  const getCol=(row,...keys)=>{
    for(const k of keys)for(const col of Object.keys(row))if(col.toLowerCase().includes(k.toLowerCase()))return row[col];
    return "";
  };
  const parseFreq=f=>{if(!f)return"";const n=parseInt(f);if(!isNaN(n)&&n>0)return String(n);return String(FMAP[String(f).toLowerCase().trim()]||"");};

  const parseFile=ev=>{
    const file=ev.target.files[0];if(!file)return;
    setDone(null);setFilas(null);
    const r=new FileReader();
    r.onload=e=>{
      const wb=XLSX.read(e.target.result,{type:"binary",cellDates:true});
      const ws=wb.Sheets[wb.SheetNames[0]];
      const rows=XLSX.utils.sheet_to_json(ws,{raw:false,dateNF:"yyyy-mm-dd"});
      const parsed=rows.map((row,i)=>{
        const serie=getCol(row,"serie","equipo","serial");
        const eq_=findEq(serie);
        const fRaw=getCol(row,"fecha_realizada","realizada","fecha real","fr","f_real");
        const fPRaw=getCol(row,"fecha_proxima","proxima","próxima","siguiente","fp");
        return{
          _i:i+1,serie:String(serie||""),eId:eq_?.id||null,
          eLabel:eq_?`${eq_.tipo} — ${eq_.marca} ${eq_.modelo} [${eq_.serie}]`:"",
          tec:String(getCol(row,"tecnico","tec","técnico")||""),
          fR:normDate(fRaw||""),fP:normDate(fPRaw||""),
          tipo:String(getCol(row,"tipo")||"Preventiva"),
          actividad:String(getCol(row,"actividad","descripcion","detalle")||""),
          ot:String(getCol(row,"ot","orden","nro ot","n° ot","numero ot","n_ot")||""),
          horometro:String(getCol(row,"horometro","horas","hrs")||""),
          frecuencia:parseFreq(getCol(row,"frecuencia","freq","días","dias")),
          obs:String(getCol(row,"observaciones","obs","notas")||""),
          status:eq_?"ok":"sin_equipo",
        };
      });
      setFilas(parsed);
    };
    r.readAsBinaryString(file);
    ev.target.value="";
  };

  const importar=()=>{
    const valid=filas.filter(f=>f.eId&&f.fR&&f.fP);
    let nextId=nxtId(mt);const newMt=[...mt];const eqUpd={};
    for(const f of valid){
      if(f.ot&&newMt.some(m=>m.eId===f.eId&&m.ot===f.ot))continue;
      newMt.push({id:nextId++,eId:f.eId,tec:f.tec,fR:f.fR,fP:f.fP,tipo:f.tipo,
                  actividad:f.actividad,ot:f.ot,horometro:f.horometro,frecuencia:f.frecuencia,obs:f.obs});
      if(!eqUpd[f.eId])eqUpd[f.eId]={};
      // Horómetro: del registro más reciente, de cualquier tipo (es solo una lectura de la máquina)
      if(!eqUpd[f.eId].fR||f.fR>eqUpd[f.eId].fR){eqUpd[f.eId].fR=f.fR;eqUpd[f.eId].ultimoHorometro=f.horometro;}
      // OT y frecuencia: SOLO de la preventiva más reciente — las correctivas no deben pisar esto
      const esPrev=(f.tipo||"Preventiva").toLowerCase()==="preventiva";
      if(esPrev&&(!eqUpd[f.eId].fRPrev||f.fR>eqUpd[f.eId].fRPrev)){eqUpd[f.eId].fRPrev=f.fR;eqUpd[f.eId].ultimaOT=f.ot;eqUpd[f.eId].frecDefault=f.frecuencia;}
    }
    setMt(newMt);
    setEq(prev=>prev.map(e=>{const u=eqUpd[e.id];if(!u)return e;return{...e,ultimaOT:u.ultimaOT||e.ultimaOT,ultimoHorometro:u.ultimoHorometro||e.ultimoHorometro,frecDefault:u.frecDefault||e.frecDefault};}));
    setDone({ok:valid.length,skip:filas.length-valid.length});setFilas(null);
  };

  const ok_=filas?.filter(f=>f.eId&&f.fR&&f.fP).length||0;
  const bad_=filas?.filter(f=>!f.eId||!f.fR||!f.fP).length||0;

  return <div style={CARD}>
    <div style={{fontSize:14,fontWeight:500,color:C.orange,marginBottom:4}}>📥 Importar OTs desde Excel</div>
    <div style={{fontSize:12,color:C.txt2,marginBottom:14}}>
      Sube un Excel con columnas: <span style={{color:C.orange}}>Serie · Centro · Técnico · Fecha_Realizada · Fecha_Proxima · Tipo · Actividad · OT · Horómetro · Frecuencia_Días · Observaciones</span>
    </div>
    {done&&<div style={{background:C.greenL,border:"0.5px solid rgba(42,122,59,0.5)",color:"#60C070",padding:"10px 14px",borderRadius:6,fontSize:12,marginBottom:12}}>
      ✅ Importadas {done.ok} OTs{done.skip>0?` · ${done.skip} omitidas (sin equipo/fechas)`:""}.
    </div>}
    {!filas&&<label style={{display:"block",border:`2px dashed ${C.border}`,borderRadius:10,padding:"2rem",textAlign:"center",cursor:"pointer",marginBottom:12}}>
      <div style={{fontSize:30,marginBottom:6}}>📊</div>
      <div style={{fontSize:13,color:C.txt,marginBottom:4,fontWeight:500}}>Seleccionar archivo Excel</div>
      <div style={{fontSize:11,color:C.txt2,marginBottom:10}}>.xlsx o .xls</div>
      <div style={{display:"inline-block",background:C.orange,color:"#fff",padding:"6px 16px",borderRadius:6,fontSize:12}}>Seleccionar</div>
      <input type="file" accept=".xlsx,.xls" onChange={parseFile} style={{display:"none"}}/>
    </label>}
    {filas&&<div>
      <div style={{display:"flex",gap:10,marginBottom:10,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{fontSize:12,color:C.txt}}><span style={{color:"#60C070",fontWeight:500}}>{ok_} listas</span> para importar</div>
        {bad_>0&&<div style={{fontSize:12,color:"#E05050"}}>{bad_} sin equipo/fechas (se omitirán)</div>}
        <button style={{...BTNP,fontSize:12,padding:"5px 16px",marginLeft:"auto"}} onClick={importar}>⬇ Importar {ok_} OTs</button>
        <button style={{...BTNS,fontSize:12,padding:"5px 12px"}} onClick={()=>setFilas(null)}>Cancelar</button>
      </div>
      <div style={{overflowX:"auto",maxHeight:340,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:6}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
          <thead><tr style={{background:C.surf,position:"sticky",top:0}}>
            {["#","Equipo / Serie","OT","Técnico","Realizada","Próxima","Tipo","Estado"].map(h=>(
              <th key={h} style={{padding:"8px 10px",textAlign:"left",color:C.txt3,borderBottom:`1px solid ${C.border}`,fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{filas.map(f=>(
            <tr key={f._i} style={{borderBottom:`0.5px solid rgba(255,255,255,0.04)`,background:f.eId?"transparent":"rgba(224,80,80,0.05)"}}>
              <td style={{padding:"5px 8px",color:C.txt3}}>{f._i}</td>
              <td style={{padding:"5px 8px",color:f.eId?C.txt:"#E05050"}}>{f.eId?f.eLabel:f.serie||"—"}</td>
              <td style={{padding:"5px 8px",color:C.orange}}>{f.ot||"—"}</td>
              <td style={{padding:"5px 8px",color:C.txt2}}>{f.tec||"—"}</td>
              <td style={{padding:"5px 8px",color:C.txt2}}>{f.fR?fmtCL(f.fR):"—"}</td>
              <td style={{padding:"5px 8px",color:C.orangeB}}>{f.fP?fmtCL(f.fP):"—"}</td>
              <td style={{padding:"5px 8px",color:C.txt3}}>{f.tipo}</td>
              <td style={{padding:"5px 8px"}}>
                {f.eId&&f.fR&&f.fP
                  ?<span style={{color:"#60C070",fontSize:10}}>✓ OK</span>
                  :<span style={{color:"#E05050",fontSize:10}}>{!f.eId?"Sin equipo":!f.fR?"Sin fecha R":"Sin fecha P"}</span>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>}
  </div>;
}

// ─── REGISTRAR MANUAL ─────────────────────────────────────────────────────────
function RegistrarManual({cl,eq,mt,setMt,setEq}){
  const iMt={eId:"",tec:"",fR:toISO(today),fP:"",tipo:"Preventiva",actividad:"",ot:"",horometro:"",frecuencia:"",obs:""};
  const [fMt,setFMt]=useState(iMt);const [ok,setOk]=useState(false);const [err,setErr]=useState("");const [adjuntoFile,setAdjuntoFile]=useState(null);
  const hFrq=val=>{const n=parseInt(val);if(fMt.fR&&n>0)setFMt(p=>({...p,frecuencia:val,fP:sigDiaHabilEid(toISO(addD(parseD(fMt.fR),n)),Number(fMt.eId),eq,cl)}));else setFMt(p=>({...p,frecuencia:val}));};
  const hFR=val=>{const n=parseInt(fMt.frecuencia);if(n>0)setFMt(p=>({...p,fR:val,fP:sigDiaHabilEid(toISO(addD(parseD(val),n)),Number(fMt.eId),eq,cl)}));else setFMt(p=>({...p,fR:val}));};
  const save=async()=>{
    if(!fMt.eId||!fMt.tec||!fMt.fR||!fMt.fP)return;
    if(fMt.ot&&mt.some(m=>m.eId===Number(fMt.eId)&&m.ot===fMt.ot)){setErr(`OT "${fMt.ot}" ya registrada.`);return;}
    let adjuntoUrl=null;
    if(adjuntoFile){setErr("⏳ Subiendo adjunto...");adjuntoUrl=await subirAdjunto(adjuntoFile);if(!adjuntoUrl){setErr("No se pudo subir el adjunto. Continúa sin él.");}}
    const eIdNum=Number(fMt.eId);
    const fPSeguro=corregirFP(fMt.fR,fMt.fP,fMt.frecuencia,eIdNum,eq,cl);
    setMt(p=>[...p,{...fMt,fP:fPSeguro,id:nxtId(p),eId:eIdNum,adjunto:adjuntoUrl||undefined}]);
    setEq(prev=>prev.map(e=>e.id===eIdNum?{...e,ultimaOT:(fMt.tipo||"Preventiva").toLowerCase()==="preventiva"?(fMt.ot||e.ultimaOT):e.ultimaOT,ultimoHorometro:fMt.horometro||e.ultimoHorometro}:e));
    setFMt(iMt);setErr("");setOk(true);setTimeout(()=>setOk(false),3000);
  };
  return <div style={CARD}>
    <div style={{fontSize:14,fontWeight:500,color:C.orange,marginBottom:14}}>Registro manual</div>
    {ok&&<div style={{background:C.greenL,color:"#60C070",padding:"8px 12px",borderRadius:6,fontSize:12,marginBottom:12,border:"0.5px solid rgba(42,122,59,0.5)"}}>Registrado correctamente.</div>}
    {err&&<div style={{background:C.redL,color:"#E05050",padding:"8px 12px",borderRadius:6,fontSize:12,marginBottom:12}}>{err}</div>}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
      <div style={{gridColumn:"1/-1"}}><LBL c="Equipo *"/><select style={INP} value={fMt.eId} onChange={e=>setFMt(p=>({...p,eId:e.target.value}))}><option value="">Seleccionar...</option>{cl.map(c=><optgroup key={c.id} label={`${c.nombre}${c.cod?` (#${c.cod})`:""}`}>{eq.filter(e=>e.cId===c.id).map(e=><option key={e.id} value={e.id}>{e.tipo} — {e.marca} {e.modelo} [{e.serie}]</option>)}</optgroup>)}</select></div>
      <div style={{gridColumn:"1/-1"}}><LBL c="Actividad"/><input style={INP} value={fMt.actividad} onChange={e=>setFMt(p=>({...p,actividad:e.target.value}))} placeholder="Ej: Mantención 250 hrs"/></div>
      <div><LBL c="Técnico *"/><input style={INP} value={fMt.tec} onChange={e=>setFMt(p=>({...p,tec:e.target.value}))} placeholder="Nombre técnico"/></div>
      <div><LBL c="Tipo"/><select style={INP} value={fMt.tipo} onChange={e=>setFMt(p=>({...p,tipo:e.target.value}))}><option>Preventiva</option><option>Correctiva</option><option>Diagnóstico</option><option>Garantía</option></select></div>
      <div><LBL c="N° OT"/><input style={INP} value={fMt.ot} onChange={e=>setFMt(p=>({...p,ot:e.target.value}))} placeholder="Ej: OT-001"/></div>
      <div><LBL c="Horómetro"/><input type="number" style={INP} value={fMt.horometro} onChange={e=>setFMt(p=>({...p,horometro:e.target.value}))}/></div>
      <div><LBL c="Fecha realizada *"/><input type="date" style={INP} value={fMt.fR} onChange={e=>hFR(e.target.value)}/></div>
      <div><LBL c="Frecuencia (días)"/><input type="number" style={INP} value={fMt.frecuencia} onChange={e=>hFrq(e.target.value)} placeholder="60/90/120 → calcula próxima"/></div>
      <div style={{gridColumn:"1/-1"}}>
        <LBL c={`Próxima *${fMt.frecuencia?" (calculada)":""}`}/>
        <input type="date" style={{...INP,borderColor:!fpEsValida(fMt.fR,fMt.fP)?C.red:fMt.fP?C.orangeB:C.border}} value={fMt.fP} onChange={e=>setFMt(p=>({...p,fP:sigDiaHabilEid(e.target.value,Number(fMt.eId),eq,cl)}))}/>
        {!fpEsValida(fMt.fR,fMt.fP)&&<div style={{fontSize:10,color:C.red,marginTop:3,fontWeight:600}}>⚠ La próxima mantención no puede ser anterior a la fecha realizada — se corregirá automáticamente al guardar</div>}
        {fMt.fP&&(()=>{const av=fpConAviso(fMt.fP,eIdExentoDiaHabil(Number(fMt.eId),eq,cl));return av.ajustada?<div style={{fontSize:10,color:C.amber,marginTop:3}}>📅 Ajustada — cae en {av.motivo}, se programó para el siguiente día hábil</div>:null;})()}
      </div>
      <div style={{gridColumn:"1/-1"}}><LBL c="Observaciones"/><textarea style={{...INP,height:60,resize:"vertical"}} value={fMt.obs} onChange={e=>setFMt(p=>({...p,obs:e.target.value}))}/></div>
      <div style={{gridColumn:"1/-1"}}><LBL c="Adjuntar PDF o foto (opcional)"/><div style={{display:"flex",gap:8,alignItems:"center"}}><label style={{...BTNS,cursor:"pointer",fontSize:11,padding:"4px 12px"}}>📎 {adjuntoFile?adjuntoFile.name:"Seleccionar archivo"}<input type="file" accept=".pdf,image/*" style={{display:"none"}} onChange={e=>setAdjuntoFile(e.target.files[0])}/></label>{adjuntoFile&&<button style={{...BTNS,fontSize:10,padding:"2px 8px",color:"#E05050"}} onClick={()=>setAdjuntoFile(null)}>✕</button>}</div></div>
    </div>
    <div style={{display:"flex",gap:8}}><button style={BTNP} onClick={save}>Guardar</button><button style={BTNS} onClick={()=>{setFMt(iMt);setErr("");setAdjuntoFile(null);}}>Limpiar</button></div>
  </div>;
}

function Registrar({cl,setCl,eq,mt,setMt,setEq}){
  const [tab,setTab]=useState("pdf");
  const tabS=a=>({padding:"6px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit",borderRadius:6,fontWeight:a?500:400,background:a?C.orangeL:"transparent",border:a?`0.5px solid ${C.orangeB}`:"0.5px solid transparent",color:a?C.orange:C.txt2});
  return <div>
    <div style={{display:"flex",gap:4,marginBottom:14,flexWrap:"wrap"}}>
      <button style={tabS(tab==="pdf")} onClick={()=>setTab("pdf")}>📄 Subir OT (PDF)</button>
      <button style={tabS(tab==="manual")} onClick={()=>setTab("manual")}>✏️ Manual</button>
      <button style={tabS(tab==="importar")} onClick={()=>setTab("importar")}>📥 Importar Excel</button>
    </div>
    {tab==="pdf"&&<PDFReader cl={cl} setCl={setCl} eq={eq} setEq={setEq} mt={mt} setMt={setMt}/>}
    {tab==="manual"&&<RegistrarManual cl={cl} eq={eq} mt={mt} setMt={setMt} setEq={setEq}/>}
    {tab==="importar"&&<ImportarExcel cl={cl} eq={eq} mt={mt} setMt={setMt} setEq={setEq}/>}

    {/* ── Últimas OTs registradas ─────────────────────────── */}
    {(()=>{
      const ultimas=[...mt].sort((a,b)=>b.fR.localeCompare(a.fR)||String(b.id).localeCompare(String(a.id))).slice(0,8);
      if(!ultimas.length)return null;
      const gE=id=>eq.find(e=>e.id===id)||{};
      const gC=id=>{const e=gE(id);return e.cId?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
      const colTipo=t=>{const tl=(t||"").toLowerCase();return tl==="preventiva"?{bg:C.blueL,c:C.blue}:tl==="correctiva"?{bg:C.amberL,c:C.amber}:tl.includes("diagnós")?{bg:C.greenL,c:C.green}:{bg:C.orangeL,c:C.orange};};
      const fmt=s=>{try{return new Date(s+"T00:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short"});}catch{return s;}};
      return <div style={{marginTop:20,borderTop:`1px solid ${C.border}`,paddingTop:14}}>
        <div style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>
          🕐 Últimas OTs registradas
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {ultimas.map(m=>{
            const e=gE(m.eId);const tp=colTipo(m.tipo);
            return <div key={m.id} style={{display:"flex",alignItems:"center",gap:8,background:C.surf,borderRadius:7,padding:"7px 12px",border:`1px solid ${C.border}`}}>
              <span style={{background:tp.bg,color:tp.c,borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:600,whiteSpace:"nowrap",flexShrink:0}}>{m.tipo||"Preventiva"}</span>
              <span style={{fontSize:12,fontWeight:500,color:C.txt,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {e.tipo||"?"} {e.marca} {e.modelo}
              </span>
              <span style={{fontSize:10,color:C.txt3,whiteSpace:"nowrap",flexShrink:0}}>{e.serie||"—"}</span>
              <span style={{fontSize:10,color:C.txt2,whiteSpace:"nowrap",flexShrink:0,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis"}}>{gC(m.eId)}</span>
              {m.ot&&<span style={{fontSize:10,color:C.orange,fontFamily:"monospace",flexShrink:0}}>OT:{m.ot}</span>}
              <span style={{fontSize:10,color:C.txt3,flexShrink:0}}>{fmt(m.fR)}</span>
            </div>;
          })}
        </div>
      </div>;
    })()}
  </div>;
}

// ─── HISTORIAL (agrupado por equipo) ─────────────────────────────────────────
function Historial({cl,eq,mt,setMt,setEq,onVerDetalle}){
  const [fCl,setFCl]=useState("");const [fMarca,setFMarca]=useState("");const [fTipo,setFTipo]=useState("");
  const [fSerie,setFSerie]=useState("");
  const [expanded,setExpanded]=useState({});
  const [confirmDelOT,setConfirmDelOT]=useState(null); // {id, ot, eqLabel}
  const gC=id=>{const e=eq.find(x=>x.id===id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const marcas=[...new Set(eq.map(e=>e.marca))].sort();
  const byEq={};
  mt.forEach(m=>{if(!byEq[m.eId])byEq[m.eId]=[];byEq[m.eId].push(m);});
  Object.values(byEq).forEach(arr=>arr.sort((a,b)=>b.fR.localeCompare(a.fR)));
  const serieQ=fSerie.toLowerCase().trim();
  const eqList=eq.filter(e=>{
    if(!byEq[e.id]?.length)return false;
    if(fCl&&e.cId!==Number(fCl))return false;
    if(fMarca&&e.marca!==fMarca)return false;
    if(fTipo&&!byEq[e.id].some(m=>m.tipo===fTipo))return false;
    if(serieQ&&!e.serie.toLowerCase().includes(serieQ))return false;
    return true;
  }).sort((a,b)=>byEq[b.id][0].fR.localeCompare(byEq[a.id][0].fR)); // más reciente primero

  const delOT=id=>{setMt(prev=>prev.filter(m=>m.id!==id));setConfirmDelOT(null);};

  return <div>
    {confirmDelOT&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"1rem"}}>
      <div style={{background:C.card,border:"0.5px solid rgba(224,80,80,0.4)",borderRadius:12,padding:"1.5rem",width:"100%",maxWidth:400}}>
        <div style={{fontSize:14,fontWeight:500,color:"#E05050",marginBottom:8}}>⚠ Eliminar OT</div>
        <div style={{fontSize:12,color:C.txt,marginBottom:4}}>{confirmDelOT.eqLabel}</div>
        {confirmDelOT.ot&&<div style={{fontSize:12,color:C.orange,marginBottom:4}}>OT: {confirmDelOT.ot}</div>}
        <div style={{fontSize:12,color:C.txt2,marginBottom:4}}>Fecha: {fmtCL(confirmDelOT.fR)}</div>
        <div style={{fontSize:11,color:C.txt3,marginBottom:14}}>Esta acción no se puede deshacer.</div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button style={BTNS} onClick={()=>setConfirmDelOT(null)}>Cancelar</button>
          <button style={{...BTNP,background:"#C03030"}} onClick={()=>delOT(confirmDelOT.id)}>Sí, eliminar</button>
        </div>
      </div>
    </div>}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
      <div><LBL c="Centro"/><select style={INP} value={fCl} onChange={e=>setFCl(e.target.value)}><option value="">Todos</option>{cl.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}</select></div>
      <div><LBL c="Marca"/><select style={INP} value={fMarca} onChange={e=>setFMarca(e.target.value)}><option value="">Todas</option>{marcas.map(m=><option key={m}>{m}</option>)}</select></div>
      <div><LBL c="Tipo OT"/><select style={INP} value={fTipo} onChange={e=>setFTipo(e.target.value)}><option value="">Todos</option><option>Preventiva</option><option>Correctiva</option><option>Diagnóstico</option><option>Garantía</option></select></div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,marginBottom:12,alignItems:"flex-end"}}>
      <div><LBL c="N° Serie"/><input style={INP} placeholder="Buscar por serie..." value={fSerie} onChange={e=>setFSerie(e.target.value)}/></div>
      <div style={{paddingBottom:3}}><span style={{fontSize:11,color:C.txt3}}>{eqList.length} equipo{eqList.length!==1?"s":""}</span></div>
    </div>
    <div style={CARD}>
      {eqList.length===0
        ?<div style={{fontSize:13,color:C.txt2,padding:"2rem",textAlign:"center"}}>{mt.length===0?"Sin mantenciones aún.":"Sin registros que coincidan"}</div>
        :eqList.map(e=>{
          const records=byEq[e.id]||[];
          const latest=records.find(esPreventiva); // "OT actual" = última preventiva, nunca una correctiva
          const older=records.filter(r=>r!==latest); // incluye correctivas + preventivas anteriores
          const sinPreventiva=!latest;
          const dp=latest?pill(diffD(latest.fP)):null;
          const ep=estP(e.est);
          const isExp=expanded[e.id];
          const correctivasPosteriores=latest?older.filter(r=>!esPreventiva(r)&&r.fR>latest.fR).length:0;
          const eqLabel=`${e.tipo} — ${e.marca} ${e.modelo} [${e.serie}]`;
          return <div key={e.id} style={{padding:"12px 0",borderBottom:`0.5px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                  <span style={{fontSize:13,fontWeight:500,color:C.txt}}>{e.tipo}</span>
                  <Chip {...ep}/>
                  {dp&&<Chip {...dp} size={11}/>}
                  {correctivasPosteriores>0&&<Chip bg={C.amberL} c={C.amber} l={`🔧 ${correctivasPosteriores} correctiva${correctivasPosteriores>1?"s":""} después`} size={10}/>}
                </div>
                <div style={{fontSize:11,color:C.txt3}}>{gC(e.id)} · {e.marca} {e.modelo} · <span style={{color:C.txt2}}>{e.serie}</span></div>
              </div>
              <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                <div style={{fontSize:11,color:C.txt2,fontWeight:500}}>{latest?`Próxima: ${fmtCL(latest.fP)}`:"Sin preventiva"}</div>
                <div style={{fontSize:10,color:C.txt3}}>{records.length} OT{records.length!==1?"s":""}</div>
              </div>
            </div>
            {latest&&<div style={{background:"rgba(255,255,255,0.02)",borderRadius:6,padding:"8px 10px",border:`1px solid ${C.border}`,borderLeft:`2px solid ${C.orange}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <div style={{fontSize:9,color:C.orange,fontWeight:500,textTransform:"uppercase",letterSpacing:"0.04em"}}>▲ OT preventiva actual</div>
                <div style={{display:"flex",gap:4,alignItems:"center"}}>
                  <button title="Ver detalle completo" onClick={()=>onVerDetalle&&onVerDetalle(latest)}
                    style={{fontSize:11,padding:"1px 8px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.orangeB}`,background:C.orangeL,color:C.orange,fontFamily:"inherit",lineHeight:1.5,fontWeight:600}}>⬡ Detalle</button>
                  <label title="Adjuntar PDF" style={{fontSize:11,padding:"1px 7px",borderRadius:4,cursor:"pointer",border:`0.5px solid ${C.orangeB}`,background:"transparent",color:C.orange,fontFamily:"inherit",lineHeight:1.5,display:"inline-flex",alignItems:"center"}}>
                    📎<input type="file" accept=".pdf,image/*" style={{display:"none"}} onChange={async ev=>{const f=ev.target.files[0];if(!f)return;try{const url=await subirAdjunto(f,latest.eId);if(url)setEq(p=>p.map(x=>x.id===latest.eId?{...x,adjunto:url,adjuntoTs:Date.now()}:x));}catch(err){alert("Error al subir: "+err.message);}ev.target.value="";}}/>
                  </label>
                  <button title="Eliminar esta OT" onClick={()=>setConfirmDelOT({id:latest.id,ot:latest.ot,fR:latest.fR,eqLabel})}
                    style={{fontSize:11,padding:"1px 7px",borderRadius:4,cursor:"pointer",border:"0.5px solid rgba(224,80,80,0.3)",background:"transparent",color:"#E05050",fontFamily:"inherit",lineHeight:1.5}}>🗑</button>
                </div>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                <Chip {...tipoP(latest.tipo)} l={latest.tipo}/>
                <span style={{fontSize:11,color:C.txt2}}>{fmtCL(latest.fR)}</span>
                {latest.tec&&<span style={{fontSize:10,color:C.txt3}}>{latest.tec}</span>}
              </div>
              <OTCard m={latest}/>
            </div>}
            {sinPreventiva&&<div style={{background:C.amberL,border:`1px solid ${C.amber}33`,borderRadius:6,padding:"8px 10px",fontSize:11,color:C.amber}}>
              ⚠ Este equipo solo tiene OTs correctivas registradas — sin mantención preventiva aún.
            </div>}
            {older.length>0&&<div style={{marginTop:6}}>
              <button style={{...BTNS,fontSize:10,padding:"3px 10px",color:C.txt3,borderColor:"rgba(255,255,255,0.06)"}}
                onClick={()=>setExpanded(p=>({...p,[e.id]:!p[e.id]}))}>
                {isExp?`▲ Ocultar historial`:`▼ ${older.length} OT${older.length>1?"s":""} adicional${older.length>1?"es":""} (incluye correctivas)`}
              </button>
              {isExp&&<div style={{marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
                {older.map(m=>{const tp=tipoP(m.tipo);const esCorr=!esPreventiva(m);return(
                  <div key={m.id} style={{padding:"6px 10px",background:esCorr?"rgba(232,160,32,0.04)":"rgba(255,255,255,0.01)",borderRadius:5,border:`1px solid ${esCorr?C.amber+"33":C.border}`,borderLeft:`2px solid ${esCorr?C.amber:C.border}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                      <div style={{display:"flex",gap:6,alignItems:"center"}}>
                        <Chip {...tp} l={m.tipo} size={10}/>
                        <span style={{fontSize:10,color:C.txt2}}>{fmtCL(m.fR)}</span>
                        {m.tec&&<span style={{fontSize:9,color:C.txt3}}>{m.tec}</span>}
                      </div>
                      <div style={{display:"flex",gap:5,alignItems:"center"}}>
                        {!esCorr&&<span style={{fontSize:10,color:C.txt3}}>Prox.: {fmtCL(m.fP)}</span>}
                        <label title="Adjuntar PDF" style={{fontSize:10,padding:"1px 6px",borderRadius:3,cursor:"pointer",border:`0.5px solid ${C.orangeB}`,background:"transparent",color:C.orange,fontFamily:"inherit",lineHeight:1.5,display:"inline-flex",alignItems:"center"}}>
                          📎<input type="file" accept=".pdf,image/*" style={{display:"none"}} onChange={async ev=>{const f=ev.target.files[0];if(!f)return;try{const url=await subirAdjunto(f,m.eId);if(url)setEq(p=>p.map(x=>x.id===m.eId?{...x,adjunto:url,adjuntoTs:Date.now()}:x));}catch(err){alert("Error al subir: "+err.message);}ev.target.value="";}}/>
                        </label>
                        <button title="Eliminar esta OT" onClick={()=>setConfirmDelOT({id:m.id,ot:m.ot,fR:m.fR,eqLabel})}
                          style={{fontSize:10,padding:"1px 6px",borderRadius:3,cursor:"pointer",border:"0.5px solid rgba(224,80,80,0.3)",background:"transparent",color:"#E05050",fontFamily:"inherit",lineHeight:1.5}}>🗑</button>
                      </div>
                    </div>
                    <OTCard m={m} mini={true}/>
                  </div>
                );})}
              </div>}
            </div>}
          </div>;
        })}
    </div>
  </div>;
}


// ─── PDF CALENDARIO MENSUAL (tamaño carta) ───────────────────────────────────
function generarPDFCalendario(mesM,mesY,semanasMes,curMts,eq,cl,filtroNombres=null){
  // Tamaño Oficio horizontal (330 x 216mm)
  const doc=new jsPDF({orientation:"landscape",unit:"mm",format:[330,216]});
  const PW=330,PH=216,margin=8;
  const gE=id=>eq.find(e=>e.id===id);
  const gC=id=>{const e=gE(id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const colorFor=m=>{const d=diffD(m.fP);return d<0?[248,81,73]:d<=7?[232,160,32]:[63,185,80];};
  const trunc=(s,n)=>{s=String(s||"");return s.length>n?s.slice(0,n-1)+"…":s;};
  // Trunca según el ancho REAL que ocupa el texto con la fuente/tamaño actual —
  // evita que jsPDF envuelva el texto a una 2da línea y choque con la línea de abajo
  const truncW=(s,maxW)=>{
    s=String(s||"");
    if(!s||doc.getTextWidth(s)<=maxW)return s;
    let cut=s;
    while(cut.length>1&&doc.getTextWidth(cut+"…")>maxW)cut=cut.slice(0,-1);
    return cut+"…";
  };

  // Columnas de ancho variable: Lun-Vie anchas, Sáb-Dom angostas
  const headerY=24,dayHdrH=6,legendH=9;
  const gridTop=headerY+dayHdrH;
  const gridHPerPage=PH-margin-legendH-gridTop-3;
  const gridW=PW-margin*2;
  const pesoDiaSemana=1.35,pesoFinSemana=0.45;
  const pesos=[pesoDiaSemana,pesoDiaSemana,pesoDiaSemana,pesoDiaSemana,pesoDiaSemana,pesoFinSemana,pesoFinSemana];
  const totalPeso=pesos.reduce((a,b)=>a+b,0);
  const colWidths=pesos.map(p=>gridW*p/totalPeso);
  const colXs=[];let accX=margin;colWidths.forEach(w=>{colXs.push(accX);accX+=w;});

  // Altura natural por semana (sin forzar a 1 sola página) — luego se reparte en páginas
  const baseH=8.5,itemH=9.8; // 4 líneas por item: Tienda(grande)/Tipo/Marca-Modelo/Serie
  const eventosPorDia=day=>{
    if(day.getMonth()!==mesM)return 0;
    return curMts.filter(m=>m.fP===toISO(day)).length;
  };
  const maxPorSemana=semanasMes.map(semana=>Math.max(0,...semana.map(eventosPorDia)));
  const alturaNatural=maxPorSemana.map(c=>baseH+c*itemH); // sin límite — se muestran TODOS los equipos

  // Paginación: agrupa semanas en páginas según el espacio realmente disponible
  const paginas=[];let actual=[];let alturaAcum=0;
  semanasMes.forEach((_,wi)=>{
    const h=alturaNatural[wi];
    if(alturaAcum+h>gridHPerPage&&actual.length>0){paginas.push(actual);actual=[];alturaAcum=0;}
    actual.push(wi);alturaAcum+=h;
  });
  if(actual.length>0)paginas.push(actual);

  const dibujarEncabezado=(numPag,totalPag)=>{
    doc.setFillColor(240,120,32);doc.rect(0,0,PW,3,"F");
    doc.setFontSize(15);doc.setFont("helvetica","bold");doc.setTextColor(30,30,30);
    doc.text("SisteMant · INMALUCA",margin,12);
    doc.setFontSize(10);doc.setFont("helvetica","normal");doc.setTextColor(100,100,100);
    let subtitulo=totalPag>1?`Calendario de Mantenciones — Página ${numPag} de ${totalPag}`:"Calendario de Mantenciones Preventivas";
    if(filtroNombres&&filtroNombres.length>0){
      const lista=filtroNombres.length<=3?filtroNombres.join(", "):`${filtroNombres.length} centros seleccionados`;
      subtitulo+=` · Filtrado: ${lista}`;
    }
    doc.text(subtitulo,margin,18);
    doc.setFontSize(18);doc.setFont("helvetica","bold");doc.setTextColor(240,120,32);
    doc.text(`${MESES[mesM]} ${mesY}`,PW-margin,14,{align:"right"});
    const fechaGen=new Date().toLocaleDateString("es-CL",{day:"2-digit",month:"2-digit",year:"numeric"});
    doc.setFontSize(8);doc.setFont("helvetica","normal");doc.setTextColor(140,140,140);
    doc.text(`Generado el ${fechaGen}`,PW-margin,20,{align:"right"});
    doc.setDrawColor(220,220,220);doc.line(margin,24,PW-margin,24);
    doc.setFontSize(8.5);doc.setFont("helvetica","bold");doc.setTextColor(90,90,90);
    DIAS_CORTO.forEach((d,i)=>doc.text(d.toUpperCase(),colXs[i]+colWidths[i]/2,headerY+dayHdrH-1,{align:"center"}));
  };
  const dibujarLeyenda=()=>{
    const legY=PH-margin-2;
    const legendItems=[[[248,81,73],"Vencida"],[[232,160,32],"Próxima (≤7 días)"],[[63,185,80],"Programada"]];
    let lx=margin;
    legendItems.forEach(([[r,g,b],label])=>{
      doc.setFillColor(r,g,b);doc.rect(lx,legY-2.3,2.5,2.5,"F");
      doc.setFontSize(7.5);doc.setFont("helvetica","normal");doc.setTextColor(90,90,90);
      doc.text(label,lx+4,legY);
      lx+=doc.getTextWidth(label)+14;
    });
    doc.setFontSize(7);doc.setTextColor(170,170,170);
    doc.text("SisteMant · Generado automáticamente",PW-margin,legY,{align:"right"});
  };

  // ── Renderizar cada página con sus semanas ──
  paginas.forEach((semanasIdx,pIdx)=>{
    if(pIdx>0)doc.addPage();
    dibujarEncabezado(pIdx+1,paginas.length);

    const alturasPagina=semanasIdx.map(wi=>alturaNatural[wi]);
    const totalPagina=alturasPagina.reduce((a,b)=>a+b,0);
    const bonus=Math.max(0,gridHPerPage-totalPagina)/semanasIdx.length;

    let y=gridTop;
    semanasIdx.forEach(wi=>{
      const rowH=alturaNatural[wi]+bonus;
      const semana=semanasMes[wi];
      semana.forEach((day,di)=>{
        const x=colXs[di],colW=colWidths[di];
        const esFinSemana=di>=5;
        const fueraDeMes=day.getMonth()!==mesM;
        const isToday=day.getTime()===today.getTime();
        doc.setDrawColor(225,225,225);
        doc.setFillColor(fueraDeMes?250:255,fueraDeMes?250:255,fueraDeMes?250:255);
        doc.rect(x,y,colW,rowH,fueraDeMes?"FD":"D");
        if(isToday){doc.setDrawColor(240,120,32);doc.setLineWidth(0.6);doc.rect(x,y,colW,rowH,"D");doc.setLineWidth(0.2);}
        doc.setFontSize(10);doc.setFont("helvetica","bold");
        doc.setTextColor(fueraDeMes?190:isToday?240:60,fueraDeMes?190:isToday?120:60,fueraDeMes?190:isToday?32:60);
        doc.text(String(day.getDate()),x+2,y+5);

        if(!fueraDeMes){
          const iso=toISO(day);
          const dayMts=curMts.filter(m=>m.fP===iso);
          const maxItemsFit=Math.max(1,Math.floor((rowH-baseH)/itemH));
          const mostrar=dayMts.slice(0,maxItemsFit);
          const restantes=dayMts.length-mostrar.length;
          let iy=y+baseH+2.6;
          mostrar.forEach(m=>{
            const e=gE(m.eId);const [r,g,b]=colorFor(m);
            doc.setFillColor(r,g,b);doc.rect(x+1.8,iy-2.3,1.8,1.8,"F");
            // Medir el OT primero con SU fuente real, para reservarle el espacio exacto
            doc.setFontSize(5.8);doc.setFont("helvetica","bold");
            const otText=m.ot?`OT:${m.ot}`:"";
            const anchoOT=otText?doc.getTextWidth(otText)+3:0;
            // Línea 1: Tienda (sucursal) — letra más grande, es el dato principal — + OT
            doc.setFontSize(8.2);doc.setFont("helvetica","bold");doc.setTextColor(40,40,40);
            doc.text(truncW(gC(m.eId).toUpperCase(),colW-5-anchoOT),x+3.8,iy);
            if(m.ot){doc.setFontSize(5.8);doc.setFont("helvetica","bold");doc.setTextColor(r,g,b);doc.text(otText,x+colW-1.5,iy,{align:"right"});}
            // Línea 2: Tipo de equipo
            doc.setFontSize(5.6);doc.setFont("helvetica","normal");doc.setTextColor(110,110,110);
            doc.text(truncW(String(e?.tipo||"").toUpperCase(),colW-5),x+3.8,iy+2.7);
            // Línea 3: Marca - Modelo
            doc.setFontSize(5.4);doc.setFont("helvetica","normal");doc.setTextColor(140,140,140);
            doc.text(truncW(`${e?.marca||""} - ${e?.modelo||""}`.toUpperCase(),colW-5),x+3.8,iy+4.8);
            // Línea 4: N° de Serie — sin prefijo "S/N"
            doc.setFontSize(5.4);doc.setFont("helvetica","bold");doc.setTextColor(80,80,80);
            doc.text(truncW(String(e?.serie||"—").toUpperCase(),colW-5),x+3.8,iy+6.9);
            iy+=itemH;
          });
          if(restantes>0){doc.setFontSize(5.6);doc.setFont("helvetica","italic");doc.setTextColor(150,150,150);doc.text(`+${restantes} más`,x+2,y+rowH-1.3);}
        }
      });
      y+=rowH;
    });
    dibujarLeyenda();
  });

  const sufijoFiltro=filtroNombres&&filtroNombres.length>0?`_filtrado-${filtroNombres.length}`:"";
  doc.save(`Calendario_${MESES[mesM]}_${mesY}${sufijoFiltro}.pdf`);
}


// ─── EXPORTAR EXCEL ───────────────────────────────────────────────────────────
function exportarExcel(cl,eq,mt){
  const wb=XLSX.utils.book_new();
  const latMap=latestPreventivaByEq(mt); // la planilla reporta próxima fecha según preventivas
  const fmtD=s=>{if(!s)return"";try{return parseD(s).toLocaleDateString("es-CL",{day:"2-digit",month:"2-digit",year:"numeric"});}catch{return s;}};
  const estadoLbl={activo:"Activo",mantencion:"En mantención",fuera:"Fuera de servicio"};

  // ── Hoja 1: Planilla de mantenciones (una fila por equipo) ──
  const h1=["Centro","Cód.","Tipo","Marca","Modelo","N° Serie","Estado",
            "Última OT","Fecha servicio","Técnico","Horómetro",
            "Frecuencia (días)","Próxima mantención","Días restantes","Situación"];
  const r1=eq.map(e=>{
    const c=cl.find(x=>x.id===e.cId)||{};
    const m=latMap[e.id]||null;
    const dias=m?diffD(m.fP):null;
    const sit=!m?"Sin registro":dias<0?`Vencida ${-dias}d`:dias===0?"Hoy":dias<=7?`En ${dias}d`:`En ${dias}d`;
    return[
      c.nombre||"",c.cod||"",e.tipo,e.marca,e.modelo,e.serie,
      estadoLbl[e.est]||e.est,
      m?.ot||e.ultimaOT||"",fmtD(m?.fR||""),m?.tec||"",
      m?.horometro||e.ultimoHorometro||"",
      e.frecDefault||m?.frecuencia||"",
      fmtD(m?.fP||""),dias!==null?dias:"",sit
    ];
  });
  // Ordenar: vencidas primero, luego próximas, luego al día
  r1.sort((a,b)=>{const da=typeof a[13]==="number"?a[13]:999,db=typeof b[13]==="number"?b[13]:999;return da-db;});
  const ws1=XLSX.utils.aoa_to_sheet([h1,...r1]);
  // Anchos de columna
  ws1["!cols"]=[{wch:18},{wch:6},{wch:20},{wch:12},{wch:14},{wch:18},{wch:14},
                {wch:10},{wch:14},{wch:16},{wch:12},{wch:14},{wch:16},{wch:10},{wch:14}];
  XLSX.utils.book_append_sheet(wb,ws1,"Planilla Equipos");

  // ── Hoja 2: Historial completo de OTs ──
  const h2=["Centro","Tipo equipo","Marca","Modelo","N° Serie",
            "N° OT","Tipo OT","Técnico","Actividad",
            "Fecha realizada","Próxima fecha","Horómetro","Frecuencia (días)","Observaciones"];
  const r2=mt.map(m=>{
    const e=eq.find(x=>x.id===m.eId)||{};
    const c=cl.find(x=>x.id===e.cId)||{};
    return[c.nombre||"",e.tipo||"",e.marca||"",e.modelo||"",e.serie||"",
           m.ot||"",m.tipo||"",m.tec||"",m.actividad||"",
           fmtD(m.fR),fmtD(m.fP),m.horometro||"",m.frecuencia||"",m.obs||""];
  }).sort((a,b)=>b[9].localeCompare(a[9]));
  const ws2=XLSX.utils.aoa_to_sheet([h2,...r2]);
  ws2["!cols"]=[{wch:18},{wch:20},{wch:12},{wch:14},{wch:18},{wch:10},{wch:12},
                {wch:16},{wch:30},{wch:14},{wch:14},{wch:12},{wch:14},{wch:30}];
  XLSX.utils.book_append_sheet(wb,ws2,"Historial OTs");

  // ── Hoja 3: Resumen por centro ──
  const h3=["Centro","Código","Total equipos","Al día","Próx. 7 días","Vencidas","Sin registro","OTs registradas"];
  const r3=cl.map(c=>{
    const eqs=eq.filter(e=>e.cId===c.id);
    const ots=mt.filter(m=>eqs.some(e=>e.id===m.eId));
    const cur=eqs.map(e=>latMap[e.id]).filter(Boolean);
    const venc=cur.filter(m=>diffD(m.fP)<0).length;
    const prox7=cur.filter(m=>{const d=diffD(m.fP);return d>=0&&d<=7;}).length;
    const alDia=cur.filter(m=>diffD(m.fP)>7).length;
    const sinReg=eqs.length-cur.length;
    return[c.nombre,c.cod,eqs.length,alDia,prox7,venc,sinReg,ots.length];
  });
  const ws3=XLSX.utils.aoa_to_sheet([h3,...r3]);
  ws3["!cols"]=[{wch:20},{wch:8},{wch:14},{wch:10},{wch:14},{wch:10},{wch:14},{wch:16}];
  XLSX.utils.book_append_sheet(wb,ws3,"Resumen por Centro");

  const fecha=today.toLocaleDateString("es-CL",{day:"2-digit",month:"2-digit",year:"numeric"}).replace(/\//g,"-");
  XLSX.writeFile(wb,`SisteMant_${fecha}.xlsx`);
}

// ─── BACKUP / RESTORE ─────────────────────────────────────────────────────────
function descargarBackup(cl,eq,mt){
  const data=JSON.stringify({v:1,ts:new Date().toISOString(),cl,eq,mt},null,2);
  const a=document.createElement("a");
  a.href=URL.createObjectURL(new Blob([data],{type:"application/json"}));
  const fecha=today.toLocaleDateString("es-CL",{day:"2-digit",month:"2-digit",year:"numeric"}).replace(/\//g,"-");
  a.download=`SisteMant_backup_${fecha}.json`;
  a.click();URL.revokeObjectURL(a.href);
}
function restaurarBackup(file,setCl,setEq,setMt,onDone){
  const r=new FileReader();
  r.onload=ev=>{
    try{
      const d=JSON.parse(ev.target.result);
      if(!d.eq||!d.mt)throw new Error("Formato inválido");
      if(d.cl)setCl(d.cl);
      setEq(d.eq.map(e=>({...e,frecDefault:e.frecDefault||""})));
      setMt(dedupMt(d.mt));
      onDone(true);
    }catch{onDone(false);}
  };
  r.readAsText(file);
}

// Combina datos nuevos con los existentes — NUNCA reemplaza ni borra nada.
// Formato esperado: {cl_nuevos:[...], eq_nuevos:[...], mt_nuevos:[...]}
// Reasigna IDs automáticamente si chocan con los que ya existen.
function combinarDatos(file,cl,eq,mt,setCl,setEq,setMt,onDone){
  const r=new FileReader();
  r.onload=ev=>{
    try{
      const d=JSON.parse(ev.target.result);
      const clNuevos=d.cl_nuevos||[];
      const eqNuevos=d.eq_nuevos||[];
      const mtNuevos=d.mt_nuevos||[];
      if(!eqNuevos.length&&!clNuevos.length){onDone({ok:false,msg:"El archivo no tiene cl_nuevos/eq_nuevos/mt_nuevos"});return;}

      // 1. Centros: reasignar ID si choca con uno existente
      let nextClId=nxtId(cl);
      const clIdMap={};
      const clFinal=clNuevos.map(c=>{
        const existeId=cl.some(x=>x.id===c.id);
        const nuevoId=existeId?nextClId++:c.id;
        clIdMap[c.id]=nuevoId;
        if(!existeId&&nuevoId>=nextClId)nextClId=nuevoId+1;
        return{...c,id:nuevoId};
      });

      // 2. Equipos: reasignar ID si choca, y actualizar cId con el mapeo de centros
      let nextEqId=nxtId(eq);
      const eqIdMap={};
      const eqFinal=eqNuevos.map(e=>{
        const existeId=eq.some(x=>x.id===e.id);
        const nuevoId=existeId?nextEqId++:e.id;
        eqIdMap[e.id]=nuevoId;
        if(!existeId&&nuevoId>=nextEqId)nextEqId=nuevoId+1;
        return{...e,id:nuevoId,cId:clIdMap[e.cId]!==undefined?clIdMap[e.cId]:e.cId};
      });

      // 3. Mantenciones: reasignar ID si choca, y actualizar eId con el mapeo de equipos
      let nextMtId=nxtId(mt);
      const mtFinal=mtNuevos.map(m=>{
        const existeId=mt.some(x=>x.id===m.id);
        const nuevoId=existeId?nextMtId++:m.id;
        if(!existeId&&nuevoId>=nextMtId)nextMtId=nuevoId+1;
        return{...m,id:nuevoId,eId:eqIdMap[m.eId]!==undefined?eqIdMap[m.eId]:m.eId};
      });

      setCl(prev=>[...prev,...clFinal]);
      setEq(prev=>[...prev,...eqFinal]);
      setMt(prev=>dedupMt([...prev,...mtFinal]));
      onDone({ok:true,centros:clFinal.length,equipos:eqFinal.length,mantenciones:mtFinal.length});
    }catch(err){onDone({ok:false,msg:err.message});}
  };
  r.readAsText(file);
}

// ─── APP PRINCIPAL ────────────────────────────────────────────────────────────
export default function App(){
  // Inyectar fuente y estilos globales una sola vez
  React.useEffect(()=>{
    if(document.getElementById("sm-styles"))return;
    const s=document.createElement("link");
    s.id="sm-styles";s.rel="stylesheet";
    s.href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap";
    document.head.appendChild(s);
    const g=document.createElement("style");
    g.id="sm-global";
    g.textContent=`
      *{box-sizing:border-box;margin:0;padding:0;}
      body{background:#07090D;font-family:'DM Sans',system-ui,sans-serif;}
      ::-webkit-scrollbar{width:5px;height:5px;}
      ::-webkit-scrollbar-track{background:#0D1117;}
      ::-webkit-scrollbar-thumb{background:#21262D;border-radius:10px;}
      ::-webkit-scrollbar-thumb:hover{background:#363B42;}
      input[type=date]::-webkit-calendar-picker-indicator{filter:invert(0.5) sepia(1) hue-rotate(180deg);}
      input,select,textarea{font-family:'DM Sans',system-ui,sans-serif;}
      select option{background:#161B22;}
      @keyframes spin{to{transform:rotate(360deg);}}
      @keyframes fadeUp{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
      .sm-card-hover:hover{border-color:#363B42!important;background:#1C2330!important;}
      .sm-btn-hover:hover{opacity:0.85;}
    `;
    document.head.appendChild(g);
  },[]);
  const [view,setView]=useState("dashboard");
  const [cl,setCl]=useState([]);const [eq,setEq]=useState([]);const [mt,setMt]=useState([]);
  const [bitacora,setBitacora]=useState([]);
  const [ready,setReady]=useState(false);
  const [savedToast,setSavedToast]=useState(false);
  const [syncState,setSyncState]=useState("idle");
  const [syncTs,setSyncTs]=useState(null);
  const [githubSha,setGithubSha]=useState(null);
  const [restoreMsg,setRestoreMsg]=useState(null);
  const [showDatos,setShowDatos]=useState(false);
  const [selectedOT,setSelectedOT]=useState(null); // Modal detalle global
  const [centroPreseleccionado,setCentroPreseleccionado]=useState(null); // Dashboard → Centros directo
  const syncTimer=React.useRef(null);

  // Último estado conocido del servidor (huellas por registro) — base para fusionar cambios
  const baseHRef=React.useRef(null);const baseTsRef=React.useRef(null);
  const stateRef=React.useRef({cl:[],eq:[],mt:[],bitacora:[]});
  const pendingRef=React.useRef(false);const retryRef=React.useRef(null);const lastPollRef=React.useRef(Date.now());
  stateRef.current={cl,eq,mt,bitacora};
  const marcarBase=(data,ts)=>{
    baseHRef.current=hashesDe(data);baseTsRef.current=ts||null;
    try{localStorage.setItem("lctech_base",JSON.stringify({ts:ts||null,h:baseHRef.current}));}catch{}
  };

  // ── Helper: guardar en servidor (lee el servidor, fusiona con lo local y recién ahí guarda) ──
  const saveToServer=React.useCallback(async()=>{
    if(!SYNC_URL)return;
    setSyncState("syncing");
    try{
      const local=stateRef.current;
      const rg=await fetch(SYNC_URL);
      if(!rg.ok)throw new Error("HTTP "+rg.status);
      const sv=await rg.json();
      const svTieneData=sv.eq?.length>0||sv.mt?.length>0;
      const svN=normDatos(sv);
      let aGuardar=local;
      if(svTieneData){
        aGuardar=mergeDatos(baseHRef.current,local,svN);
        if(!mismoContenido(aGuardar,local)){setCl(aGuardar.cl);setEq(aGuardar.eq);setMt(aGuardar.mt);setBitacora(aGuardar.bitacora);}
        if(mismoContenido(aGuardar,svN)){ // el servidor ya tiene todo → nada que subir
          marcarBase(svN,sv.ts);pendingRef.current=false;
          setSyncState("ok");setSyncTs(sv.ts||null);return;
        }
      }
      if(!(aGuardar.eq.length>0||aGuardar.mt.length>0))throw new Error("Datos vacíos");
      const r=await fetch(SYNC_URL,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({cl:aGuardar.cl,eq:aGuardar.eq,mt:aGuardar.mt,bitacora:aGuardar.bitacora})});
      if(!r.ok)throw new Error("HTTP "+r.status);
      const d=await r.json();
      marcarBase(aGuardar,d.ts);pendingRef.current=false;
      setSyncState("ok");setSyncTs(d.ts||new Date().toISOString());if(d.sha)setGithubSha(d.sha);
    }catch(e){
      setSyncState(navigator.onLine?"error":"offline");
      clearTimeout(retryRef.current);
      retryRef.current=setTimeout(()=>saveToServer(),30000); // reintenta solo
    }
  },[]);

  // ── Carga inicial: servidor → localStorage → defaults + migración automática ──
  useEffect(()=>{
    const loadData=async()=>{
      try{const b=JSON.parse(localStorage.getItem("lctech_base")||"null");if(b&&b.h){baseHRef.current=b.h;baseTsRef.current=b.ts||null;}}catch{}
      // Leer localStorage primero (siempre disponible)
      let lsCl,lsEq,lsMt;
      try{
        lsCl=JSON.parse(localStorage.getItem("lctech_cl")||"null")||null;
        lsEq=(JSON.parse(localStorage.getItem("lctech_eq")||"null")||[]).map(e=>({...e,frecDefault:e.frecDefault||""}));
        lsMt=dedupMt(JSON.parse(localStorage.getItem("lctech_mt")||"null")||[]);
      }catch{lsCl=null;lsEq=[];lsMt=[];}
      const lsTieneData=lsEq.length>0||lsMt.length>0;

      if(SYNC_URL){
        try{
          setSyncState("syncing");
          const r=await fetch(SYNC_URL);
          if(r.ok){
            const d=await r.json();
            const servidorTieneData=d.eq?.length>0||d.mt?.length>0;

            if(servidorTieneData){
              // ✅ Servidor tiene datos → usar servidor, conservando cambios locales que no alcanzaron a subirse
              const svN=normDatos(d);
              let fin=svN;
              if(baseHRef.current&&lsTieneData){
                let lsBit=null;try{lsBit=JSON.parse(localStorage.getItem("lctech_bit")||"null");}catch{}
                fin=mergeDatos(baseHRef.current,{cl:lsCl||svN.cl,eq:lsEq,mt:lsMt,bitacora:lsBit||svN.bitacora},svN);
              }
              setCl(fin.cl);setEq(fin.eq);setMt(fin.mt);setBitacora(fin.bitacora);
              setSyncState("ok");setSyncTs(d.ts||null);
              if(d._sha)setGithubSha(d._sha);
              if(mismoContenido(fin,svN))marcarBase(svN,d.ts); // si quedaron cambios locales pendientes, se conserva la base anterior
              setReady(true);return;
            } else if(lsTieneData){
              // 🔄 Servidor vacío pero localStorage tiene datos → migrar al servidor
              setSyncState("syncing");
              const payload={cl:lsCl||CL0,eq:lsEq,mt:lsMt};
              const rPost=await fetch(SYNC_URL,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
              if(rPost.ok){
                const saved=await rPost.json();
                marcarBase(normDatos(payload),saved.ts);
                setSyncState("ok");setSyncTs(saved.ts||new Date().toISOString());
              }
            }
          }
        }catch(e){setSyncState("offline");}
      }

      // Usar localStorage (puede ser datos migrados o datos locales)
      try{
        setCl(lsCl||CL0);
        setEq(lsEq.length?lsEq:E0);
        setMt(lsMt);
      }catch{setCl(CL0);setEq(E0);setMt([]);}
      if(!SYNC_URL)setSyncState("idle");
      setReady(true);
    };
    loadData();
  },[]);

  // ── Guardar: localStorage inmediato + servidor debounced (2s) ──
  useEffect(()=>{
    if(!ready)return;
    // localStorage siempre
    localStorage.setItem("lctech_cl",JSON.stringify(cl));
    localStorage.setItem("lctech_eq",JSON.stringify(eq));
    localStorage.setItem("lctech_mt",JSON.stringify(mt));
    try{localStorage.setItem("lctech_bit",JSON.stringify(bitacora));}catch{}
    setSavedToast(true);
    const t1=setTimeout(()=>setSavedToast(false),2000);
    // servidor con debounce — SOLO si hay OTs (evita sobreescribir con estado vacío)
    if(SYNC_URL && mt.length>0){
      pendingRef.current=true;
      clearTimeout(syncTimer.current);
      syncTimer.current=setTimeout(()=>saveToServer(),2000);
    }
    return()=>{clearTimeout(t1);};
  },[cl,eq,mt,bitacora,ready]);

  // Avisar si se intenta cerrar la pestaña con cambios que aún no llegan al servidor
  useEffect(()=>{
    const h=e=>{if(pendingRef.current){e.preventDefault();e.returnValue="";}};
    window.addEventListener("beforeunload",h);
    return()=>window.removeEventListener("beforeunload",h);
  },[]);

  const syncNow=()=>saveToServer();

  const [ajusteInfo,setAjusteInfo]=useState(null);
  const ajustarFechasExistentes=()=>{
    let count=0;
    const newMt=mt.map(m=>{
      if(!m.fP)return m;
      if(eIdExentoDiaHabil(m.eId,eq,cl))return m; // cliente exento — no se ajusta
      const adj=sigDiaHabil(m.fP);
      if(adj!==m.fP){count++;return{...m,fP:adj};}
      return m;
    });
    if(count>0){
      setMt(newMt);
      setAjusteInfo({count,ts:new Date().toLocaleTimeString("es-CL",{hour:"2-digit",minute:"2-digit"})});
      setTimeout(()=>setAjusteInfo(null),5000);
    } else {
      setAjusteInfo({count:0});
      setTimeout(()=>setAjusteInfo(null),3000);
    }
  };

  // Encuentra y corrige OTs donde la próxima mantención quedó ANTES de la fecha realizada
  const [fechasInvalidas,setFechasInvalidas]=useState(null); // null=no revisado, []=sin problemas, [...]=encontrados
  const [corrigiendoFechas,setCorrigiendoFechas]=useState(false);
  const revisarFechasInvalidas=()=>{
    const problemas=mt.filter(m=>!fpEsValida(m.fR,m.fP)).map(m=>{
      const e=eq.find(x=>x.id===m.eId);
      const c=e?cl.find(x=>x.id===e.cId):null;
      return{...m,_eqLabel:e?`${e.tipo} — ${e.marca} ${e.modelo} [${e.serie}]`:"Equipo no encontrado",_centroLabel:c?.nombre||"—"};
    });
    setFechasInvalidas(problemas);
  };
  const corregirTodasLasFechas=()=>{
    setCorrigiendoFechas(true);
    setMt(prev=>prev.map(m=>{
      if(fpEsValida(m.fR,m.fP))return m;
      const e=eq.find(x=>x.id===m.eId);
      const freq=parseInt(m.frecuencia)>0?m.frecuencia:(e?.frecDefault||"");
      return{...m,fP:corregirFP(m.fR,m.fP,freq,m.eId,eq,cl)};
    }));
    setTimeout(()=>{setCorrigiendoFechas(false);setFechasInvalidas([]);},400);
  };

  const recargarDesdeServidor=async()=>{
    if(!SYNC_URL)return;
    setSyncState("syncing");
    try{
      const r=await fetch(SYNC_URL);
      if(!r.ok)throw new Error("HTTP "+r.status);
      const d=await r.json();
      if(d.eq?.length>0||d.mt?.length>0){
        // Pausar el sync automático momentáneamente para que la recarga
        // no dispare un guardado inmediato de vuelta al servidor
        clearTimeout(syncTimer.current);pendingRef.current=false;
        if(d.cl)setCl(d.cl);
        setEq((d.eq||[]).map(e=>({...e,frecDefault:e.frecDefault||""})));
        setMt(dedupMt(d.mt||[]));setBitacora(d.bitacora||[]);
        marcarBase(normDatos(d),d.ts);
        setSyncState("ok");setSyncTs(d.ts||null);
        // Actualizar también el localStorage local
        localStorage.setItem("lctech_cl",JSON.stringify(d.cl||[]));
        localStorage.setItem("lctech_eq",JSON.stringify(d.eq||[]));
        localStorage.setItem("lctech_mt",JSON.stringify(d.mt||[]));
      } else {
        setSyncState(mt.length>0?"ok":"offline");
      }
    }catch(e){setSyncState("error");}
  };

  const onRestore=ok=>{
    setRestoreMsg({ok,txt:ok?"✅ Datos restaurados correctamente":"❌ Archivo inválido o corrupto"});
    setTimeout(()=>setRestoreMsg(null),4000);
    setShowDatos(false);
  };

  const onCombinar=res=>{
    setRestoreMsg({ok:res.ok,txt:res.ok?`✅ Agregados: ${res.centros} centros, ${res.equipos} equipos, ${res.mantenciones} OTs`:`❌ ${res.msg}`});
    setTimeout(()=>setRestoreMsg(null),6000);
    setShowDatos(false);
  };

  // Etiqueta e ícono de estado sync
  const syncInfo={
    idle:  {ico:"☁",  c:C.txt3,   l:"Sin configurar"},
    syncing:{ico:"↻",  c:"#5090C0",l:"Sincronizando..."},
    ok:    {ico:"✓",  c:"#50B060", l:syncTs?`Sync ${new Date(syncTs).toLocaleTimeString("es-CL",{hour:"2-digit",minute:"2-digit"})}`:  "Sincronizado"},
    error: {ico:"⚠",  c:"#E05050", l:"Error al sincronizar"},
    offline:{ico:"⊘", c:"#D08840", l:"Sin conexión al servidor"},
  }[syncState]||{ico:"?",c:C.txt3,l:""};

  const NAVS=[{id:"dashboard",l:"Dashboard"},{id:"planificador",l:"Planificador"},{id:"centros",l:"Centros"},{id:"registrar",l:"Registrar"},{id:"historial",l:"Historial"},{id:"reportes",l:"Reportes 📊"},{id:"horas",l:"⏱️ Horas"},{id:"proximos",l:"📅 Próximos 6m"}];

  // ── Auto-sync: trae cambios de otros computadores y los fusiona con lo local (cada 4 h y al volver a la pestaña) ──
  useEffect(()=>{
    if(!ready||!SYNC_URL)return;
    const poll=async()=>{
      lastPollRef.current=Date.now();
      try{
        const r=await fetch(SYNC_URL);
        if(!r.ok)return;
        const d=await r.json();
        if(!(d.ts&&d.ts!==baseTsRef.current&&(d.eq?.length>0||d.mt?.length>0)))return;
        const svN=normDatos(d);
        const local=stateRef.current;
        const merged=mergeDatos(baseHRef.current,local,svN);
        if(!mismoContenido(merged,local)){setCl(merged.cl);setEq(merged.eq);setMt(merged.mt);setBitacora(merged.bitacora);}
        if(mismoContenido(merged,svN)){marcarBase(svN,d.ts);setSyncTs(d.ts);}
        else saveToServer(); // hay cambios locales pendientes: se suben ya fusionados
      }catch{/* ok */}
    };
    const iv=setInterval(poll,14400000); // cada 4 horas
    const onVis=()=>{if(document.visibilityState==="visible"&&Date.now()-lastPollRef.current>120000)poll();};
    document.addEventListener("visibilitychange",onVis);
    return()=>{clearInterval(iv);document.removeEventListener("visibilitychange",onVis);};
  },[ready,saveToServer]);

  return <div style={{fontFamily:"'DM Sans',system-ui,-apple-system,sans-serif",color:C.txt,background:C.bg,minHeight:"100vh"}}>

    {/* ── Toast guardado automático ── */}
    {savedToast&&<div style={{position:"fixed",bottom:24,right:24,zIndex:999,background:C.card,border:`1px solid ${C.green}44`,color:C.green,padding:"10px 18px",borderRadius:10,fontSize:12,fontWeight:600,pointerEvents:"none",boxShadow:"0 4px 20px rgba(0,0,0,0.4)",display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:14}}>✓</span> Guardado automáticamente
    </div>}

    {/* ── Toast restore ── */}
    {ajusteInfo&&<div style={{position:"fixed",bottom:24,right:24,zIndex:999,background:C.card,border:`1px solid ${C.amber}44`,color:C.amber,padding:"10px 18px",borderRadius:10,fontSize:12,fontWeight:600,pointerEvents:"none",boxShadow:"0 4px 20px rgba(0,0,0,0.4)",display:"flex",alignItems:"center",gap:8}}>
      {ajusteInfo.count>0?<>📅 {ajusteInfo.count} fecha{ajusteInfo.count>1?"s":""} ajustada{ajusteInfo.count>1?"s":""} al día hábil</>:<>✓ Todas las fechas ya son días hábiles</>}
    </div>}
    {restoreMsg&&<div style={{position:"fixed",bottom:20,right:20,zIndex:999,background:restoreMsg.ok?C.greenL:C.redL,border:`1px solid ${restoreMsg.ok?"rgba(42,122,59,0.5)":"rgba(139,32,32,0.5)"}`,color:restoreMsg.ok?"#60C070":"#E05050",padding:"7px 14px",borderRadius:7,fontSize:12,fontWeight:500}}>
      {restoreMsg.txt}
    </div>}

    {/* ── Header ── */}
    <div style={{background:"rgba(13,17,23,0.95)",backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",borderBottom:`1px solid ${C.border}`,padding:"0.65rem 1.75rem",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,position:"sticky",top:0,zIndex:50}}>
      <Logo/>
      <nav style={{display:"flex",gap:3,flexWrap:"wrap",alignItems:"center"}}>
        {NAVS.map(n=>{const active=view===n.id;return(
          <button key={n.id} style={{padding:"6px 14px",fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',inherit",borderRadius:7,fontWeight:active?600:400,background:active?C.orangeL:"transparent",border:active?`1px solid ${C.orangeB}`:"1px solid transparent",color:active?C.orange:C.txt2,transition:"all 0.15s",letterSpacing:"0.01em"}}
            onClick={()=>{setView(n.id);setShowDatos(false);}}>{n.l}
          </button>
        );})}
        <div style={{width:1,height:20,background:C.border,margin:"0 4px"}}/>
        <button onClick={()=>setShowDatos(p=>!p)} style={{padding:"6px 12px",fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',inherit",borderRadius:7,background:showDatos?C.orangeL:"transparent",border:showDatos?`1px solid ${C.orangeB}`:"1px solid transparent",color:showDatos?C.orange:C.txt2,display:"flex",alignItems:"center",gap:5,transition:"all 0.15s"}}>
          <span style={{fontSize:11,color:syncInfo.c,fontWeight:700}}>{syncInfo.ico}</span>
          <span style={{fontWeight:500}}>Datos</span>
          <span style={{fontSize:9,background:"#1a3a1a",color:"#4CAF50",borderRadius:4,padding:"1px 4px",marginLeft:4}}>●AUTO</span>
        </button>
      </nav>
    </div>

    {/* ── Panel Datos (sync + backup + exportar) ── */}
    {showDatos&&ready&&<div style={{background:"#0D1117",borderBottom:`1px solid ${C.border}`,padding:"1rem 1.75rem"}}>
      <div style={{maxWidth:1020,margin:"0 auto",display:"flex",gap:16,flexWrap:"wrap",alignItems:"center"}}>

        {/* Estado sync servidor */}
        <div style={{display:"flex",flexDirection:"column",gap:4,minWidth:160}}>
          <div style={{fontSize:11,color:C.txt2,fontWeight:500}}>☁ Servidor</div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:16,color:syncInfo.c,animation:syncState==="syncing"?"spin 1s linear infinite":undefined}}>{syncInfo.ico}</span>
            <span style={{fontSize:11,color:syncInfo.c}}>{syncInfo.l}</span>
          </div>
          {SYNC_URL
            ?<div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              <button style={{...BTNS,fontSize:11,padding:"3px 10px"}} onClick={syncNow} disabled={syncState==="syncing"}>
                {syncState==="syncing"?"↻ Sincronizando...":"↑ Guardar en servidor"}
              </button>
              <button style={{...BTNP,fontSize:11,padding:"3px 10px"}} onClick={recargarDesdeServidor} disabled={syncState==="syncing"}>
                ↓ Recargar desde servidor
              </button>
              <div style={{fontSize:10,color:"#4CAF50",marginTop:6,display:"flex",alignItems:"center",gap:4}}>
                <span>●</span>
                <span>Actualización automática activa — se sincroniza automáticamente cada 4 horas</span>
              </div>
              <button style={{fontSize:11,padding:"3px 10px",borderRadius:4,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.txt3,fontFamily:"inherit"}}
                onClick={async()=>{
                  try{
                    const getR=await fetch(SYNC_URL);
                    const getTxt=await getR.text();
                    let info="";
                    try{const d=JSON.parse(getTxt);info=`\nEquipos: ${d.eq?.length||0} | OTs: ${d.mt?.length||0} | Sync: ${d.ts||"nunca"}`;}catch{}
                    alert(`GET → ${getR.status}:${info}\n\n${getTxt.slice(0,200)}`);
                  }catch(e){alert("Error de red: "+e.message);}
                }}>
                🔍 Probar conexión
              </button>
            </div>
            :<div style={{fontSize:10,color:C.txt3}}>Configura SYNC_URL en App.js</div>}
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Ajustar fechas hábiles */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <div style={{fontSize:11,color:C.txt2,fontWeight:500}}>📅 Días hábiles</div>
          <button style={{...BTNS,fontSize:12,padding:"5px 14px",borderColor:C.amber+"66",color:C.amber}}
            onClick={ajustarFechasExistentes}>
            Ajustar fechas existentes
          </button>
          <div style={{fontSize:10,color:C.txt3}}>Mueve sáb/dom/feriados al lunes</div>
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Diagnóstico de fechas inválidas (próxima antes de realizada) */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <div style={{fontSize:11,color:C.red,fontWeight:500}}>⚠ Fechas inconsistentes</div>
          <button style={{...BTNS,fontSize:12,padding:"5px 14px",borderColor:C.red+"66",color:C.red}}
            onClick={revisarFechasInvalidas}>
            Revisar próxima &lt; realizada
          </button>
          <div style={{fontSize:10,color:C.txt3}}>Detecta OTs con fecha próxima anterior a la realizada</div>
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Exportar Excel */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <div style={{fontSize:11,color:C.txt2,fontWeight:500}}>📊 Exportar planilla</div>
          <button style={{...BTNP,fontSize:12,padding:"5px 16px"}} onClick={()=>exportarExcel(cl,eq,mt)}>
            Descargar Excel
          </button>
          <div style={{fontSize:10,color:C.txt3}}>Equipos · Historial OTs · Resumen centros</div>
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Backup JSON */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <div style={{fontSize:11,color:C.txt2,fontWeight:500}}>💾 Copia de seguridad</div>
          <button style={{...BTNS,fontSize:12,padding:"5px 16px"}} onClick={()=>descargarBackup(cl,eq,mt)}>
            Descargar backup (.json)
          </button>
          <div style={{fontSize:10,color:C.txt3}}>{eq.length} equipos · {mt.length} OTs registradas</div>
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Restaurar JSON (reemplaza todo) */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <div style={{fontSize:11,color:C.txt2,fontWeight:500}}>📂 Restaurar backup</div>
          <label style={{...BTNS,fontSize:12,padding:"5px 16px",cursor:"pointer",display:"inline-block",textAlign:"center"}}>
            Cargar archivo .json
            <input type="file" accept=".json" style={{display:"none"}} onChange={ev=>{const f=ev.target.files[0];if(f)restaurarBackup(f,setCl,setEq,setMt,onRestore);ev.target.value="";}}/>
          </label>
          <div style={{fontSize:10,color:C.txt3}}>Reemplaza todos los datos actuales</div>
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Combinar/Agregar (NO reemplaza) */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <div style={{fontSize:11,color:C.green,fontWeight:500}}>➕ Agregar clientes/equipos</div>
          <label style={{...BTNP,fontSize:12,padding:"5px 16px",cursor:"pointer",display:"inline-block",textAlign:"center"}}>
            Cargar archivo .json
            <input type="file" accept=".json" style={{display:"none"}} onChange={ev=>{const f=ev.target.files[0];if(f)combinarDatos(f,cl,eq,mt,setCl,setEq,setMt,onCombinar);ev.target.value="";}}/>
          </label>
          <div style={{fontSize:10,color:C.txt3}}>Suma sin borrar lo existente</div>
        </div>

        <div style={{width:1,height:52,background:C.border}}/>

        {/* Estadísticas rápidas */}
        <div style={{display:"flex",gap:14,alignItems:"center"}}>
          {[
            {l:"Centros",v:cl.length,c:C.txt2},
            {l:"Equipos",v:eq.length,c:C.orange},
            {l:"OTs totales",v:mt.length,c:"#5090C0"},
            {l:"Vencidas",v:Object.values(latestPreventivaByEq(mt)).filter(m=>diffD(m.fP)<0).length,c:"#E05050"}
          ].map(k=><div key={k.l} style={{textAlign:"center"}}>
            <div style={{fontSize:18,fontWeight:500,color:k.c}}>{k.v}</div>
            <div style={{fontSize:10,color:C.txt3}}>{k.l}</div>
          </div>)}
        </div>

      </div>
    </div>}

    <div style={{padding:"1.25rem 1.75rem 4rem",maxWidth:1020,margin:"0 auto",animation:"fadeUp 0.2s ease"}}>
      {!ready?<div style={{color:C.txt2,fontSize:13,padding:"3rem",textAlign:"center"}}>Cargando...</div>:<>
        {/* Modal detalle global — accesible desde cualquier pestaña */}
        {selectedOT&&<ModalDetalle m={selectedOT} eq={eq} cl={cl} mt={mt} bitacora={bitacora} setMt={setMt} setEq={setEq} setCl={setCl} onClose={()=>setSelectedOT(null)}/>}
        {fechasInvalidas!==null&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:250,padding:"1rem"}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,width:"100%",maxWidth:640,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>
            <div style={{padding:"1.1rem 1.3rem",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:fechasInvalidas.length>0?C.red:C.green}}>
                  {fechasInvalidas.length>0?`⚠ ${fechasInvalidas.length} OT${fechasInvalidas.length>1?"s":""} con fecha inconsistente`:"✅ Sin problemas encontrados"}
                </div>
                <div style={{fontSize:11,color:C.txt3,marginTop:2}}>Próxima mantención anterior a la fecha realizada</div>
              </div>
              <button style={{...BTNS,padding:"3px 10px",fontSize:12}} onClick={()=>setFechasInvalidas(null)}>✕</button>
            </div>
            <div style={{padding:"1.1rem 1.3rem"}}>
              {fechasInvalidas.length===0
                ?<div style={{fontSize:13,color:C.txt2,textAlign:"center",padding:"1.5rem 0"}}>Todas las fechas de "próxima mantención" son posteriores a su fecha realizada. 👍</div>
                :<>
                  <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                    {fechasInvalidas.map(m=><div key={m.id} style={{background:C.redL,border:`1px solid ${C.red}33`,borderRadius:9,padding:"10px 12px"}}>
                      <div style={{fontSize:12,fontWeight:600,color:C.txt}}>{m._eqLabel}</div>
                      <div style={{fontSize:10,color:C.txt3,marginBottom:5}}>{m._centroLabel} · OT: {m.ot||"—"}</div>
                      <div style={{display:"flex",gap:14,fontSize:11}}>
                        <span style={{color:C.txt2}}>Realizada: <strong>{fmtCL(m.fR)}</strong></span>
                        <span style={{color:C.red}}>Próxima (actual): <strong>{fmtCL(m.fP)}</strong></span>
                        <span style={{color:C.green}}>→ Corregida: <strong>{fmtCL(corregirFP(m.fR,m.fP,m.frecuencia||eq.find(e=>e.id===m.eId)?.frecDefault,m.eId,eq,cl))}</strong></span>
                      </div>
                    </div>)}
                  </div>
                  <button style={{...BTNP,width:"100%",opacity:corrigiendoFechas?0.6:1}} disabled={corrigiendoFechas} onClick={corregirTodasLasFechas}>
                    {corrigiendoFechas?"Corrigiendo...":`✅ Corregir las ${fechasInvalidas.length} fechas mostradas`}
                  </button>
                  <div style={{fontSize:10,color:C.txt3,marginTop:6,textAlign:"center"}}>Se recalcula usando la frecuencia del equipo (o 90 días si no hay ninguna definida)</div>
                </>}
            </div>
          </div>
        </div>}
        {view==="dashboard"   &&<Dashboard   cl={cl} eq={eq} mt={mt} setMt={setMt} setEq={setEq} onNav={setView} onVerDetalle={setSelectedOT} onVerCentro={cId=>{setCentroPreseleccionado(cId);setView("centros");}}/>}
        {view==="planificador"&&<Planificador cl={cl} eq={eq} mt={mt} setMt={setMt} setEq={setEq} onVerDetalle={setSelectedOT}/>}
        {view==="centros"     &&<Centros      cl={cl} eq={eq} mt={mt} setEq={setEq} setMt={setMt} setCl={setCl} setBitacora={setBitacora} bitacora={bitacora} onVerDetalle={setSelectedOT} preseleccionar={centroPreseleccionado} onPreseleccionado={()=>setCentroPreseleccionado(null)}/>}
        {view==="registrar"   &&<Registrar    cl={cl} setCl={setCl} eq={eq} mt={mt} setMt={setMt} setEq={setEq}/>}
        {view==="historial"   &&<Historial    cl={cl} eq={eq} mt={mt} setMt={setMt} setEq={setEq} onVerDetalle={setSelectedOT}/>}
        {view==="reportes"    &&<Reportes     cl={cl} eq={eq} mt={mt}/>}
        {view==="horas"       &&<ProgramaHoras cl={cl} eq={eq} mt={mt}/>}
        {view==="proximos"    &&<ProximosMeses  cl={cl} eq={eq} mt={mt}/>}
      </>}
    </div>
  </div>;
}

// ═══════════════════════════════════════════════════════════════════
// REPORTES — Análisis avanzado de mantenciones INMALUCA
// ═══════════════════════════════════════════════════════════════════
function Reportes({cl,eq,mt}){
  const [tab,setTab]=useState("mensual");
  const [mesNum,setMesNum]=useState(new Date().getFullYear()*12+new Date().getMonth());
  const [filtroCentro,setFiltroCentro]=useState("todos");
  const [fechaDesde,setFechaDesde]=useState("");
  const [fechaHasta,setFechaHasta]=useState("");
  const [filtroFechaCentro,setFiltroFechaCentro]=useState("todos");
  const [filtroFechaTipo,setFiltroFechaTipo]=useState("todos");
  const [filtroFechaTec,setFiltroFechaTec]=useState("todos");
  const [filtroTipo,setFiltroTipo]=useState("todos");
  const [filtroModelo,setFiltroModelo]=useState("todos");
  const mesY=Math.floor(mesNum/12);
  const mesM=mesNum%12;
  const mesLabel=new Date(mesY,mesM,1).toLocaleDateString("es-CL",{month:"long",year:"numeric"});
  const esPreventiva=m=>(m.tipo||"Preventiva").toLowerCase()==="preventiva";
  const gE=id=>eq.find(e=>e.id===id);
  const gC=id=>{const e=gE(id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const mtMes=mt.filter(m=>{if(!m.fR)return false;const d=new Date(m.fR+"T00:00:00");return d.getFullYear()===mesY&&d.getMonth()===mesM;});
  const latestPrev={};
  [...mt].filter(esPreventiva).sort((a,b)=>b.fR.localeCompare(a.fR)).forEach(m=>{if(!latestPrev[m.eId])latestPrev[m.eId]=m;});
  const today2=new Date();today2.setHours(0,0,0,0);
  const diffD2=str=>Math.round((new Date(str+"T00:00:00")-today2)/86400000);
  const Pill=({v,c})=><span style={{background:c+"22",color:c,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600}}>{v}</span>;
  const Card=({l,v,sub,c})=><div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 18px",minWidth:120}}>
    <div style={{fontSize:11,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{l}</div>
    <div style={{fontSize:32,fontWeight:700,color:c||C.txt,lineHeight:1}}>{v}</div>
    {sub&&<div style={{fontSize:11,color:C.txt3,marginTop:4}}>{sub}</div>}
  </div>;

  const exportarReporte=()=>{
    const wb=window.XLSX||XLSX;if(!wb)return;
    const workbook=wb.utils.book_new();
    const addSheet=(rows,name,widths)=>{const ws=wb.utils.aoa_to_sheet(rows);ws["!cols"]=widths.map(w=>({wch:w}));wb.utils.book_append_sheet(workbook,ws,name);};
    let fn="";
    if(tab==="mensual"){
      const rows=[["Centro","Equipo","Marca","Modelo","Serie","Tipo OT","Fecha Realizada","Próxima","Técnico","OT","Actividad","Observaciones"]];
      mtMes.forEach(m=>{const e=gE(m.eId)||{};rows.push([gC(m.eId),e.tipo||"",e.marca||"",e.modelo||"",e.serie||"",m.tipo||"Preventiva",m.fR,m.fP||"",m.tec||"",m.ot||"",m.actividad||"",m.obs||""]);});
      addSheet(rows,mesLabel.replace(" ","_"),[20,20,14,14,14,12,14,14,16,10,24,30]);
      fn=`SisteMant_Mensual_${mesLabel.replace(" ","_")}.xlsx`;
    }else if(tab==="cumplimiento"){
      const rows=[["Centro","Equipos","Con Prev.","En Regla","Vencidos","Sin Prev.","Cumplimiento %"]];
      cl.forEach(c=>{const eqC=eq.filter(e=>e.cId===c.id);const conP=eqC.filter(e=>latestPrev[e.id]);const venc=conP.filter(e=>diffD2(latestPrev[e.id].fP)<0);const ok2=conP.length-venc.length;const pct=conP.length?Math.round(ok2/conP.length*100):0;rows.push([c.nombre,eqC.length,conP.length,ok2,venc.length,eqC.length-conP.length,pct+"%"]);});
      addSheet(rows,"Cumplimiento",[24,10,10,10,10,10,14]);
      fn="SisteMant_Cumplimiento.xlsx";
    }else if(tab==="tecnicos"){
      const tecs={};mt.forEach(m=>{const t=m.tec?.trim()||"Sin asignar";if(!tecs[t])tecs[t]={prev:0,corr:0,diag:0,gar:0,total:0};const tp=(m.tipo||"Preventiva").toLowerCase();if(tp==="preventiva")tecs[t].prev++;else if(tp==="correctiva")tecs[t].corr++;else if(tp.includes("diagnós"))tecs[t].diag++;else if(tp.includes("garantí"))tecs[t].gar++;tecs[t].total++;});
      const rows=[["Técnico","Preventivas","Correctivas","Diagnósticos","Garantías","Total"]];
      Object.entries(tecs).sort((a,b)=>b[1].total-a[1].total).forEach(([t,v])=>rows.push([t,v.prev,v.corr,v.diag,v.gar,v.total]));
      addSheet(rows,"Técnicos",[24,12,12,14,12,10]);
      fn="SisteMant_Tecnicos.xlsx";
    }else if(tab==="criticos"){
      const corrByEq={};mt.filter(m=>!esPreventiva(m)).forEach(m=>{corrByEq[m.eId]=(corrByEq[m.eId]||0)+1;});
      const rows=[["Centro","Tipo","Marca","Modelo","Serie","Estado","Días Vencida","Correctivas"]];
      eq.filter(e=>(latestPrev[e.id]&&diffD2(latestPrev[e.id].fP)<0)||!latestPrev[e.id]||corrByEq[e.id])
        .sort((a,b)=>(corrByEq[b.id]||0)-(corrByEq[a.id]||0))
        .forEach(e=>{const lp=latestPrev[e.id];const d=lp?diffD2(lp.fP):null;rows.push([gC(e.id),e.tipo||"",e.marca||"",e.modelo||"",e.serie||"",e.est||"",d!==null&&d<0?Math.abs(d)+"d":"",corrByEq[e.id]||0]);});
      addSheet(rows,"Críticos",[22,20,14,14,14,10,14,12]);
      fn="SisteMant_Criticos.xlsx";
    }else if(tab==="centros"){
      const eqF=eq.filter(e=>{if(filtroCentro!=="todos"&&String(e.cId)!==String(filtroCentro))return false;if(filtroTipo!=="todos"&&e.tipo!==filtroTipo)return false;if(filtroModelo!=="todos"&&e.modelo!==filtroModelo)return false;return true;});
      const eqIds=new Set(eqF.map(e=>e.id));
      const rows=[["Centro","Tipo Equipo","Marca","Modelo","N° Serie","N° OT","Tipo OT","Técnico","Actividad","Fecha Realizada","Próxima Mantención","Horómetro","Observaciones"]];
      mt.filter(m=>eqIds.has(m.eId)).sort((a,b)=>b.fR.localeCompare(a.fR)).forEach(m=>{const e=gE(m.eId)||{};rows.push([gC(m.eId),e.tipo||"",e.marca||"",e.modelo||"",e.serie||"",m.ot||"",m.tipo||"Preventiva",m.tec||"",m.actividad||"",m.fR||"",m.fP||"",m.horometro||"",m.obs||""]);});
      if(!rows.slice(1).length)eqF.forEach(e=>rows.push([gC(e.id),e.tipo||"",e.marca||"",e.modelo||"",e.serie||"","","","","","","","","Sin OTs registradas"]));
      const ws=wb.utils.aoa_to_sheet(rows);ws["!cols"]=[22,20,12,14,18,10,12,18,30,14,18,12,40].map(w=>({wch:w}));ws["!autofilter"]={ref:`A1:M${rows.length}`};wb.utils.book_append_sheet(workbook,ws,"Historial");
      const partes=[];if(filtroCentro!=="todos"){const cc=cl.find(x=>String(x.id)===String(filtroCentro));if(cc)partes.push(cc.nombre);}if(filtroTipo!=="todos")partes.push(filtroTipo);if(filtroModelo!=="todos")partes.push(filtroModelo);
      fn=`SisteMant - ${partes.length?partes.join(" - "):"Todos"} - ${new Date().toISOString().split("T")[0]}.xlsx`;
    }
    if(!fn)return;
    const wbout=wb.write(workbook,{bookType:"xlsx",type:"array"});
    const blob=new Blob([wbout],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
    const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=fn;
    document.body.appendChild(a);a.click();setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},500);
  };

  const TABS2=[{id:"mensual",l:"📅 Mensual"},{id:"fecha",l:"🗓️ Por Fecha"},{id:"cumplimiento",l:"✅ Cumplimiento"},{id:"tecnicos",l:"🔧 Técnicos"},{id:"criticos",l:"⚠️ Críticos"},{id:"centros",l:"🏢 Centros"}];

  return <div style={{padding:24,maxWidth:1100,margin:"0 auto"}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontSize:22,fontWeight:700}}>Reportes <span style={{color:C.orange}}>& Análisis</span></div>
        <div style={{fontSize:12,color:C.txt2,marginTop:2}}>{eq.length} equipos · {mt.length} OTs registradas</div>
      </div>
      <button onClick={exportarReporte} style={{...BTNP,display:"flex",alignItems:"center",gap:6}}>{tab==="centros"||tab==="fecha"?"⬇ Exportar Excel":"⬇ Exportar Excel"}</button>
    </div>
    <div style={{display:"flex",gap:4,marginBottom:20,background:C.surf,borderRadius:9,padding:4,width:"fit-content"}}>
      {TABS2.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"7px 16px",fontSize:12,fontWeight:tab===t.id?600:400,cursor:"pointer",fontFamily:"inherit",borderRadius:7,border:"none",background:tab===t.id?C.card:"transparent",color:tab===t.id?C.txt:C.txt2,boxShadow:tab===t.id?"0 1px 4px rgba(0,0,0,0.3)":"none",transition:"all 0.15s"}}>{t.l}</button>)}
    </div>

    {tab==="mensual"&&(()=>{
      const prev=mtMes.filter(esPreventiva);const corr=mtMes.filter(m=>!esPreventiva(m));
      const porCentro={};mtMes.forEach(m=>{const cc=gC(m.eId);if(!porCentro[cc])porCentro[cc]={prev:0,corr:0};esPreventiva(m)?porCentro[cc].prev++:porCentro[cc].corr++;});
      const porTipo={};mtMes.forEach(m=>{const e=gE(m.eId);const t=e?.tipo||"Sin tipo";porTipo[t]=(porTipo[t]||0)+1;});
      return <div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
          <button onClick={()=>setMesNum(n=>n-1)} style={{...BTNS,padding:"5px 12px"}}>◀</button>
          <div style={{fontSize:16,fontWeight:600,textTransform:"capitalize",minWidth:180,textAlign:"center"}}>{mesLabel}</div>
          <button onClick={()=>setMesNum(n=>n+1)} style={{...BTNS,padding:"5px 12px"}}>▶</button>
          <button onClick={()=>setMesNum(new Date().getFullYear()*12+new Date().getMonth())} style={{...BTNS,fontSize:11}}>Mes actual</button>
        </div>
        <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <Card l="Total OTs" v={mtMes.length} sub={mesLabel}/>
          <Card l="Preventivas" v={prev.length} c={C.blue}/>
          <Card l="Correctivas" v={corr.length} c={C.amber}/>
          <Card l="Diagnósticos" v={mtMes.filter(m=>(m.tipo||"").toLowerCase().includes("diagnós")).length} c={C.green}/>
          <Card l="Garantías" v={mtMes.filter(m=>(m.tipo||"").toLowerCase().includes("garantí")).length} c={C.orange}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
            <div style={{fontSize:11,fontWeight:600,marginBottom:12,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Por Centro</div>
            {Object.keys(porCentro).length===0?<div style={{color:C.txt3,fontSize:13,padding:16,textAlign:"center"}}>Sin OTs este mes</div>
            :Object.entries(porCentro).sort((a,b)=>(b[1].prev+b[1].corr)-(a[1].prev+a[1].corr)).map(([cc,v])=><div key={cc} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontSize:13}}>{cc}</div>
              <div style={{display:"flex",gap:6}}>{v.prev>0&&<Pill v={`${v.prev} prev`} c={C.blue}/>}{v.corr>0&&<Pill v={`${v.corr} corr`} c={C.amber}/>}</div>
            </div>)}
          </div>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
            <div style={{fontSize:11,fontWeight:600,marginBottom:12,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Por Tipo de Equipo</div>
            {Object.entries(porTipo).sort((a,b)=>b[1]-a[1]).map(([t,n])=><div key={t} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontSize:13,flex:1}}>{t}</div>
              <Pill v={n} c={C.orange}/>
              <div style={{width:70,height:5,background:C.border,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",background:C.orange,borderRadius:3,width:`${mtMes.length?Math.round(n/mtMes.length*100):0}%`}}/></div>
            </div>)}
          </div>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Detalle — {mtMes.length} OTs en {mesLabel}</div>
          {mtMes.length===0?<div style={{padding:40,textAlign:"center",color:C.txt3}}>Sin OTs registradas en {mesLabel}</div>
          :<div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.surf}}>{["Fecha","Centro","Equipo","Serie","Tipo","Técnico","OT","Actividad"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.5,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
            <tbody>{[...mtMes].sort((a,b)=>b.fR.localeCompare(a.fR)).map((m,i)=>{const e=gE(m.eId)||{};const tp=esPreventiva(m)?{bg:C.blueL,c:C.blue}:(m.tipo||"").toLowerCase()==="correctiva"?{bg:C.amberL,c:C.amber}:(m.tipo||"").toLowerCase().includes("diagnós")?{bg:C.greenL,c:C.green}:{bg:C.orangeL,c:C.orange};
              return <tr key={m.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf}}>
                <td style={{padding:"8px 12px",color:C.txt2,whiteSpace:"nowrap"}}>{m.fR}</td><td style={{padding:"8px 12px"}}>{gC(m.eId)}</td>
                <td style={{padding:"8px 12px"}}>{e.tipo||"?"} {e.marca||""}</td><td style={{padding:"8px 12px",fontFamily:"monospace",color:C.txt2,fontSize:11}}>{e.serie||"—"}</td>
                <td style={{padding:"8px 12px"}}><span style={{background:tp.bg,color:tp.c,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:600}}>{m.tipo||"Preventiva"}</span></td>
                <td style={{padding:"8px 12px",color:C.txt2}}>{m.tec||"—"}</td><td style={{padding:"8px 12px",fontFamily:"monospace",fontSize:11,color:C.orange}}>{m.ot||"—"}</td>
                <td style={{padding:"8px 12px",color:C.txt2,maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.actividad||"—"}</td>
              </tr>;})}
            </tbody>
          </table></div>}
        </div>
      </div>;})()} 

    {tab==="cumplimiento"&&(()=>{
      const conPrev=eq.filter(e=>latestPrev[e.id]);const vencidos=conPrev.filter(e=>diffD2(latestPrev[e.id].fP)<0);const ok2=conPrev.filter(e=>diffD2(latestPrev[e.id].fP)>=0);const pctG=conPrev.length?Math.round(ok2.length/conPrev.length*100):0;
      const porCentro2=cl.map(c=>{const eqC=eq.filter(e=>e.cId===c.id);const conP2=eqC.filter(e=>latestPrev[e.id]);const venc2=conP2.filter(e=>diffD2(latestPrev[e.id].fP)<0);const enR=conP2.length-venc2.length;const pct=conP2.length?Math.round(enR/conP2.length*100):0;return{nombre:c.nombre,total:eqC.length,conP:conP2.length,venc:venc2.length,enR,sinP:eqC.length-conP2.length,pct};}).filter(c=>c.total>0).sort((a,b)=>b.pct-a.pct);
      return <div>
        <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <Card l="Equipos" v={eq.length}/><Card l="Con preventiva" v={conPrev.length} sub={`${eq.length-conPrev.length} sin registro`}/>
          <Card l="En regla" v={ok2.length} c={C.green}/><Card l="Vencidos" v={vencidos.length} c={C.red}/>
          <Card l="Cumplimiento global" v={pctG+"%"} c={pctG>=80?C.green:pctG>=60?C.amber:C.red} sub="preventivas a tiempo"/>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Cumplimiento Global de Flota</div>
          <div style={{height:22,background:C.border,borderRadius:11,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${pctG}%`,background:pctG>=80?C.green:pctG>=60?C.amber:C.red,borderRadius:11,display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:8,minWidth:pctG>8?"auto":0}}>
              {pctG>8&&<span style={{fontSize:11,fontWeight:700,color:"#fff"}}>{pctG}%</span>}
            </div>
          </div>
          <div style={{display:"flex",gap:16,marginTop:8,fontSize:11,color:C.txt3}}><span>🟢 En regla: {ok2.length}</span><span>🔴 Vencidos: {vencidos.length}</span><span>⚪ Sin registro: {eq.length-conPrev.length}</span></div>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Por Centro</div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.surf}}>{["Centro","Equipos","Con Prev.","En Regla","Vencidos","Sin Prev.","Cumplimiento"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:h==="Centro"?"left":"center",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.5}}>{h}</th>)}</tr></thead>
            <tbody>{porCentro2.map((c,i)=><tr key={c.nombre} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf}}>
              <td style={{padding:"10px 12px",fontWeight:500}}>{c.nombre}</td>
              <td style={{padding:"10px 12px",textAlign:"center",color:C.txt2}}>{c.total}</td>
              <td style={{padding:"10px 12px",textAlign:"center",color:C.txt2}}>{c.conP}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}><Pill v={c.enR} c={C.green}/></td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>{c.venc>0?<Pill v={c.venc} c={C.red}/>:<span style={{color:C.txt3}}>0</span>}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>{c.sinP>0?<Pill v={c.sinP} c={C.txt3}/>:<span style={{color:C.txt3}}>0</span>}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>
                  <div style={{width:60,height:5,background:C.border,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${c.pct}%`,background:c.pct>=80?C.green:c.pct>=60?C.amber:C.red,borderRadius:3}}/></div>
                  <span style={{fontWeight:700,fontSize:13,color:c.pct>=80?C.green:c.pct>=60?C.amber:C.red,minWidth:36}}>{c.pct}%</span>
                </div>
              </td>
            </tr>)}</tbody>
          </table>
        </div>
      </div>;})()} 

    {tab==="tecnicos"&&(()=>{
      const tecs3={};mt.forEach(m=>{const t=m.tec?.trim()||"Sin asignar";if(!tecs3[t])tecs3[t]={nombre:t,prev:0,corr:0,diag:0,gar:0,total:0,centros:new Set()};const tp=(m.tipo||"Preventiva").toLowerCase();if(tp==="preventiva")tecs3[t].prev++;else if(tp==="correctiva")tecs3[t].corr++;else if(tp.includes("diagnós"))tecs3[t].diag++;else if(tp.includes("garantí"))tecs3[t].gar++;tecs3[t].total++;const cn=gC(m.eId);if(cn!=="—")tecs3[t].centros.add(cn);});
      const lista3=Object.values(tecs3).sort((a,b)=>b.total-a.total);const maxT=lista3[0]?.total||1;
      return <div>
        <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <Card l="Técnicos" v={lista3.filter(t=>t.nombre!=="Sin asignar").length}/><Card l="OTs totales" v={mt.length}/>
          <Card l="Preventivas" v={mt.filter(esPreventiva).length} c={C.blue}/><Card l="Correctivas" v={mt.filter(m=>!esPreventiva(m)&&(m.tipo||"").toLowerCase()==="correctiva").length} c={C.amber}/>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>OTs por Técnico</div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.surf}}>{["Técnico","Prev.","Corr.","Diag.","Gar.","Total","Centros","Distribución"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:h==="Técnico"||h==="Centros"||h==="Distribución"?"left":"center",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.5}}>{h}</th>)}</tr></thead>
            <tbody>{lista3.map((t,i)=><tr key={t.nombre} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf}}>
              <td style={{padding:"10px 12px",fontWeight:500}}>{t.nombre}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>{t.prev>0?<Pill v={t.prev} c={C.blue}/>:<span style={{color:C.txt3}}>—</span>}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>{t.corr>0?<Pill v={t.corr} c={C.amber}/>:<span style={{color:C.txt3}}>—</span>}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>{t.diag>0?<Pill v={t.diag} c={C.green}/>:<span style={{color:C.txt3}}>—</span>}</td>
              <td style={{padding:"10px 12px",textAlign:"center"}}>{t.gar>0?<Pill v={t.gar} c={C.orange}/>:<span style={{color:C.txt3}}>—</span>}</td>
              <td style={{padding:"10px 12px",textAlign:"center",fontWeight:700,fontSize:14}}>{t.total}</td>
              <td style={{padding:"10px 12px",fontSize:11,color:C.txt2,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{[...t.centros].slice(0,3).join(", ")}{t.centros.size>3?` +${t.centros.size-3}`:""}</td>
              <td style={{padding:"10px 12px"}}><div style={{display:"flex",alignItems:"center",gap:6}}><div style={{flex:1,height:5,background:C.border,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round(t.total/maxT*100)}%`,background:C.orange,borderRadius:3}}/></div><span style={{fontSize:11,color:C.txt3,minWidth:28,textAlign:"right"}}>{Math.round(t.total/maxT*100)}%</span></div></td>
            </tr>)}</tbody>
          </table>
        </div>
      </div>;})()} 

    {tab==="criticos"&&(()=>{
      const corrByEq2={};mt.filter(m=>!esPreventiva(m)).forEach(m=>{corrByEq2[m.eId]=(corrByEq2[m.eId]||0)+1;});
      const vencidos2=eq.filter(e=>latestPrev[e.id]&&diffD2(latestPrev[e.id].fP)<0).sort((a,b)=>diffD2(latestPrev[a.id].fP)-diffD2(latestPrev[b.id].fP));
      const sinPrev2=eq.filter(e=>!latestPrev[e.id]);
      const masCorr=Object.entries(corrByEq2).sort((a,b)=>b[1]-a[1]).slice(0,10).map(([id,n])=>({e:gE(Number(id)),n})).filter(x=>x.e);
      return <div>
        <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <Card l="Equipos vencidos" v={vencidos2.length} c={C.red}/><Card l="Sin preventiva" v={sinPrev2.length} c={C.amber}/>
          <Card l="Con intervenciones" v={Object.keys(corrByEq2).length} c={C.orange}/><Card l="Total intervenciones" v={mt.filter(m=>!esPreventiva(m)).length} c={C.txt2}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8}}><span style={{color:C.red}}>🔴</span><span style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Preventivas Vencidas ({vencidos2.length})</span></div>
            <div style={{maxHeight:360,overflowY:"auto"}}>
              {vencidos2.length===0?<div style={{padding:32,textAlign:"center",color:C.txt3}}>✅ Sin vencidos</div>
              :vencidos2.map(e=>{const lp=latestPrev[e.id];const d=Math.abs(diffD2(lp.fP));return <div key={e.id} style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <div><div style={{fontSize:12,fontWeight:500}}>{e.tipo} — {e.marca} {e.modelo}</div><div style={{fontSize:11,color:C.txt3,fontFamily:"monospace"}}>{e.serie||"Sin serie"} · {gC(e.id)}</div></div>
                <span style={{background:C.redL,color:C.red,borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>-{d}d</span>
              </div>;})}
            </div>
          </div>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8}}><span>🔧</span><span style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Top Intervenciones ({masCorr.length})</span></div>
            <div style={{maxHeight:360,overflowY:"auto"}}>
              {masCorr.length===0?<div style={{padding:32,textAlign:"center",color:C.txt3}}>Sin intervenciones</div>
              :masCorr.map(({e,n},i)=><div key={e.id} style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}><div style={{fontSize:16,fontWeight:700,color:C.txt3,minWidth:20,textAlign:"center"}}>{i+1}</div><div><div style={{fontSize:12,fontWeight:500}}>{e.tipo} — {e.marca} {e.modelo}</div><div style={{fontSize:11,color:C.txt3,fontFamily:"monospace"}}>{e.serie||"Sin serie"} · {gC(e.id)}</div></div></div>
                <span style={{background:C.amberL,color:C.amber,borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{n} interv.</span>
              </div>)}
            </div>
          </div>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden",gridColumn:"1/-1"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8}}><span>⚪</span><span style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Sin Preventiva Registrada ({sinPrev2.length})</span></div>
            {sinPrev2.length===0?<div style={{padding:24,textAlign:"center",color:C.txt3}}>✅ Todos los equipos tienen preventiva</div>
            :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:1,background:C.border}}>
              {sinPrev2.map(e=><div key={e.id} style={{padding:"10px 14px",background:C.card}}><div style={{fontSize:12,fontWeight:500}}>{e.tipo} — {e.marca}</div><div style={{fontSize:11,color:C.txt3,fontFamily:"monospace",marginTop:2}}>{e.serie||e.modelo} · {gC(e.id)}</div></div>)}
            </div>}
          </div>
        </div>
      </div>;})()} 

    {tab==="fecha"&&(()=>{
      // Filtrar OTs por rango de fecha + filtros adicionales
      const hoyStr=new Date().toISOString().split("T")[0];
      const tecnicos=["todos",...new Set(mt.map(m=>m.tec?.trim()||"Sin asignar").filter(Boolean).sort())];
      const tiposOT=["todos","Preventiva","Correctiva","Diagnóstico","Garantía"];
      const centrosLF=[{id:"todos",nombre:"Todos los centros"},...cl];

      const mtFecha=mt.filter(m=>{
        if(!m.fR)return false;
        if(fechaDesde&&m.fR<fechaDesde)return false;
        if(fechaHasta&&m.fR>fechaHasta)return false;
        if(filtroFechaCentro!=="todos"){const e=eq.find(x=>x.id===m.eId);if(!e||String(e.cId)!==String(filtroFechaCentro))return false;}
        if(filtroFechaTipo!=="todos"&&(m.tipo||"Preventiva")!==filtroFechaTipo)return false;
        if(filtroFechaTec!=="todos"&&(m.tec?.trim()||"Sin asignar")!==filtroFechaTec)return false;
        return true;
      }).sort((a,b)=>b.fR.localeCompare(a.fR));

      const prev=mtFecha.filter(esPreventiva);
      const corr=mtFecha.filter(m=>!esPreventiva(m)&&(m.tipo||"").toLowerCase()==="correctiva");

      const exportarFecha=()=>{
        const wb=window.XLSX||XLSX;if(!wb)return;
        const workbook=wb.utils.book_new();
        const rows=[["Fecha Realizada","Próxima Mant.","Centro","Tipo Equipo","Marca","Modelo","N° Serie","Tipo OT","Técnico","N° OT","Horómetro","Actividad","Observaciones"]];
        mtFecha.forEach(m=>{const e=gE(m.eId)||{};rows.push([m.fR,m.fP||"",gC(m.eId),e.tipo||"",e.marca||"",e.modelo||"",e.serie||"",m.tipo||"Preventiva",m.tec||"",m.ot||"",m.horometro||"",m.actividad||"",m.obs||""]);});
        const ws=wb.utils.aoa_to_sheet(rows);
        ws["!cols"]=[14,14,22,20,14,14,16,12,18,10,10,28,35].map(w=>({wch:w}));
        ws["!autofilter"]={ref:`A1:M${rows.length}`};
        wb.utils.book_append_sheet(workbook,ws,"OTs por Fecha");
        const rango=(fechaDesde||"inicio")+"_al_"+(fechaHasta||"hoy");
        const fn=`SisteMant_Fecha_${rango}.xlsx`;
        const wbout=wb.write(workbook,{bookType:"xlsx",type:"array"});
        const blob=new Blob([wbout],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
        const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=fn;
        document.body.appendChild(a);a.click();setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},500);
      };

      return <div>
        {/* Filtros */}
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>Filtros</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Fecha Desde</div>
              <input type="date" value={fechaDesde} onChange={e=>setFechaDesde(e.target.value)}
                style={{...INP,width:"100%",fontSize:12}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Fecha Hasta</div>
              <input type="date" value={fechaHasta} onChange={e=>setFechaHasta(e.target.value)}
                style={{...INP,width:"100%",fontSize:12}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Centro</div>
              <select value={filtroFechaCentro} onChange={e=>setFiltroFechaCentro(e.target.value)}
                style={{...INP,width:"100%",fontSize:12}}>
                {centrosLF.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Tipo OT</div>
              <select value={filtroFechaTipo} onChange={e=>setFiltroFechaTipo(e.target.value)}
                style={{...INP,width:"100%",fontSize:12}}>
                {tiposOT.map(t=><option key={t} value={t}>{t==="todos"?"Todos los tipos":t}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Técnico</div>
              <select value={filtroFechaTec} onChange={e=>setFiltroFechaTec(e.target.value)}
                style={{...INP,width:"100%",fontSize:12}}>
                {tecnicos.map(t=><option key={t} value={t}>{t==="todos"?"Todos los técnicos":t}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            {/* Accesos rápidos */}
            {[
              ["Hoy",hoyStr,hoyStr],
              ["Esta semana",(()=>{const d=new Date();d.setDate(d.getDate()-d.getDay()+1);return d.toISOString().split("T")[0];})(),hoyStr],
              ["Este mes",(()=>{const d=new Date();return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`;})(),hoyStr],
              ["Último trimestre",(()=>{const d=new Date();d.setMonth(d.getMonth()-3);return d.toISOString().split("T")[0];})(),hoyStr],
            ].map(([l,desde,hasta])=><button key={l}
              onClick={()=>{setFechaDesde(desde);setFechaHasta(hasta);}}
              style={{...BTNS,fontSize:11,padding:"4px 10px"}}>{l}</button>)}
            <button onClick={()=>{setFechaDesde("");setFechaHasta("");setFiltroFechaCentro("todos");setFiltroFechaTipo("todos");setFiltroFechaTec("todos");}}
              style={{...BTNS,fontSize:11}}>✕ Limpiar</button>
            <span style={{fontSize:11,color:C.txt3,marginLeft:4}}>
              {mtFecha.length} OT{mtFecha.length!==1?"s":""} encontrada{mtFecha.length!==1?"s":""}
              {(!fechaDesde&&!fechaHasta)?" (sin filtro de fecha — mostrando todas)":""}
            </span>
            <button onClick={exportarFecha} style={{...BTNS,fontSize:11,display:"flex",alignItems:"center",gap:4,marginLeft:"auto"}}>
              ⬇ Exportar Excel
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
          <Card l="OTs totales" v={mtFecha.length}/>
          <Card l="Preventivas" v={prev.length} c={C.blue}/>
          <Card l="Correctivas" v={corr.length} c={C.amber}/>
          <Card l="Diagnósticos" v={mtFecha.filter(m=>(m.tipo||"").toLowerCase().includes("diagnós")).length} c={C.green}/>
          <Card l="Garantías" v={mtFecha.filter(m=>(m.tipo||"").toLowerCase().includes("garantí")).length} c={C.orange}/>
          {mtFecha.length>0&&<Card l="Técnicos únicos" v={new Set(mtFecha.map(m=>m.tec?.trim()||"")).size} c={C.txt2}/>}
        </div>

        {/* Tabla detalle */}
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>
            Detalle de OTs — {mtFecha.length} registro{mtFecha.length!==1?"s":""}
            {fechaDesde&&fechaHasta&&fechaDesde===fechaHasta&&<span style={{color:C.orange,marginLeft:8,fontWeight:700,fontSize:12}}>📅 {fechaDesde}</span>}
            {fechaDesde&&fechaHasta&&fechaDesde!==fechaHasta&&<span style={{color:C.txt3,marginLeft:8,fontWeight:400}}>del {fechaDesde} al {fechaHasta}</span>}
          </div>
          {mtFecha.length===0
            ?<div style={{padding:40,textAlign:"center",color:C.txt3}}>
                {!fechaDesde&&!fechaHasta?"Selecciona un rango de fechas o usa los accesos rápidos":"Sin OTs en el período seleccionado"}
              </div>
            :<div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.surf}}>
                    {["Fecha","Próxima","Centro","Equipo","Marca / Modelo","Serie","Tipo OT","Técnico","N° OT","Actividad","Obs."].map(h=>
                      <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.5,whiteSpace:"nowrap"}}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {mtFecha.map((m,i)=>{
                    const e=gE(m.eId)||{};
                    const dias=m.fP?diffD2(m.fP):null;
                    const col=dias===null?C.txt3:dias<0?C.red:dias===0?C.orange:C.green;
                    const tp=esPreventiva(m)?{bg:C.blueL,c:C.blue}:
                      (m.tipo||"").toLowerCase()==="correctiva"?{bg:C.amberL,c:C.amber}:
                      (m.tipo||"").toLowerCase().includes("diagnós")?{bg:C.greenL,c:C.green}:
                      {bg:C.orangeL,c:C.orange};
                    return <tr key={m.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf}}>
                      <td style={{padding:"8px 10px",fontWeight:500,whiteSpace:"nowrap",color:C.txt}}>{m.fR}</td>
                      <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                        {m.fP
                          ?<span style={{color:col,fontWeight:dias!==null&&dias<0?700:400}}>{m.fP}{dias!==null&&<span style={{fontSize:10,marginLeft:4,opacity:0.8}}>{dias<0?`(-${Math.abs(dias)}d)`:`(+${dias}d)`}</span>}</span>
                          :<span style={{color:C.txt3}}>—</span>}
                      </td>
                      <td style={{padding:"8px 10px"}}>{gC(m.eId)}</td>
                      <td style={{padding:"8px 10px",fontWeight:500}}>{e.tipo||"?"}</td>
                      <td style={{padding:"8px 10px",color:C.txt2}}>{e.marca||""} {e.modelo||""}</td>
                      <td style={{padding:"8px 10px",fontFamily:"monospace",fontSize:10,color:C.txt3}}>{e.serie||"—"}</td>
                      <td style={{padding:"8px 10px"}}><span style={{background:tp.bg,color:tp.c,borderRadius:20,padding:"2px 7px",fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>{m.tipo||"Preventiva"}</span></td>
                      <td style={{padding:"8px 10px",color:C.txt2,whiteSpace:"nowrap"}}>{m.tec||"—"}</td>
                      <td style={{padding:"8px 10px",fontFamily:"monospace",fontSize:11,color:C.orange}}>{m.ot||"—"}</td>
                      <td style={{padding:"8px 10px",color:C.txt2,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={m.actividad||""}>{m.actividad||"—"}</td>
                      <td style={{padding:"8px 10px",color:C.txt3,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={m.obs||""}>{m.obs||"—"}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>;
    })()}

    {tab==="centros"&&(()=>{
      const modelos=["todos",...new Set(eq.map(e=>e.modelo).filter(Boolean).sort())];
      const tipos=["todos",...new Set(eq.map(e=>e.tipo).filter(Boolean).sort())];
      const centrosList=[{id:"todos",nombre:"Todos los centros"},...cl];
      const eqFiltrados=eq.filter(e=>{if(filtroCentro!=="todos"&&String(e.cId)!==String(filtroCentro))return false;if(filtroTipo!=="todos"&&e.tipo!==filtroTipo)return false;if(filtroModelo!=="todos"&&e.modelo!==filtroModelo)return false;return true;});
      const eqIds=new Set(eqFiltrados.map(e=>e.id));const mtFiltrados=mt.filter(m=>eqIds.has(m.eId));
      const prev=mtFiltrados.filter(esPreventiva);const corr=mtFiltrados.filter(m=>!esPreventiva(m));
      const vencidos=eqFiltrados.filter(e=>latestPrev[e.id]&&diffD2(latestPrev[e.id].fP)<0);
      const sinPrev=eqFiltrados.filter(e=>!latestPrev[e.id]);
      const pct=eqFiltrados.length?Math.round((eqFiltrados.length-vencidos.length-sinPrev.length)/eqFiltrados.length*100):0;
      return <div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>Filtros</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <div><div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Centro</div>
              <select value={filtroCentro} onChange={e=>setFiltroCentro(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
                {centrosList.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select></div>
            <div><div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Tipo de Equipo</div>
              <select value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
                {tipos.map(t=><option key={t} value={t}>{t==="todos"?"Todos los tipos":t}</option>)}
              </select></div>
            <div><div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Modelo</div>
              <select value={filtroModelo} onChange={e=>setFiltroModelo(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
                {modelos.map(m=><option key={m} value={m}>{m==="todos"?"Todos los modelos":m}</option>)}
              </select></div>
          </div>
          <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>{setFiltroCentro("todos");setFiltroTipo("todos");setFiltroModelo("todos");}} style={{...BTNS,fontSize:11}}>✕ Limpiar filtros</button>
            <span style={{fontSize:11,color:C.txt3}}>{eqFiltrados.length} equipo{eqFiltrados.length!==1?"s":""} encontrado{eqFiltrados.length!==1?"s":""}</span>
          </div>
        </div>
        <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
          <Card l="Equipos" v={eqFiltrados.length}/><Card l="OTs totales" v={mtFiltrados.length}/>
          <Card l="Preventivas" v={prev.length} c={C.blue}/><Card l="Correctivas" v={corr.length} c={C.amber}/>
          <Card l="Vencidos" v={vencidos.length} c={C.red}/><Card l="Sin preventiva" v={sinPrev.length} c={C.amber}/>
          <Card l="Cumplimiento" v={eqFiltrados.length?pct+"%":"—"} c={pct>=80?C.green:pct>=60?C.amber:C.red}/>
        </div>
        {eqFiltrados.length===0
          ?<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:40,textAlign:"center",color:C.txt3}}>Sin equipos con los filtros seleccionados</div>
          :<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>Detalle — {eqFiltrados.length} equipos</div>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:C.surf}}>{["Centro","Tipo","Marca","Modelo","Serie","Estado","Frec.","Última Prev.","Próxima","Días","OTs"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.5,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
              <tbody>{eqFiltrados.map((e,i)=>{const lp=latestPrev[e.id];const dias=lp?diffD2(lp.fP):null;const nOTs=mt.filter(m=>m.eId===e.id).length;const col=dias===null?C.txt3:dias<0?C.red:dias===0?C.orange:C.green;
                return <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf}}>
                  <td style={{padding:"8px 12px"}}>{gC(e.id)}</td><td style={{padding:"8px 12px"}}>{e.tipo}</td>
                  <td style={{padding:"8px 12px",color:C.txt2}}>{e.marca}</td><td style={{padding:"8px 12px",color:C.txt2}}>{e.modelo}</td>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",fontSize:11,color:C.txt2}}>{e.serie||"—"}</td>
                  <td style={{padding:"8px 12px"}}><span style={{background:e.est==="activo"?C.greenL:C.redL,color:e.est==="activo"?C.green:C.red,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:600}}>{e.est==="activo"?"Activo":"Inactivo"}</span></td>
                  <td style={{padding:"8px 12px",color:C.txt2}}>{e.frecDefault?e.frecDefault+"d":"—"}</td>
                  <td style={{padding:"8px 12px",color:C.txt2,whiteSpace:"nowrap"}}>{lp?.fR||"—"}</td>
                  <td style={{padding:"8px 12px",color:col,whiteSpace:"nowrap"}}>{lp?.fP||"—"}</td>
                  <td style={{padding:"8px 12px"}}>{dias===null?<span style={{color:C.txt3,fontSize:11}}>—</span>:<span style={{background:col+"22",color:col,borderRadius:20,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{dias<0?`-${Math.abs(dias)}d`:dias===0?"Hoy":`+${dias}d`}</span>}</td>
                  <td style={{padding:"8px 12px",textAlign:"center",color:C.txt2}}>{nOTs}</td>
                </tr>;})}
              </tbody>
            </table></div>
          </div>}
      </div>;})()} 
  </div>;
}

// ═══════════════════════════════════════════════════════════════════
// PROGRAMA DE HORAS — Control de ciclo PM por días (frecDefault)
// Ciclo 4 mantenciones: PM-1 → PM-2 → PM-3 → PM-Mayor → PM-1 → ...
// ═══════════════════════════════════════════════════════════════════
function ProgramaHoras({cl,eq,mt}){
  const [filtroCentro,setFiltroCentro]=useState("todos");
  const [filtroTipo,setFiltroTipo]=useState("todos");
  const [soloUrgentes,setSoloUrgentes]=useState(false);

  const hoy=new Date();hoy.setHours(0,0,0,0);
  const diffD=str=>Math.round((new Date(str+"T00:00:00")-hoy)/86400000);
  const gC=id=>{const e=eq.find(x=>x.id===id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const fmt=str=>{try{const d=new Date(str+"T00:00:00");return d.toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"});}catch{return "—";}};

  // Definición del ciclo de 4 mantenciones
  const CICLO=[
    {pos:1,label:"PM-1",sub:"Mantención básica",color:"#4CAF50",bg:"#4CAF5022"},
    {pos:2,label:"PM-2",sub:"Mantención media",color:C.blue,bg:C.blueL},
    {pos:3,label:"PM-3",sub:"Mantención básica",color:C.amber,bg:C.amberL},
    {pos:4,label:"PM-Mayor",sub:"Mantención mayor completa",color:C.orange,bg:C.orangeL},
  ];

  // Calcular estado del ciclo para un equipo
  const getCiclo=eId=>{
    const e=eq.find(x=>x.id===eId);
    const frecDias=parseInt(e?.frecDefault)||0;
    if(!frecDias)return null;

    // Todas las OTs preventivas ordenadas por fecha
    const otsPrev=mt.filter(m=>m.eId===eId&&(m.tipo||"Preventiva").toLowerCase()==="preventiva")
                    .sort((a,b)=>a.fR.localeCompare(b.fR));

    const totalOTs=otsPrev.length;
    const ultimaOT=otsPrev[otsPrev.length-1]||null;

    // Posición actual en el ciclo (0-indexed → 1-indexed para mostrar)
    const posActual=(totalOTs%4); // 0=próxima es PM-1, 1=PM-2, etc.
    const proximaPM=CICLO[posActual]; // siguiente PM a realizar

    // Fecha próxima = última OT + frecDefault (o hoy + frecDefault si no hay OTs)
    const baseDate=ultimaOT?.fP||ultimaOT?.fR||null;
    const proxFecha=baseDate||null;
    const diasRestantes=proxFecha?diffD(proxFecha):null;

    // Historial del ciclo con etiquetas
    const historial=otsPrev.map((m,i)=>({
      ...m,
      pmLabel:CICLO[i%4].label,
      pmColor:CICLO[i%4].color,
      pmBg:CICLO[i%4].bg,
      numCiclo:Math.floor(i/4)+1,
    }));

    // Número de ciclo actual (cuántas veces se ha completado el ciclo de 4)
    const nCicloActual=Math.floor(totalOTs/4)+1;

    return{frecDias,totalOTs,ultimaOT,proximaPM,proxFecha,diasRestantes,historial,nCicloActual,posActual};
  };

  const tipos=["todos",...new Set(eq.map(e=>e.tipo).filter(Boolean).sort())];
  const centrosList=[{id:"todos",nombre:"Todos los centros"},...cl];

  const equiposFiltrados=eq.filter(e=>{
    if(!parseInt(e.frecDefault))return false; // sin frecuencia definida
    if(filtroCentro!=="todos"&&String(e.cId)!==String(filtroCentro))return false;
    if(filtroTipo!=="todos"&&e.tipo!==filtroTipo)return false;
    if(soloUrgentes){
      const c=getCiclo(e.id);
      if(!c||c.diasRestantes===null||c.diasRestantes>14)return false;
    }
    return true;
  });

  // KPIs
  const todos=eq.filter(e=>parseInt(e.frecDefault));
  const vencidos=todos.filter(e=>{const c=getCiclo(e.id);return c&&c.diasRestantes!==null&&c.diasRestantes<0;});
  const proximos=todos.filter(e=>{const c=getCiclo(e.id);return c&&c.diasRestantes!==null&&c.diasRestantes>=0&&c.diasRestantes<=14;});
  const pmMayores=todos.filter(e=>{const c=getCiclo(e.id);return c&&c.proximaPM?.label==="PM-Mayor";});

  const exportar=()=>{
    const wb=window.XLSX||XLSX;if(!wb)return;
    const workbook=wb.utils.book_new();
    const rows=[["Centro","Tipo","Marca","Modelo","Serie","Frec.(días)","OTs realizadas","Ciclo N°","Próxima PM","Fecha Próxima","Días restantes","Estado"]];
    equiposFiltrados.forEach(e=>{
      const c=getCiclo(e.id);if(!c)return;
      const estado=c.diasRestantes===null?"Sin fecha":`${c.diasRestantes<0?"Vencida "+Math.abs(c.diasRestantes)+"d":c.diasRestantes===0?"Hoy":"En "+c.diasRestantes+"d"}`;
      rows.push([gC(e.id),e.tipo,e.marca,e.modelo,e.serie||"",c.frecDias,c.totalOTs,c.nCicloActual,c.proximaPM.label,c.proxFecha||"",c.diasRestantes??"",estado]);
    });
    const ws=wb.utils.aoa_to_sheet(rows);ws["!cols"]=[22,18,12,12,14,10,12,10,12,14,14,14].map(w=>({wch:w}));
    ws["!autofilter"]={ref:`A1:L${rows.length}`};
    wb.utils.book_append_sheet(workbook,ws,"Programa");
    const out=wb.write(workbook,{bookType:"xlsx",type:"array"});
    const blob=new Blob([out],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
    const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`SisteMant_ProgramaHoras_${new Date().toISOString().split("T")[0]}.xlsx`;
    document.body.appendChild(a);a.click();setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},500);
  };

  return <div style={{padding:24,maxWidth:1100,margin:"0 auto"}}>

    {/* Header */}
    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontSize:22,fontWeight:700}}>Programa <span style={{color:C.orange}}>de Mantenciones</span></div>
        <div style={{fontSize:12,color:C.txt2,marginTop:2}}>Control de ciclo por días · PM-1 → PM-2 → PM-3 → PM-Mayor → PM-1 → ...</div>
      </div>
      <button onClick={exportar} style={{...BTNP,display:"flex",alignItems:"center",gap:6}}>⬇ Exportar Excel</button>
    </div>

    {/* Leyenda del ciclo */}
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:14}}>
      <div style={{fontSize:11,color:C.txt3,marginBottom:10,textTransform:"uppercase",letterSpacing:1,fontWeight:600}}>Ciclo de mantenciones</div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        {CICLO.map((pm,i)=><div key={pm.pos} style={{display:"flex",alignItems:"center",gap:6}}>
          {i>0&&<span style={{color:C.txt3,fontSize:16}}>→</span>}
          <div style={{background:pm.bg,border:`1px solid ${pm.color}44`,borderRadius:8,padding:"6px 14px",textAlign:"center"}}>
            <div style={{fontSize:12,fontWeight:700,color:pm.color}}>{pm.label}</div>
            <div style={{fontSize:10,color:C.txt3}}>{pm.sub}</div>
          </div>
        </div>)}
        <span style={{color:C.txt3,fontSize:16}}>→</span>
        <span style={{fontSize:11,color:C.txt3,fontStyle:"italic"}}>vuelve a PM-1</span>
      </div>
    </div>

    {/* KPIs */}
    <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
      {[
        {l:"Con ciclo activo",v:todos.length,c:C.txt},
        {l:"Vencidas",v:vencidos.length,c:C.red},
        {l:"Próximas 14 días",v:proximos.length,c:C.orange},
        {l:"PM-Mayor próxima",v:pmMayores.length,c:C.amber},
      ].map(k=><div key={k.l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",minWidth:130}}>
        <div style={{fontSize:10,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:3}}>{k.l}</div>
        <div style={{fontSize:28,fontWeight:700,color:k.c,lineHeight:1}}>{k.v}</div>
      </div>)}
    </div>

    {/* Filtros */}
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:12,marginBottom:14,display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
      <div style={{flex:1,minWidth:160}}>
        <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Centro</div>
        <select value={filtroCentro} onChange={e=>setFiltroCentro(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
          {centrosList.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </div>
      <div style={{flex:1,minWidth:160}}>
        <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Tipo equipo</div>
        <select value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
          {tipos.map(t=><option key={t} value={t}>{t==="todos"?"Todos":t}</option>)}
        </select>
      </div>
      <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.txt2,cursor:"pointer",paddingBottom:6}}>
        <input type="checkbox" checked={soloUrgentes} onChange={e=>setSoloUrgentes(e.target.checked)} style={{accentColor:C.orange}}/>
        Solo urgentes (≤14 días)
      </label>
      <div style={{fontSize:11,color:C.txt3,paddingBottom:6}}>{equiposFiltrados.length} equipos</div>
    </div>

    {/* Tabla */}
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:C.surf}}>
              {["Centro","Equipo / Serie","Frec.","OTs\nRealizadas","Ciclo\nN°","Próxima\nPM","Fecha Próxima\nMantención","Días\nRestantes","Progreso del ciclo"].map(h=>
                <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.4,whiteSpace:"pre",lineHeight:1.3}}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {equiposFiltrados.map((e,i)=>{
              const c=getCiclo(e.id);
              if(!c)return null;
              const dias=c.diasRestantes;
              const urgente=dias!==null&&dias<0;
              const proximo=dias!==null&&dias>=0&&dias<=14;
              const rowBg=urgente?"rgba(212,44,44,0.08)":proximo?"rgba(240,120,0,0.06)":i%2===0?"transparent":C.surf;
              const colDias=dias===null?C.txt3:dias<0?C.red:dias<=7?C.orange:dias<=14?"#F9A825":C.green;

              return <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`,background:rowBg}}>
                <td style={{padding:"10px 12px",color:C.txt2,fontSize:11}}>{gC(e.id)}</td>
                <td style={{padding:"10px 12px"}}>
                  <div style={{fontWeight:500}}>{e.tipo}</div>
                  <div style={{fontSize:10,color:C.txt3}}>{e.marca} {e.modelo}</div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:C.txt3,marginTop:2}}>{e.serie||"—"}</div>
                </td>
                <td style={{padding:"10px 12px",color:C.orange,fontWeight:600,whiteSpace:"nowrap"}}>c/{c.frecDias}d</td>
                <td style={{padding:"10px 12px",textAlign:"center",fontWeight:700,fontSize:16,color:C.txt}}>{c.totalOTs}</td>
                <td style={{padding:"10px 12px",textAlign:"center"}}>
                  <span style={{background:C.orangeL,color:C.orange,borderRadius:20,padding:"2px 10px",fontSize:12,fontWeight:700}}>#{c.nCicloActual}</span>
                </td>
                <td style={{padding:"10px 12px"}}>
                  <span style={{background:c.proximaPM.bg,color:c.proximaPM.color,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                    {c.proximaPM.label}
                  </span>
                  <div style={{fontSize:10,color:C.txt3,marginTop:2}}>{c.proximaPM.sub}</div>
                </td>
                <td style={{padding:"10px 12px",whiteSpace:"nowrap",color:C.txt}}>{c.proxFecha?fmt(c.proxFecha):"—"}</td>
                <td style={{padding:"10px 12px",textAlign:"center"}}>
                  {dias===null
                    ?<span style={{color:C.txt3,fontSize:11}}>Sin fecha</span>
                    :<div>
                      <div style={{fontSize:18,fontWeight:700,color:colDias,lineHeight:1}}>{dias<0?`-${Math.abs(dias)}`:dias===0?"Hoy":"+"+dias}</div>
                      <div style={{fontSize:9,color:C.txt3}}>{dias<0?"días vencida":dias===0?"":dias===1?"mañana":"días"}</div>
                    </div>
                  }
                </td>
                <td style={{padding:"10px 12px",minWidth:160}}>
                  {/* Barra de progreso del ciclo de 4 mantenciones */}
                  <div style={{display:"flex",gap:3,marginBottom:4}}>
                    {CICLO.map((pm,idx)=><div key={pm.pos} style={{flex:1,height:12,borderRadius:4,background:idx<c.posActual?pm.color:idx===c.posActual?"transparent":C.border,border:idx===c.posActual?`2px dashed ${pm.color}`:"none",position:"relative"}} title={pm.label}>
                      {idx<c.posActual&&<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"#fff",fontWeight:700}}>✓</div>}
                    </div>)}
                  </div>
                  <div style={{display:"flex",gap:3}}>
                    {CICLO.map((pm,idx)=><div key={pm.pos} style={{flex:1,fontSize:9,color:idx<c.posActual?pm.color:idx===c.posActual?pm.color:C.txt3,textAlign:"center",fontWeight:idx===c.posActual?700:400}}>{pm.label}</div>)}
                  </div>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
        {equiposFiltrados.length===0&&
          <div style={{padding:40,textAlign:"center",color:C.txt3}}>
            {todos.length===0?"Ningún equipo tiene frecuencia definida — configúrala en el botón ✏️ de cada equipo":"Sin resultados con los filtros seleccionados"}
          </div>
        }
      </div>
    </div>

    {/* Historial de OTs por ciclo */}
    {equiposFiltrados.length>0&&equiposFiltrados.length<=3&&equiposFiltrados.map(e=>{
      const c=getCiclo(e.id);if(!c||c.historial.length===0)return null;
      return <div key={e.id} style={{marginTop:16,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
        <div style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:600,color:C.txt2,textTransform:"uppercase",letterSpacing:1}}>
          Historial — {e.tipo} {e.marca} {e.modelo} · {e.serie}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,padding:12}}>
          {c.historial.map((m,i)=><div key={m.id} style={{background:m.pmBg,border:`1px solid ${m.pmColor}44`,borderRadius:8,padding:"8px 12px",minWidth:140}}>
            <div style={{fontSize:10,fontWeight:700,color:m.pmColor}}>{m.pmLabel} · Ciclo #{m.numCiclo}</div>
            <div style={{fontSize:11,color:C.txt,marginTop:2}}>{fmt(m.fR)}</div>
            {m.ot&&<div style={{fontSize:10,color:C.txt3}}>OT: {m.ot}</div>}
            {m.tec&&<div style={{fontSize:10,color:C.txt3}}>{m.tec}</div>}
          </div>)}
        </div>
      </div>;
    })}
  </div>;
}

// ═══════════════════════════════════════════════════════════════════
// PRÓXIMOS 6 MESES — Calendario de mantenciones programadas
// ═══════════════════════════════════════════════════════════════════
function ProximosMeses({cl,eq,mt}){
  const hoy=new Date();hoy.setHours(0,0,0,0);
  const [mesesVista,setMesesVista]=useState(6);
  const [filtroCentro,setFiltroCentro]=useState("todos");
  const [filtroTipo,setFiltroTipo]=useState("todos");
  const [vistaAgrupacion,setVistaAgrupacion]=useState("mes"); // "mes" | "semana" | "equipo"
  const [soloMayor,setSoloMayor]=useState(false);

  const gC=id=>{const e=eq.find(x=>x.id===id);return e?cl.find(c=>c.id===e.cId)?.nombre||"—":"—";};
  const addDias=(fecha,dias)=>{const d=new Date(fecha);d.setDate(d.getDate()+dias);return d;};
  const fmt=(d)=>d instanceof Date?d.toLocaleDateString("es-CL",{day:"2-digit",month:"short"}):d;
  const fmtLargo=(d)=>d instanceof Date?d.toLocaleDateString("es-CL",{day:"2-digit",month:"long",year:"numeric"}):d;
  const diffD=(d)=>Math.round((d-hoy)/86400000);

  // Ciclo PM
  const PM_CICLO=[
    {label:"PM-1",color:"#4CAF50",bg:"#4CAF5022"},
    {label:"PM-2",color:C.blue,bg:C.blueL},
    {label:"PM-3",color:C.amber,bg:C.amberL},
    {label:"PM-Mayor",color:C.orange,bg:C.orangeL},
  ];

  // Generar todas las mantenciones proyectadas en los próximos N meses
  const fechaFin=addDias(hoy,mesesVista*30);

  const generarProximas=()=>{
    const eventos=[];
    eq.forEach(e=>{
      const frecDias=parseInt(e.frecDefault)||0;
      if(!frecDias)return;
      if(filtroCentro!=="todos"&&String(e.cId)!==String(filtroCentro))return;
      if(filtroTipo!=="todos"&&e.tipo!==filtroTipo)return;

      // Última OT preventiva → próxima fecha base
      const otsPrev=mt.filter(m=>m.eId===e.id&&(m.tipo||"Preventiva").toLowerCase()==="preventiva")
                      .sort((a,b)=>b.fR.localeCompare(a.fR));
      const totalPrev=otsPrev.length;
      const ultimaOT=otsPrev[0]||null;
      let proxFechaBase=ultimaOT?.fP?new Date(ultimaOT.fP+"T00:00:00"):
                        ultimaOT?.fR?addDias(new Date(ultimaOT.fR+"T00:00:00"),frecDias):
                        null;
      if(!proxFechaBase)return;

      // Proyectar todas las fechas dentro del período
      let fecha=new Date(proxFechaBase);
      let nOT=totalPrev; // índice para calcular PM label
      while(fecha<=fechaFin){
        if(fecha>=hoy){
          const pm=PM_CICLO[nOT%4];
          if(!soloMayor||pm.label==="PM-Mayor"){
            eventos.push({
              eId:e.id,tipo:e.tipo,marca:e.marca,modelo:e.modelo,
              serie:e.serie||"",centro:gC(e.id),cId:e.cId,
              fecha:new Date(fecha),frecDias,
              pm,dias:diffD(new Date(fecha)),
              nMant:nOT+1,
            });
          }
        }
        fecha=addDias(fecha,frecDias);
        nOT++;
      }
    });
    return eventos.sort((a,b)=>a.fecha-b.fecha);
  };

  const eventos=generarProximas();

  // Agrupar por mes
  const porMes={};
  eventos.forEach(ev=>{
    const key=ev.fecha.toLocaleDateString("es-CL",{month:"long",year:"numeric"});
    if(!porMes[key])porMes[key]={label:key,fecha:ev.fecha,items:[]};
    porMes[key].items.push(ev);
  });

  // Agrupar por equipo
  const porEquipo={};
  eventos.forEach(ev=>{
    const key=`${ev.tipo} ${ev.marca} ${ev.serie}`;
    if(!porEquipo[key])porEquipo[key]={label:`${ev.tipo} — ${ev.marca} ${ev.modelo}`,serie:ev.serie,centro:ev.centro,items:[]};
    porEquipo[key].items.push(ev);
  });

  const centrosList=[{id:"todos",nombre:"Todos los centros"},...cl];
  const tipos=["todos",...new Set(eq.map(e=>e.tipo).filter(Boolean).sort())];

  // KPIs del período
  const enPeriodo=eventos.length;
  const pmMayores=eventos.filter(e=>e.pm.label==="PM-Mayor").length;
  const proximos14=eventos.filter(e=>e.dias>=0&&e.dias<=14).length;
  const vencidos=eventos.filter(e=>e.dias<0).length;

  // Exportar Excel
  const exportar=()=>{
    const wb=window.XLSX||XLSX;if(!wb)return;
    const workbook=wb.utils.book_new();
    const rows=[["Fecha","Días","Centro","Tipo Equipo","Marca","Modelo","Serie","PM","N° Mantención","Frecuencia"]];
    eventos.forEach(ev=>rows.push([
      fmtLargo(ev.fecha),ev.dias,ev.centro,ev.tipo,ev.marca,ev.modelo,
      ev.serie,ev.pm.label,ev.nMant,ev.frecDias+"d"
    ]));
    const ws=wb.utils.aoa_to_sheet(rows);
    ws["!cols"]=[18,8,22,18,12,12,14,12,14,10].map(w=>({wch:w}));
    ws["!autofilter"]={ref:`A1:J${rows.length}`};
    wb.utils.book_append_sheet(workbook,ws,"Calendario");
    const out=wb.write(workbook,{bookType:"xlsx",type:"array"});
    const blob=new Blob([out],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
    const url=URL.createObjectURL(blob);const a=document.createElement("a");
    a.href=url;a.download=`SisteMant_Calendario_${mesesVista}meses_${new Date().toISOString().split("T")[0]}.xlsx`;
    document.body.appendChild(a);a.click();setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},500);
  };

  return <div style={{padding:24,maxWidth:1200,margin:"0 auto"}}>

    {/* Header */}
    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontSize:22,fontWeight:700}}>Calendario <span style={{color:C.orange}}>de Mantenciones</span></div>
        <div style={{fontSize:12,color:C.txt2,marginTop:2}}>Proyección de mantenciones programadas basada en frecDefault de cada equipo</div>
      </div>
      <button onClick={exportar} style={{...BTNP,display:"flex",alignItems:"center",gap:6}}>⬇ Exportar Excel</button>
    </div>

    {/* Controles */}
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:14,marginBottom:14}}>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
        <div>
          <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Período</div>
          <div style={{display:"flex",gap:4}}>
            {[3,6,9,12].map(n=><button key={n} onClick={()=>setMesesVista(n)}
              style={{padding:"5px 12px",fontSize:12,borderRadius:6,border:`1px solid ${mesesVista===n?C.orange:C.border}`,background:mesesVista===n?C.orangeL:"transparent",color:mesesVista===n?C.orange:C.txt2,cursor:"pointer",fontFamily:"inherit",fontWeight:mesesVista===n?600:400}}>
              {n}m
            </button>)}
          </div>
        </div>
        <div style={{flex:1,minWidth:160}}>
          <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Centro</div>
          <select value={filtroCentro} onChange={e=>setFiltroCentro(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
            {centrosList.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div style={{flex:1,minWidth:160}}>
          <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Tipo equipo</div>
          <select value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)} style={{...INP,width:"100%",fontSize:12}}>
            {tipos.map(t=><option key={t} value={t}>{t==="todos"?"Todos":t}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:11,color:C.txt3,marginBottom:4}}>Vista</div>
          <div style={{display:"flex",gap:4}}>
            {[["mes","Por mes"],["equipo","Por equipo"]].map(([v,l])=><button key={v} onClick={()=>setVistaAgrupacion(v)}
              style={{padding:"5px 10px",fontSize:11,borderRadius:6,border:`1px solid ${vistaAgrupacion===v?C.orange:C.border}`,background:vistaAgrupacion===v?C.orangeL:"transparent",color:vistaAgrupacion===v?C.orange:C.txt2,cursor:"pointer",fontFamily:"inherit"}}>
              {l}
            </button>)}
          </div>
        </div>
        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.txt2,cursor:"pointer",paddingBottom:6}}>
          <input type="checkbox" checked={soloMayor} onChange={e=>setSoloMayor(e.target.checked)} style={{accentColor:C.orange}}/>
          Solo PM-Mayor
        </label>
      </div>
    </div>

    {/* KPIs */}
    <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
      {[
        {l:`Mantenciones en ${mesesVista}m`,v:enPeriodo,c:C.txt},
        {l:"Vencidas / Urgentes",v:vencidos,c:vencidos>0?C.red:C.txt3},
        {l:"Próximas 14 días",v:proximos14,c:proximos14>0?C.orange:C.txt3},
        {l:"PM-Mayor en período",v:pmMayores,c:C.orange},
      ].map(k=><div key={k.l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",flex:1,minWidth:130}}>
        <div style={{fontSize:10,color:C.txt2,textTransform:"uppercase",letterSpacing:1,marginBottom:3}}>{k.l}</div>
        <div style={{fontSize:28,fontWeight:700,color:k.c,lineHeight:1}}>{k.v}</div>
      </div>)}
    </div>

    {/* Vista por MES */}
    {vistaAgrupacion==="mes"&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
      {Object.values(porMes).map(mes=><div key={mes.label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
        {/* Header del mes */}
        <div style={{padding:"10px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:C.surf}}>
          <div style={{fontSize:14,fontWeight:700,textTransform:"capitalize",color:C.txt}}>{mes.label}</div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            {["PM-1","PM-2","PM-3","PM-Mayor"].map(pm=>{
              const n=mes.items.filter(e=>e.pm.label===pm).length;
              if(!n)return null;
              const c=PM_CICLO.find(p=>p.label===pm);
              return <span key={pm} style={{background:c.bg,color:c.color,borderRadius:20,padding:"2px 8px",fontSize:11,fontWeight:600}}>{pm}: {n}</span>;
            })}
            <span style={{fontSize:11,color:C.txt3,marginLeft:4}}>{mes.items.length} total</span>
          </div>
        </div>
        {/* Items del mes */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:1,background:C.border}}>
          {mes.items.map((ev,i)=>{
            const urgente=ev.dias<0;
            const proximo=ev.dias>=0&&ev.dias<=7;
            return <div key={`${ev.eId}-${ev.fecha.toISOString()}`}
              style={{background:urgente?"rgba(212,44,44,0.08)":proximo?"rgba(240,120,0,0.06)":C.card,padding:"10px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:4}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.txt}}>{ev.tipo}</div>
                  <div style={{fontSize:11,color:C.txt2}}>{ev.marca} {ev.modelo}</div>
                  <div style={{fontSize:10,color:C.txt3,fontFamily:"monospace"}}>{ev.serie} · {ev.centro}</div>
                </div>
                <span style={{background:ev.pm.bg,color:ev.pm.color,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>{ev.pm.label}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:C.txt2}}>📅 {fmt(ev.fecha)}</span>
                <span style={{fontSize:12,fontWeight:700,color:urgente?C.red:proximo?C.orange:C.green}}>
                  {ev.dias<0?`Vencida ${Math.abs(ev.dias)}d`:ev.dias===0?"Hoy":`En ${ev.dias}d`}
                </span>
              </div>
            </div>;
          })}
        </div>
      </div>)}
      {Object.keys(porMes).length===0&&
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:40,textAlign:"center",color:C.txt3}}>
          Sin mantenciones proyectadas · Verifica que los equipos tengan frecuencia definida y OTs registradas
        </div>}
    </div>}

    {/* Vista por EQUIPO */}
    {vistaAgrupacion==="equipo"&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:C.surf}}>
              {["Equipo","Centro","Serie","Frec.","Próximas mantenciones en el período →"].map(h=>
                <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:600,color:C.txt3,textTransform:"uppercase",letterSpacing:0.4,whiteSpace:"nowrap"}}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {Object.values(porEquipo).map((grp,i)=><tr key={grp.label} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"transparent":C.surf}}>
              <td style={{padding:"10px 12px"}}>
                <div style={{fontWeight:500}}>{grp.label}</div>
              </td>
              <td style={{padding:"10px 12px",color:C.txt2,fontSize:11}}>{grp.centro}</td>
              <td style={{padding:"10px 12px",fontFamily:"monospace",fontSize:10,color:C.txt3}}>{grp.serie||"—"}</td>
              <td style={{padding:"10px 12px",color:C.orange,fontWeight:600,whiteSpace:"nowrap"}}>c/{grp.items[0]?.frecDias}d</td>
              <td style={{padding:"10px 12px"}}>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {grp.items.map((ev,j)=>{
                    const urgente=ev.dias<0;
                    const proximo=ev.dias>=0&&ev.dias<=14;
                    return <div key={j} style={{background:ev.pm.bg,border:`1px solid ${ev.pm.color}44`,borderRadius:6,padding:"3px 8px",whiteSpace:"nowrap"}}>
                      <div style={{fontSize:10,fontWeight:700,color:ev.pm.color}}>{ev.pm.label}</div>
                      <div style={{fontSize:9,color:urgente?C.red:proximo?C.orange:C.txt3}}>{fmt(ev.fecha)}</div>
                    </div>;
                  })}
                </div>
              </td>
            </tr>)}
          </tbody>
        </table>
        {Object.keys(porEquipo).length===0&&
          <div style={{padding:40,textAlign:"center",color:C.txt3}}>Sin datos · Configura la frecuencia en cada equipo</div>}
      </div>
    </div>}
  </div>;
}
